import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Stack,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  SportsTennis as SportsIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsTennis as TennisIcon,
  SportsHockey as IceHockeyIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  EmojiEvents as TrophyIcon,
  Handshake as HandshakeIcon,
  Apps as AppsIcon,
  CardMembership as CardMembershipIcon,
  SupportAgent as SupportAgentIcon,
  Groups as GroupsIcon,
  WorkOutline as WorkOutlineIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import AdminService, { CreatePlayerData, CreateCoachData, PerformanceData, Player, Coach, SystemStats } from '@services/adminService';
import ShotService from '@services/shotService';
import PlayerCoachLinking from '@components/PlayerCoachLinking';
import VideoPlayer from '@components/VideoPlayer';
import AdminVideoLimitsTab from '@components/admin/AdminVideoLimitsTab';
import AdminVideoEvaluationQueue from '@components/admin/AdminVideoEvaluationQueue';
import EvaluationAdminManagement from '@components/admin/EvaluationAdminManagement';
import AdminUserManagement from '@components/admin/AdminUserManagement';
import AdminMasterDrills from '@components/admin/AdminMasterDrills';
import AdminMasterFitness from '@components/admin/AdminMasterFitness';
import AdminLevelMappings from '@components/admin/AdminLevelMappings';
import AdminLandingEliteBenchmarks from '@components/admin/AdminLandingEliteBenchmarks';
import AdminTrainingHomework from '@components/admin/AdminTrainingHomework';
import AdminTrainingContentMaster from '@components/admin/AdminTrainingContentMaster';
import AdminContestTasks from '@components/admin/AdminContestTasks';
import AdminContestCatalog from '@components/admin/AdminContestCatalog';
import {
  AdminSidebarChildButton,
  AdminSidebarLegend,
  AdminSidebarNavButton,
  AdminSidebarSectionDivider,
  AdminSidebarSportLabel,
} from '@components/admin/AdminSidebarNav';
import { adminChildNavButtonSx, adminSectionHeaderSx, ADMIN_SIDEBAR_BG, ADMIN_SIDEBAR_BORDER, getAdminNavTone } from '@components/admin/adminSidebarTheme';
import { brandColors } from '@config/brandTokens';
import { RELEASE_SPORTS } from '@config/releaseProfile';
import AdminSponsorManagement from '@components/admin/AdminSponsorManagement';
import AdminSubscriptions from '@components/admin/AdminSubscriptions';
import AdminSupportTickets from '@components/admin/AdminSupportTickets';
import AdminCustomerOps from '@components/admin/AdminCustomerOps';
import AdminCareersApplications from '@components/admin/AdminCareersApplications';
import AdminSportModuleRollout from '@components/admin/AdminSportModuleRollout';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import api from '@services/api';
import * as XLSX from 'xlsx';
import { COUNTRIES } from '@utils/countries';
import { CORE_SPORTS, isShootingSport } from '@utils/sports';
import { createEmptyStrokeVideoSections, getStrokeSectionVideoUrls, normalizeStrokeVideoSections, StrokeVideoSection } from '@utils/strokeVideoSections';
import { CourtDimensionsForm } from './CourtDimensionsForm';


const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

const toSportLabel = (value: string): string => {
  if (!value) return 'Badminton';
  const decoded = decodeURIComponent(value).replace(/-/g, ' ').trim();
  if (!decoded) return 'Badminton';
  return decoded
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const toSportSlug = (value: string): string => {
  return encodeURIComponent(value.trim().toLowerCase().replace(/\s+/g, '-'));
};

/** Bearer token for /api/admin/* calls (Express v1 admin routes require JWT). */
function adminAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function adminJsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...adminAuthHeaders() };
}

interface RankingStrokeType {
  id?: string;
  code: string;
  courtPosition?: string;
  halfSide?: string;
  strokeType?: string;
  strokeAction?: string;
  playType?: string;
  shotType?: string;
  placement?: string;
  difficulty?: string | number;
  deception?: string;
  returnResponse?: string;
  referenceVideos?: string;
  strokeVideoSections?: StrokeVideoSection[];
  regularTrainingVideos?: StrokeMediaItem[];
  creatorReferenceVideos?: StrokeMediaItem[];
  amateurSampleVideos?: StrokeMediaItem[];
  contestVideos?: StrokeMediaItem[];
  areaOfImprovementOptions?: string | string[];
  suggestedPracticesOptions?: string | string[];
  isActive?: boolean;
  showOnHomepage?: boolean;
  displayOrder?: number;
  goalTargetZone?: string;
  shootingPosition?: string;
  playerPosition?: string;
  distanceYards?: string;
  shootingMethod?: string;
  areaOfImprovement?: string;
  suggestedPractices?: string;
  possiblePitfalls?: string;
  shootingRating?: string | number;
  controlledPressureLevel?: string;
  foot?: string;
  roles?: string;
  singles?: boolean;
  doubles?: boolean;
}

interface StrokeMediaItem {
  displayOrder?: number;
  sequence?: number;
  title?: string;
  url?: string;
  name?: string;
  notes?: string;
  type?: 'video' | 'image';
  sponsorName?: string;
  sponsorLogoUrl?: string;
  isActive?: boolean;
}

type StrokeMediaGroupKey = 'regularTrainingVideos' | 'creatorReferenceVideos' | 'amateurSampleVideos' | 'contestVideos';

const createEmptyStrokeMediaItem = (displayOrder = 1): StrokeMediaItem => ({
  displayOrder,
  sequence: displayOrder,
  title: '',
  url: '',
  name: '',
  notes: '',
  type: 'video',
  sponsorName: '',
  sponsorLogoUrl: '',
  isActive: true,
});

const normalizeStrokeMediaItems = (items?: StrokeMediaItem[]): StrokeMediaItem[] => {
  const normalized = (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      ...item,
      displayOrder: Number(item.displayOrder ?? item.sequence ?? index + 1),
      sequence: Number(item.displayOrder ?? item.sequence ?? index + 1),
      title: item.title || '',
      url: item.url || '',
      name: item.name || '',
      notes: item.notes || '',
      type: item.type || 'video',
      sponsorName: item.sponsorName || '',
      sponsorLogoUrl: item.sponsorLogoUrl || '',
      isActive: item.isActive !== false,
    }))
    .sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));

  return normalized.length > 0 ? normalized : [createEmptyStrokeMediaItem(1)];
};

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getRouteState = () => {
    const drillsMatch = location.pathname.match(/^\/admin\/([^/]+)\/master-drills\/?$/i);
    if (drillsMatch) {
      return { view: 'master-drills', sport: toSportLabel(drillsMatch[1]) };
    }

    const fitnessMatch = location.pathname.match(/^\/admin\/([^/]+)\/master-fitness\/?$/i);
    if (fitnessMatch) {
      return { view: 'master-fitness', sport: toSportLabel(fitnessMatch[1]) };
    }

    return null as { view: string; sport: string } | null;
  };

  const normalizeAdminView = (view: string | null | undefined, sport: string | null | undefined) => {
    const v = String(view || '').trim().toLowerCase();
    if (v === 'ranking-shooting-types' && isShootingSport(sport)) return 'ranking-stroke-types';
    if (v === 'ranking-shooting-types') return 'ranking-stroke-types';
    if (v === 'ranking-stroke-types') return 'ranking-stroke-types';
    return view || 'dashboard';
  };

  const viewParamForUrl = (view: string, sport: string) => {
    if (view === 'ranking-stroke-types' && isShootingSport(sport)) {
      return 'ranking-shooting-types';
    }
    return view;
  };
  
  // Initialize activeView from URL params or default to 'dashboard'
  const routeState = getRouteState();
  const viewFromUrl = normalizeAdminView(routeState?.view || searchParams.get('view') || 'dashboard', routeState?.sport || searchParams.get('sport') || 'Badminton');
  const sportFromUrl = routeState?.sport || searchParams.get('sport') || 'Badminton';
  
  const [activeView, setActiveView] = useState<string>(viewFromUrl);
  const [expandedSports, setExpandedSports] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, string[]>>({
    Badminton: [],
    Cricket: [],
    Tennis: [],
    Football: [],
    'Ice Hockey': [],
    Athletics: [],
  });
  const [contestNavExpanded, setContestNavExpanded] = useState(true);
  const [currentSport, setCurrentSport] = useState<string>(sportFromUrl);
  const isFootballSport = isShootingSport(currentSport);
  const isUpdatingFromNavigation = useRef(false);
  
  // Read from URL on mount and when URL changes (browser back/forward)
  useEffect(() => {
    // Skip if we're updating from handleViewChange
    if (isUpdatingFromNavigation.current) {
      isUpdatingFromNavigation.current = false;
      return;
    }
    
    const currentRouteState = getRouteState();
    const rawUrlView = currentRouteState?.view || searchParams.get('view') || 'dashboard';
    const urlSport = currentRouteState?.sport || searchParams.get('sport') || 'Badminton';
    const urlView = normalizeAdminView(rawUrlView, urlSport);
    
    // Canonicalize football view URL for consistency in address bar.
    if (isShootingSport(urlSport) && rawUrlView === 'ranking-stroke-types') {
      const canonical = new URLSearchParams(searchParams.toString());
      canonical.set('view', 'ranking-shooting-types');
      canonical.set('sport', urlSport);
      const canonicalUrl = `/admin?${canonical.toString()}`;
      if (location.pathname + location.search !== canonicalUrl) {
        isUpdatingFromNavigation.current = true;
        navigate(canonicalUrl, { replace: true });
      }
    }

    const shouldSyncViewState = urlView !== activeView || urlSport !== currentSport;
    if (shouldSyncViewState) {
      setActiveView(urlView);
      setCurrentSport(urlSport);
    }

    // Load data for the URL view even on direct entry, where state may already match the URL.
    if (
      urlView === 'assessment-skills' &&
      (shouldSyncViewState || assessmentSkills.length === 0 || selectedSportForAssessment !== urlSport)
    ) {
      setSelectedSportForAssessment(urlSport);
      setSelectedCategoryFilter('all');
      loadAssessmentSkills(urlSport);
    } else if (
      urlView === 'skill-assessment-high-level' &&
      (shouldSyncViewState ||
        assessmentSkills.length === 0 ||
        selectedSportForAssessment !== urlSport ||
        selectedAssessmentType !== 'high-level')
    ) {
      setSelectedSportForAssessment(urlSport);
      setSelectedAssessmentType('high-level');
      setSelectedCategoryFilter('all');
      loadAssessmentSkills(urlSport, 'high-level');
    } else if (
      (urlView === 'skill-assessment-master' || urlView === 'skill-assessment-low-level') &&
      (shouldSyncViewState ||
        assessmentSkills.length === 0 ||
        selectedSportForAssessment !== urlSport ||
        selectedAssessmentType !== 'master-training')
    ) {
      setSelectedSportForAssessment(urlSport);
      setSelectedAssessmentType('master-training');
      setSelectedCategoryFilter('all');
      loadAssessmentSkills(urlSport, 'master-training');
    } else if (urlView === 'fitness-assessment' && shouldSyncViewState) {
      setSelectedSportForFitness(urlSport);
      setSelectedFitnessCategoryFilter('all');
      loadFitnessAssessments(urlSport);
    } else if (urlView === 'drill-assessment' && shouldSyncViewState) {
      setSelectedSportForDrill(urlSport);
      setSelectedDrillCategoryFilter('all');
      loadDrillAssessments(urlSport);
    } else if (urlView === 'grade-configurations' && shouldSyncViewState) {
      loadGradeConfigs(urlSport);
    } else if (urlView === 'ranking-stroke-types' && shouldSyncViewState) {
      loadStrokeTypes(urlSport);
    } else if (urlView === 'coaches' && shouldSyncViewState) {
      loadCoaches();
    } else if (urlView === 'academies' && shouldSyncViewState) {
      loadAcademies();
    } else if (urlView === 'category-management' && shouldSyncViewState) {
      loadCategories();
    } else if (urlView === 'skills-management' && shouldSyncViewState) {
      if (categories.length === 0) {
        loadCategories();
      }
    }
  }, [location.pathname, searchParams]); // React to URL changes (browser navigation)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data states
  const [players, setPlayers] = useState<Player[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [shotTypes, setShotTypes] = useState<any[]>([]);
  const [courtGrids, setCourtGrids] = useState<any[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);

  // Dialog states
  const [createPlayerOpen, setCreatePlayerOpen] = useState(false);
  const [createCoachOpen, setCreateCoachOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [playerCoachLinkOpen, setPlayerCoachLinkOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{type: 'player' | 'coach', id: string} | null>(null);
  const [deleteSkillConfirmOpen, setDeleteSkillConfirmOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<string | null>(null);

  // Edit mode states
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editingCoachId, setEditingCoachId] = useState<string | null>(null);
  const [selectedCoachForLinking, setSelectedCoachForLinking] = useState<Coach | null>(null);

  // Form states
  const [playerForm, setPlayerForm] = useState<Partial<CreatePlayerData>>({});
  const [coachForm, setCoachForm] = useState<Partial<CreateCoachData>>({});
  const [performanceForm, setPerformanceForm] = useState<Partial<PerformanceData>>({});
  
  // Grade configurations state
  const [gradeConfigs, setGradeConfigs] = useState<any[]>([]);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [editingGradeId, setEditingGradeId] = useState<string | null>(null);
  const [gradeForm, setGradeForm] = useState<any>({
    sport: 'Badminton',
    grade: 'C',
    min_score: 0,
    max_score: null,
    description: '',
    is_active: true
  });

  // Ranking Stroke Types state
  const [strokeTypes, setStrokeTypes] = useState<RankingStrokeType[]>([]);
  const [strokeTypesLoading, setStrokeTypesLoading] = useState(false);
  const [strokeTypesDialogOpen, setStrokeTypesDialogOpen] = useState(false);
  const [strokeTypesBulkUploadOpen, setStrokeTypesBulkUploadOpen] = useState(false);
  const [editingStrokeType, setEditingStrokeType] = useState<RankingStrokeType | null>(null);

  // Assessment Skills state
  const [assessmentSkills, setAssessmentSkills] = useState<any[]>([]);
  const [assessmentSkillsLoading, setAssessmentSkillsLoading] = useState(false);
  const [assessmentSkillsDialogOpen, setAssessmentSkillsDialogOpen] = useState(false);
  const [editingAssessmentSkill, setEditingAssessmentSkill] = useState<any | null>(null);
  const [selectedSportForAssessment, setSelectedSportForAssessment] = useState<string>('Badminton');
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<'high-level' | 'master-training'>('high-level');
  const [assessmentSkillFormData, setAssessmentSkillFormData] = useState({
    sport: 'Badminton',
    assessmentType: 'high-level' as 'high-level' | 'master-training',
    itemName: '',
    category: '',
    displayOrder: 0,
    isActive: true,
    areaOfImprovementOptions: ['Consult the coaches'],
    suggestedPracticesOptions: ['Consult the coaches'],
    showInBeginner: true,
    showInIntermediate: true,
    showInAdvanced: true,
    showInPro: true,
    showOnHomepage: false,
  });
  const [areaOptionInput, setAreaOptionInput] = useState('');
  const [suggestedOptionInput, setSuggestedOptionInput] = useState('');

  // Fitness Assessment state
  const [fitnessAssessments, setFitnessAssessments] = useState<any[]>([]);
  const [fitnessAssessmentsLoading, setFitnessAssessmentsLoading] = useState(false);
  const [fitnessAssessmentsDialogOpen, setFitnessAssessmentsDialogOpen] = useState(false);
  const [editingFitnessAssessment, setEditingFitnessAssessment] = useState<any | null>(null);
  const [selectedSportForFitness, setSelectedSportForFitness] = useState<string>('Badminton');
  const [fitnessFormData, setFitnessFormData] = useState({
    sport: 'Badminton',
    assessmentType: 'fitness' as const,
    itemName: '',
    category: '',
    displayOrder: 0,
    isActive: true,
  });
  const [fitnessCategories, setFitnessCategories] = useState<string[]>([]);
  const [fitnessCategoryManagementOpen, setFitnessCategoryManagementOpen] = useState(false);
  const [newFitnessCategoryName, setNewFitnessCategoryName] = useState('');
  const [selectedFitnessCategoryFilter, setSelectedFitnessCategoryFilter] = useState<string>('all');

  // Drill Assessment state
  const [drillAssessments, setDrillAssessments] = useState<any[]>([]);
  const [drillAssessmentsLoading, setDrillAssessmentsLoading] = useState(false);
  const [drillAssessmentsDialogOpen, setDrillAssessmentsDialogOpen] = useState(false);
  const [editingDrillAssessment, setEditingDrillAssessment] = useState<any | null>(null);
  const [selectedSportForDrill, setSelectedSportForDrill] = useState<string>('Badminton');
  const [drillFormData, setDrillFormData] = useState({
    sport: 'Badminton',
    assessmentType: 'drill' as const,
    itemName: '',
    category: '',
    displayOrder: 0,
    isActive: true,
  });
  const [drillCategories, setDrillCategories] = useState<string[]>([]);
  const [drillCategoryManagementOpen, setDrillCategoryManagementOpen] = useState(false);
  const [newDrillCategoryName, setNewDrillCategoryName] = useState('');
  const [selectedDrillCategoryFilter, setSelectedDrillCategoryFilter] = useState<string>('all');
  
  // Category Management state
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedSportForCategories, setSelectedSportForCategories] = useState<string>('Badminton');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({ 
    sport: 'Badminton', 
    name: '', 
    parentCategoryId: '', 
    tableName: '', 
    description: '', 
    displayOrder: 0,
    categoryType: 'general'
  });
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
  
  // Skills Management state
  const [selectedCategoryForSkills, setSelectedCategoryForSkills] = useState<any>(null);
  const [skills, setSkills] = useState<any[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<any>(null);
  const [skillForm, setSkillForm] = useState<any>({});
  const [categoryTemplate, setCategoryTemplate] = useState<any>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [expandedFormSections, setExpandedFormSections] = useState<Record<string, boolean>>({});
  
  const [strokeTypeFormData, setStrokeTypeFormData] = useState<RankingStrokeType>({
    code: '',
    courtPosition: '',
    halfSide: '',
    strokeType: '',
    strokeAction: '',
    playType: '',
    shotType: '',
    placement: '',
    difficulty: 0,
    deception: '',
    returnResponse: '',
    referenceVideos: '',
    areaOfImprovementOptions: ['Consult the coaches'],
    suggestedPracticesOptions: ['Consult the coaches'],
    controlledPressureLevel: '',
    foot: '',
    roles: '',
    singles: false,
    doubles: false,
    isActive: true,
    showOnHomepage: false,
    displayOrder: 0,
  });
  const [strokeVideoSections, setStrokeVideoSections] = useState<StrokeVideoSection[]>(createEmptyStrokeVideoSections());
  const [strokeMediaGroups, setStrokeMediaGroups] = useState<Record<StrokeMediaGroupKey, StrokeMediaItem[]>>({
    regularTrainingVideos: [createEmptyStrokeMediaItem(1)],
    creatorReferenceVideos: [createEmptyStrokeMediaItem(1)],
    amateurSampleVideos: [createEmptyStrokeMediaItem(1)],
    contestVideos: [createEmptyStrokeMediaItem(1)],
  });
  const [strokeAreaOptionInput, setStrokeAreaOptionInput] = useState('');
  const [strokeSuggestedOptionInput, setStrokeSuggestedOptionInput] = useState('');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const assessmentSkillsContainerRef = useRef<HTMLDivElement>(null);
  const fixedAssessmentSkillsContainerRef = useRef<HTMLDivElement>(null);

  // Academy Management state
  const [academies, setAcademies] = useState<any[]>([]);
  const [academiesLoading, setAcademiesLoading] = useState(false);
  const [academyDialogOpen, setAcademyDialogOpen] = useState(false);
  const [academyBulkUploadOpen, setAcademyBulkUploadOpen] = useState(false);
  const [editingAcademy, setEditingAcademy] = useState<any | null>(null);
  const [academyFormData, setAcademyFormData] = useState({
    name: '',
    description: '',
    country: '',
    city: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    status: 'active',
  });
  const [generateAdminAcademy, setGenerateAdminAcademy] = useState<any | null>(null);
  const [generateAdminForm, setGenerateAdminForm] = useState({
    firstName: '',
    lastName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [generatedAdminCredentials, setGeneratedAdminCredentials] = useState<{
    adminIdCode: string;
    temporaryPassword?: string | null;
  } | null>(null);
  const [generateAdminLoading, setGenerateAdminLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Auto-clear success and error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [playersRes, coachesRes, shotTypesRes, singlesRes, doublesRes, statsRes] = await Promise.allSettled([
        AdminService.getPlayers({ limit: 10 }),
        AdminService.getCoaches({ limit: 10 }),
        ShotService.getShotTypes(),
        ShotService.getCourtGrids('singles'),
        ShotService.getCourtGrids('doubles'),
        AdminService.getSystemStats(),
      ]);

      if (playersRes.status === 'fulfilled') {
        const playersData: any = playersRes.value;
        setPlayers(playersData?.data || []);
      } else {
        console.warn('[Admin] Initial players load failed:', playersRes.reason);
      }

      if (coachesRes.status === 'fulfilled') {
        const coachesData: any = coachesRes.value;
        setCoaches(coachesData?.data || []);
      } else {
        console.warn('[Admin] Initial coaches load failed:', coachesRes.reason);
      }

      if (shotTypesRes.status === 'fulfilled') {
        const shotTypesData: any = shotTypesRes.value;
        setShotTypes(Array.isArray(shotTypesData) ? shotTypesData : (shotTypesData?.data || []));
      } else {
        console.warn('[Admin] Initial shot types load failed:', shotTypesRes.reason);
      }

      const singlesGrids: any[] =
        singlesRes.status === 'fulfilled' && Array.isArray((singlesRes.value as any))
          ? (singlesRes.value as any[])
          : [];
      const doublesGrids: any[] =
        doublesRes.status === 'fulfilled' && Array.isArray((doublesRes.value as any))
          ? (doublesRes.value as any[])
          : [];
      setCourtGrids([...(singlesGrids || []), ...(doublesGrids || [])]);

      if (statsRes.status === 'fulfilled') {
        const statsData: any = statsRes.value;
        setStats(statsData?.data || null);
      } else {
        console.warn('[Admin] Initial stats load failed:', statsRes.reason);
      }

      const hasCriticalFailure = playersRes.status === 'rejected' && coachesRes.status === 'rejected' && statsRes.status === 'rejected';
      if (hasCriticalFailure) {
        setError('Failed to load key admin data. Please refresh.');
      }
    } catch (err: any) {
      console.error('[Admin] Unexpected initial load error:', err);
      setError(err?.message || 'Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const loadGradeConfigs = async (sport: string) => {
    try {
      const response = await api.get(`/admin/grade-configurations/${encodeURIComponent(sport)}`);
      const data = response.data;
      if (data.success) {
        setGradeConfigs(data.data);
      }
    } catch (error) {
      console.error('Error loading grade configurations:', error);
      // Don't show error toast for this, it's called on mount
    }
  };

  const handleSaveGrade = async () => {
    if (!gradeForm.sport || !gradeForm.grade || gradeForm.min_score === undefined) {
      setError('Sport, grade, and min score are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...(editingGradeId && { id: editingGradeId }),
        sport: gradeForm.sport || 'Badminton',
        grade: gradeForm.grade,
        min_score: gradeForm.min_score,
        max_score: gradeForm.max_score || null,
        description: gradeForm.description || null,
        is_active: gradeForm.is_active !== undefined ? gradeForm.is_active : true
      };

      const response = await api.post('/admin/grade-configurations', payload);
      const data = response.data;
      if (data.success) {
        setSuccess(editingGradeId ? 'Grade configuration updated successfully!' : 'Grade configuration created successfully!');
        setGradeDialogOpen(false);
        setEditingGradeId(null);
        setGradeForm({
          sport: 'Badminton',
          grade: 'C',
          min_score: 0,
          max_score: null,
          description: '',
          is_active: true
        });
        loadGradeConfigs(gradeForm.sport || currentSport || 'Badminton');
      } else {
        setError(data.message || 'Failed to save grade configuration');
      }
    } catch (error: any) {
      setError('Failed to save grade configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGrade = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this grade configuration?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.delete(`/admin/grade-configurations/${id}`);
      const data = response.data;
      if (data.success) {
        setSuccess('Grade configuration deleted successfully!');
        loadGradeConfigs(currentSport || 'Badminton');
      } else {
        setError(data.message || 'Failed to delete grade configuration');
      }
    } catch (error: any) {
      setError('Failed to delete grade configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCreatePlayer = async () => {
    if (!playerForm.firstName || !playerForm.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    // If editing, don't require email and password
    if (!editingPlayerId && (!playerForm.email || !playerForm.password)) {
      setError('Email and password are required for new players');
      return;
    }

    setLoading(true);
    try {
      if (editingPlayerId) {
        // Update existing player
        await AdminService.updatePlayer(editingPlayerId, playerForm);
        setSuccess('Player updated successfully!');
      } else {
        // Create new player
        await AdminService.createPlayer(playerForm as CreatePlayerData);
        setSuccess('Player created successfully!');
      }
      setCreatePlayerOpen(false);
      setPlayerForm({});
      setEditingPlayerId(null);
      loadInitialData();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${editingPlayerId ? 'update' : 'create'} player`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlayer = (player: Player) => {
    console.log('Editing player:', player);
    setEditingPlayerId(player.id);
    
    // Fix DOB timezone issue - convert to local date
    let dobValue = '';
    if (player.dateOfBirth) {
      const date = new Date(player.dateOfBirth);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dobValue = `${year}-${month}-${day}`;
    }
    
    const formData = {
      firstName: player.user?.firstName || '',
      lastName: player.user?.lastName || '',
      email: player.user?.email || '',
      dateOfBirth: dobValue,
      gender: player.gender,
      level: player.level,
      ageCategory: player.ageCategory,
      country: player.country || '',
      primarySport: player.primarySport || '',
      secondarySport: player.secondarySport || '',
      academyName: player.academyName || '',
    };
    
    console.log('Setting player form to:', formData);
    setPlayerForm(formData);
    setCreatePlayerOpen(true);
  };

  const handleDeletePlayer = (playerId: string) => {
    setDeleteTarget({ type: 'player', id: playerId });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCoachClick = (coachId: string) => {
    setDeleteTarget({ type: 'coach', id: coachId });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    try {
      if (deleteTarget.type === 'player') {
        await AdminService.deletePlayer(deleteTarget.id);
        setSuccess('Player deleted successfully!');
      } else {
        await AdminService.deleteCoach(deleteTarget.id);
        setSuccess('Coach deleted successfully!');
      }
      loadInitialData();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to delete ${deleteTarget.type}`);
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleEditCoach = (coach: Coach) => {
    setEditingCoachId(coach.id);
    setCoachForm({
      firstName: coach.user?.firstName || '',
      lastName: coach.user?.lastName || '',
      email: coach.user?.email || '',
      primarySport: coach.primarySport || '',
      specialization: coach.specialization || '',
      experience: coach.experience,
      country: coach.country || '',
      academyName: coach.academyName || '',
      isPersonalTrainer: coach.isPersonalTrainer || false,
      bio: coach.bio || '',
    });
    setCreateCoachOpen(true);
  };


  const handleCreateCoach = async () => {
    if (!coachForm.firstName || !coachForm.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    // If editing, don't require email and password
    if (!editingCoachId && (!coachForm.email || !coachForm.password)) {
      setError('Email and password are required for new coaches');
      return;
    }

    setLoading(true);
    try {
      if (editingCoachId) {
        // Update existing coach
        await AdminService.updateCoach(editingCoachId, coachForm);
        setSuccess('Coach updated successfully!');
      } else {
        // Create new coach
        await AdminService.createCoach(coachForm as CreateCoachData);
        setSuccess('Coach created successfully!');
      }
      setCreateCoachOpen(false);
      setCoachForm({});
      setEditingCoachId(null);
      loadInitialData();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${editingCoachId ? 'update' : 'create'} coach`);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterPerformance = async () => {
    if (!performanceForm.playerId || !performanceForm.shotTypeId || !performanceForm.courtGridId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await AdminService.enterPerformanceData(performanceForm as PerformanceData);
      setSuccess('Performance data entered successfully!');
      setPerformanceOpen(false);
      setPerformanceForm({});
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enter performance data');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (level: number) => {
    if (level <= 3) return 'success';
    if (level <= 5) return 'warning';
    if (level <= 7) return 'error';
    return 'error';
  };

  // Check if view is accessible for current user role
  const isViewAccessible = (view: string): boolean => {
    if (user?.role === 'evaluation_admin') {
      const allowedViews = ['dashboard', 'video-evaluation-queue'];
      return allowedViews.includes(view);
    }
    if (user?.role === 'contest_admin') {
      const allowedViews = ['dashboard', 'contest-tasks'];
      return allowedViews.includes(view);
    }
    if (user?.role === 'support_admin') {
      const allowedViews = ['dashboard', 'customer-ops', 'support-tickets', 'subscriptions'];
      return allowedViews.includes(view);
    }
    // Full admins can access everything
    return true;
  };

  const handleViewChange = (view: string, sport?: string, extraParams?: Record<string, string>) => {
    // Restrict evaluation_admin to only evaluation-related views
    if (!isViewAccessible(view)) {
      console.warn('Access denied: Evaluation admin cannot access this view:', view);
      return;
    }
    
    const sportToUse = sport || currentSport || expandedSports[0] || 'Badminton';
    setActiveView(view);
    setCurrentSport(sportToUse);

    if (view === 'master-drills' || view === 'master-fitness') {
      const targetPath =
        view === 'master-drills'
          ? `/admin/${toSportSlug(sportToUse)}/master-drills`
          : `/admin/${toSportSlug(sportToUse)}/master-fitness`;

      if (location.pathname !== targetPath) {
        isUpdatingFromNavigation.current = true;
        navigate(targetPath, { replace: true });
      }
      setError(null);
      setSuccess(null);
      return;
    }
    
    // Update URL parameters
    const params = new URLSearchParams();
    if (view !== 'dashboard') {
      params.set('view', viewParamForUrl(view, sportToUse));
    }
    // Always include sport for sport-specific views like video-evaluation-queue
    if (view === 'video-evaluation-queue' || (sportToUse && sportToUse !== 'Badminton')) {
      params.set('sport', sportToUse);
    }
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
    }
    const newSearch = params.toString();
    const newUrl = `/admin${newSearch ? `?${newSearch}` : ''}`;
    // Only navigate if URL is different to avoid unnecessary updates
    if (location.pathname + location.search !== newUrl) {
      isUpdatingFromNavigation.current = true; // Flag that we're updating from navigation
      navigate(newUrl, { replace: true });
    }
    if (view === 'assessment-skills') {
      setSelectedSportForAssessment(sportToUse);
      setSelectedCategoryFilter('all'); // Reset filter when switching sports
      loadAssessmentSkills(sportToUse);
    } else if (view === 'skill-assessment-high-level') {
      setSelectedSportForAssessment(sportToUse);
      setSelectedAssessmentType('high-level');
      setSelectedCategoryFilter('all'); // Reset filter when switching sports
      loadAssessmentSkills(sportToUse, 'high-level');
    } else if (view === 'skill-assessment-master' || view === 'skill-assessment-low-level') {
      setSelectedSportForAssessment(sportToUse);
      setSelectedAssessmentType('master-training');
      setSelectedCategoryFilter('all'); // Reset filter when switching sports
      loadAssessmentSkills(sportToUse, 'master-training');
    } else if (view === 'fitness-assessment') {
      setSelectedSportForFitness(sportToUse);
      setSelectedFitnessCategoryFilter('all'); // Reset filter when switching sports
      loadFitnessAssessments(sportToUse);
    } else if (view === 'drill-assessment') {
      setSelectedSportForDrill(sportToUse);
      setSelectedDrillCategoryFilter('all'); // Reset filter when switching sports
      loadDrillAssessments(sportToUse);
    }
    // Clear any stale error messages when switching views
    setError(null);
    setSuccess(null);
    
    if (view === 'grade-configurations') {
      // Load grade configs when switching to Grade Configurations
      loadGradeConfigs(sportToUse);
    } else if (view === 'ranking-stroke-types') {
      // Load stroke types when switching to Ranking Stroke Types
      loadStrokeTypes(sportToUse);
    } else if (view === 'coaches') {
      // Load coaches when switching to Coaches view
      loadCoaches();
    } else if (view === 'academies') {
      // Load academies when switching to Academies view
      loadAcademies();
    } else if (view === 'category-management') {
      // Load categories when switching to Category Management
      loadCategories();
    } else if (view === 'skills-management') {
      // Load categories when switching to Skills Management (needed for dropdown)
      if (categories.length === 0) {
        loadCategories();
      }
    }
  };

  const loadCoaches = async () => {
    setLoading(true);
    try {
      const coachesData = await AdminService.getCoaches({ limit: 1000 });
      console.log('Coaches data received:', coachesData);
      // Handle both response formats: { data: [...] } or { success: true, data: [...] }
      const coachesList = coachesData.data || coachesData || [];
      console.log('Setting coaches:', coachesList);
      setCoaches(coachesList);
    } catch (err: any) {
      console.error('Error loading coaches:', err);
      setError('Failed to load coaches: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadAcademies = async () => {
    setAcademiesLoading(true);
    try {
      const response = await fetch('/api/academies');
      const data = await response.json();
      if (data.success) {
        setAcademies(data.data || []);
      } else {
        setError(data.message || 'Failed to load academies');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load academies');
    } finally {
      setAcademiesLoading(false);
    }
  };

  const handleOpenAcademyDialog = (academy?: any) => {
    if (academy) {
      setEditingAcademy(academy);
      setAcademyFormData({
        name: academy.name,
        description: academy.description || '',
        country: academy.country || '',
        city: academy.city || '',
        address: academy.address || '',
        contact_email: academy.contact_email || '',
        contact_phone: academy.contact_phone || '',
        status: academy.status || 'active',
      });
    } else {
      setEditingAcademy(null);
      setAcademyFormData({
        name: '',
        description: '',
        country: '',
        city: '',
        address: '',
        contact_email: '',
        contact_phone: '',
        status: 'active',
      });
    }
    setAcademyDialogOpen(true);
  };

  const handleCloseAcademyDialog = () => {
    setAcademyDialogOpen(false);
    setEditingAcademy(null);
    setAcademyFormData({
      name: '',
      description: '',
      country: '',
      city: '',
      address: '',
      contact_email: '',
      contact_phone: '',
      status: 'active',
    });
  };

  const handleSaveAcademy = async () => {
    if (!academyFormData.name) {
      setError('Academy name is required');
      return;
    }
    setAcademiesLoading(true);
    try {
      const url = editingAcademy ? `/api/academies/${editingAcademy.id}` : '/api/academies';
      const method = editingAcademy ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(academyFormData),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(editingAcademy ? 'Academy updated successfully!' : 'Academy created successfully!');
        handleCloseAcademyDialog();
        loadAcademies();
      } else {
        setError(data.message || 'Failed to save academy');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save academy');
    } finally {
      setAcademiesLoading(false);
    }
  };

  const handleDeleteAcademy = async (academyId: string) => {
    if (!window.confirm('Are you sure you want to delete this academy?')) {
      return;
    }
    setAcademiesLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Academy deleted successfully!');
        loadAcademies();
      } else {
        setError(data.message || 'Failed to delete academy');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete academy');
    } finally {
      setAcademiesLoading(false);
    }
  };

  const handleGenerateAcademyAdmin = async () => {
    if (!generateAdminAcademy?.id) return;
    if (!generateAdminForm.firstName.trim() || !generateAdminForm.lastName.trim()) {
      setError('First name and last name are required to generate an Admin ID');
      return;
    }
    setGenerateAdminLoading(true);
    try {
      const response = await fetch(`/api/academies/${generateAdminAcademy.id}/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: generateAdminForm.firstName.trim(),
          lastName: generateAdminForm.lastName.trim(),
          contactEmail: generateAdminForm.contactEmail.trim() || undefined,
          contactPhone: generateAdminForm.contactPhone.trim() || undefined,
        }),
      });
      const data = await response.json();
      if (data.success && data.data?.adminIdCode) {
        setGeneratedAdminCredentials({
          adminIdCode: data.data.adminIdCode,
          temporaryPassword: data.data.temporaryPassword,
        });
        setSuccess(`Admin ID created for ${generateAdminAcademy.name}. Share it offline or by email.`);
      } else {
        setError(data.message || 'Failed to generate Admin ID');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate Admin ID');
    } finally {
      setGenerateAdminLoading(false);
    }
  };

  const loadCategories = async (forceReload: boolean = false, sport?: string) => {
    const sportToLoad = sport || selectedSportForAssessment || currentSport || 'Badminton';
    // Don't set loading state if categories are already loaded to avoid blocking UI
    // Unless forceReload is true (e.g., after update) or sport changed
    if (categories.length > 0 && !forceReload && categories[0]?.sport === sportToLoad) {
      return; // Already loaded for this sport
    }
    setLoading(true);
    try {
      const response = await fetch(`/api/learning/categories?sport=${sportToLoad}`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data || []);
      } else {
        setError(data.message || 'Failed to load categories');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    // Validate required fields
    if (!categoryForm.name || categoryForm.name.trim() === '') {
      setError('Category name is required');
      return;
    }
    
    if (!editingCategory && (!categoryForm.sport || categoryForm.sport.trim() === '')) {
      setError('Sport is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const url = editingCategory 
        ? `/api/admin/learning/categories/${editingCategory.id}`
        : '/api/admin/learning/categories';
      
      const method = editingCategory ? 'PUT' : 'POST';
      const body = editingCategory
        ? {
            name: categoryForm.name.trim(),
            parentCategoryId: categoryForm.parentCategoryId || null,
            tableName: categoryForm.tableName?.trim() || null,
            description: categoryForm.description?.trim() || null,
            displayOrder: categoryForm.displayOrder || 0,
            categoryType: categoryForm.categoryType || 'general'
          }
        : {
            sport: categoryForm.sport.trim(),
            name: categoryForm.name.trim(),
            parentCategoryId: categoryForm.parentCategoryId || null,
            tableName: categoryForm.tableName?.trim() || null,
            description: categoryForm.description?.trim() || null,
            displayOrder: categoryForm.displayOrder || 0,
            categoryType: categoryForm.categoryType || 'general'
          };
      
      const tokenToUse = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!tokenToUse) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('[Save Category] Request URL:', url);
      console.log('[Save Category] Request body:', body);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenToUse}`
        },
        body: JSON.stringify(body)
      });
      
      console.log('[Save Category] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Save Category] Error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.message || `Failed to save category (${response.status})`);
        } catch {
          setError(`Failed to save category: ${response.status} ${response.statusText}`);
        }
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('[Save Category] Response data:', data);
      if (data.success) {
        // Save editing state before clearing it
        const wasEditing = !!editingCategory;
        
        // Close dialog FIRST before showing success message
        setCategoryDialogOpen(false);
        setEditingCategory(null);
      setCategoryForm({ 
        sport: selectedSportForCategories, 
        name: '', 
        parentCategoryId: '', 
        tableName: '', 
        description: '', 
        displayOrder: 0,
        categoryType: 'general'
      });
        
        // Show success message AFTER dialog closes
        const successMessage = wasEditing ? 'Category updated successfully' : 'Category created successfully';
        setSuccess(successMessage);
        
        // Force reload categories to update UI
        await loadCategories(true);
      } else {
        setError(data.message || 'Failed to save category');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const [deleteCategoryConfirmOpen, setDeleteCategoryConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  const handleDeleteCategory = async (id: string) => {
    setCategoryToDelete(id);
    setDeleteCategoryConfirmOpen(true);
  };

  const confirmDeleteCategory = async () => {
    console.log('[Delete Category] ===== confirmDeleteCategory CALLED =====');
    console.log('[Delete Category] categoryToDelete:', categoryToDelete);
    
    if (!categoryToDelete) {
      console.error('[Delete Category] ❌ No category to delete');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }
      
      const url = `/api/admin/learning/categories/${categoryToDelete}`;
      console.log('[Delete Category] Calling DELETE:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[Delete Category] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Delete Category] Error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.message || `Failed to delete category (${response.status})`);
        } catch {
          setError(`Failed to delete category: ${response.status} ${response.statusText}`);
        }
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('[Delete Category] Response data:', data);
      
      if (data.success) {
        console.log('[Delete Category] ✅ Delete successful');
        
        // Close dialog first
        setDeleteCategoryConfirmOpen(false);
        const deletedCategoryId = categoryToDelete;
        setCategoryToDelete(null);
        
        // IMMEDIATE state update - remove deleted category from UI instantly
        setCategories(prevCategories => {
          const updated = prevCategories.filter(cat => cat.id !== deletedCategoryId);
          console.log('[Delete Category] 🔄 State updated immediately');
          console.log('[Delete Category] Before:', prevCategories.length, 'After:', updated.length);
          return updated;
        });
        
        // Show success message (will auto-clear after 5 seconds via useEffect)
        setSuccess('Category deleted successfully');
        
        // Then reload from server to ensure consistency
        console.log('[Delete Category] Reloading categories from server...');
        await loadCategories(true); // Force reload
        console.log('[Delete Category] ✅ Categories reloaded from server');
      } else {
        console.error('[Delete Category] ❌ Delete failed:', data.message);
        setError(data.message || 'Failed to delete category');
      }
    } catch (err: any) {
      console.error('[Delete Category] Exception:', err);
      setError(err.message || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (categoryId: string) => {
    setTemplateLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/admin/learning/templates/${categoryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Template loaded for category:', categoryId, data.data);
          console.log('Template displayColumns:', data.data.displayColumns);
          console.log('Template formSections:', data.data.formSections);
          setCategoryTemplate(data.data);
          // Initialize expanded sections - basic_info always open, others closed
          const sections: Record<string, boolean> = { basic_info: true };
          if (data.data.formSections) {
            Object.keys(data.data.formSections).forEach(sectionKey => {
              if (sectionKey !== 'basic_info') {
                sections[sectionKey] = false;
              }
            });
          }
          setExpandedFormSections(sections);
          return data.data;
        }
      } else if (response.status === 404) {
        console.warn('⚠️ Template not found for category:', categoryId);
        setCategoryTemplate(null);
        return null;
      }
      // Template not found is OK - category might not have a template yet
      console.warn('⚠️ Template load failed for category:', categoryId, 'Status:', response.status);
      setCategoryTemplate(null);
      return null;
    } catch (err: any) {
      console.error('Load template error:', err);
      setCategoryTemplate(null);
      return null;
    } finally {
      setTemplateLoading(false);
    }
  };

  const loadSkills = async (category: any) => {
    if (!category || (!category.table_name && !category.full_table_name)) {
      setError('Category does not have a table assigned');
      return;
    }
    
    // Load template for this category first
    await loadTemplate(category.id);
    
    setSkillsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setSkillsLoading(false);
        return;
      }
      
      console.log('Loading skills for category:', category.name, 'with token:', token.substring(0, 20) + '...');
      
      const response = await fetch(`/api/admin/learning/skills?categoryId=${category.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please log out and log in again.');
          console.error('401 Unauthorized - Token may be invalid or expired');
          console.error('Token used:', token ? token.substring(0, 20) + '...' : 'No token');
        } else {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          setError(errorData.message || `Failed to load skills (${response.status})`);
        }
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        const loadedSkills = data.data || [];
        // Show all skills (is_active removed - everything published is active)
        console.log('[Load Skills] ✅ Loaded', loadedSkills.length, 'skills');
        setSkills(loadedSkills);
      } else {
        setError(data.message || 'Failed to load skills');
        console.error('[Load Skills] ❌ Error:', data);
      }
    } catch (err: any) {
      console.error('Load skills exception:', err);
      setError(err.message || 'Failed to load skills');
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleSaveSkill = async (publish: boolean = false) => {
    const tableName = selectedCategoryForSkills?.full_table_name || selectedCategoryForSkills?.table_name;
    if (!selectedCategoryForSkills || !tableName) {
      setError('Please select a category first');
      return;
    }
    
    setSkillsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const url = editingSkill 
        ? `/api/admin/learning/skills/${editingSkill.id}`
        : '/api/admin/learning/skills';
      
      const method = editingSkill ? 'PUT' : 'POST';
      
      // Prepare form data - handle Court Dimensions content structure
      let formData: any = { ...skillForm };
      
      // If this uses Court Dimensions form component, map content to table columns
      const renderComponent = categoryTemplate?.renderComponent || categoryTemplate?.render_component;
      if (renderComponent === 'court_dimensions' || renderComponent === 'CourtDimensionsTemplate') {
        // Map content structure to table columns
        // The CourtDimensionsForm component uses content.Images/Videos/Dimensions structure
        // but the database stores them as images/videos/measurements JSONB[] columns
        if (formData.content && typeof formData.content === 'object') {
          // Convert content.Images to images array - convert empty arrays to null
          if (formData.content.Images && Array.isArray(formData.content.Images)) {
            const filtered = formData.content.Images
              .filter((img: any) => img && (img.url || img.title))
              .map((img: any) => ({
                title: img.title || '',
                url: img.url || '',
                Author: img.Author || '',
                textBefore: img.textBefore || '',
                textAfter: img.textAfter || ''
              }));
            formData.images = filtered.length > 0 ? filtered : null;
          } else {
            formData.images = null;
          }
          
          // Convert content.Videos to videos array - convert empty arrays to null
          if (formData.content.Videos && Array.isArray(formData.content.Videos)) {
            const filtered = formData.content.Videos
              .filter((vid: any) => vid && (vid.url || vid.title))
              .map((vid: any) => ({
                title: vid.title || '',
                url: vid.url || '',
                Author: vid.Author || '',
                textBefore: vid.textBefore || '',
                textAfter: vid.textAfter || '',
                videoType: vid.videoType || 'youtube'
              }));
            formData.videos = filtered.length > 0 ? filtered : null;
          } else {
            formData.videos = null;
          }
          
          // Convert content.Notes (or content.Dimensions for backward compatibility) to measurements array
          const notesData = formData.content.Notes || formData.content.Dimensions;
          if (notesData && Array.isArray(notesData)) {
            const filtered = notesData
              .filter((note: any) => note && (note.NoteTitle || note.Dimensions || note.Value))
              .map((note: any) => ({
                // Support both new (NoteTitle) and old (Dimensions) structure
                Dimensions: note.NoteTitle || note.Dimensions || '',
                Value: note.Value || {}
              }));
            formData.measurements = filtered.length > 0 ? filtered : null;
          } else {
            formData.measurements = null;
          }
          
          // Convert content.Text to text_content array - convert empty arrays to null
          if (formData.content.Text && Array.isArray(formData.content.Text)) {
            const filtered = formData.content.Text
              .filter((text: any) => text && (text.content || text.title))
              .map((text: any) => ({
                title: text.title || '',
                content: text.content || '',
                Author: text.Author || ''
              }));
            formData.text_content = filtered.length > 0 ? filtered : null;
            console.log('[Save Skill] Mapped content.Text to text_content:', formData.text_content);
          } else {
            formData.text_content = null;
            console.log('[Save Skill] No content.Text found, setting text_content to null');
          }
          
          // Remove content field as it's not a table column
          delete formData.content;
        }
      }
      
      // Ensure JSONB[] columns: empty arrays = null (BEFORE JSON.stringify)
      // This handles both cases: data from content structure (mapped above) or direct fields
      const jsonbArrayColumns = ['images', 'videos', 'measurements', 'text_content'];
      jsonbArrayColumns.forEach(col => {
        if (!formData[col] || formData[col] === '' || formData[col] === undefined) {
          formData[col] = null;
        } else if (Array.isArray(formData[col])) {
          formData[col] = formData[col].length > 0 ? formData[col] : null;
        } else {
          formData[col] = null; // Not an array = null
        }
      });
      
      const body = editingSkill
        ? {
            tableName: tableName,
            ...formData,
            is_active: true  // Always true - if published, it's active
          }
        : {
            categoryId: selectedCategoryForSkills.id,
            tableName: tableName,
            ...formData,
            is_active: true  // Always true - if published, it's active
          };
      
      console.log('[Save Skill] Request body:', body);
      console.log('[Save Skill] text_content in body:', body.text_content);
      console.log('[Save Skill] formData.text_content:', formData.text_content);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      let data;
      try {
        const responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('[Save Skill] Failed to parse response:', parseError);
        const errorMessage = `Server error: ${response.status} ${response.statusText}`;
        setError(errorMessage);
        setTimeout(() => setError(null), 8000);
        setSkillsLoading(false);
        return;
      }
      
      if (!response.ok || !data.success) {
        const errorMessage = data.message || data.error || `Failed to ${editingSkill ? 'update' : 'create'} skill (${response.status})`;
        console.error('[Save Skill] Error:', errorMessage, data);
        setError(errorMessage);
        setTimeout(() => setError(null), 8000); // Show error longer
        setSkillsLoading(false);
        return;
      }
      
      // Success - prepare message
      const successMessage = publish 
        ? (editingSkill ? 'Published successfully' : 'Created and published')
        : (editingSkill ? 'Saved as draft' : 'Created as draft');
      
      // Close dialog first so message is visible
      setSkillDialogOpen(false);
      setEditingSkill(null);
      setSkillForm({});
      
      // Show success message after dialog closes
      setTimeout(() => {
        setSuccess(successMessage);
        setTimeout(() => setSuccess(null), 3000);
      }, 100);
      
      // Reload skills
      await loadSkills(selectedCategoryForSkills);
      setSkillsLoading(false);
    } catch (err: any) {
      console.error('[Save Skill] Exception:', err);
      const errorMessage = err.message || 'Failed to save skill';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
      setSkillsLoading(false);
    }
  };

  const handleDeleteSkill = async (skillId: string) => {
    if (!selectedCategoryForSkills) {
      setError('Category not selected');
      return;
    }
    
    setSkillToDelete(skillId);
    setDeleteSkillConfirmOpen(true);
  };

  const confirmDeleteSkill = async () => {
    console.log('[Delete Skill] ===== confirmDeleteSkill CALLED =====');
    console.log('[Delete Skill] skillToDelete:', skillToDelete);
    console.log('[Delete Skill] selectedCategoryForSkills:', selectedCategoryForSkills);
    console.log('[Delete Skill] Current skills count:', skills.length);
    
    if (!skillToDelete || !selectedCategoryForSkills) {
      console.error('[Delete Skill] ❌ Missing skillToDelete or selectedCategoryForSkills');
      console.error('[Delete Skill] skillToDelete:', skillToDelete);
      console.error('[Delete Skill] selectedCategoryForSkills:', selectedCategoryForSkills);
      setError('Missing required information for deletion');
      setSkillsLoading(false);
      return;
    }
    
    setSkillsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setSkillsLoading(false);
        return;
      }
      
      // Use categoryId instead of tableName - backend will resolve table name
      const categoryId = selectedCategoryForSkills.id;
      const url = `/api/admin/learning/skills/${skillToDelete}?categoryId=${encodeURIComponent(categoryId)}`;
      
      console.log('[Delete Skill] ===== DELETE REQUEST =====');
      console.log('[Delete Skill] URL:', url);
      console.log('[Delete Skill] Skill ID:', skillToDelete);
      console.log('[Delete Skill] Category ID:', categoryId);
      console.log('[Delete Skill] Category Name:', selectedCategoryForSkills.name);
      console.log('[Delete Skill] Token (first 20 chars):', token.substring(0, 20) + '...');
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[Delete Skill] Response status:', response.status);
      console.log('[Delete Skill] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Delete Skill] Error response:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.message || `Failed to delete skill (${response.status})`);
        } catch {
          setError(`Failed to delete skill: ${response.status} ${response.statusText}`);
        }
        setSkillsLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('[Delete Skill] Response data:', data);
      
      if (data.success) {
        console.log('[Delete Skill] ✅ Delete successful - Response:', data);
        
        const deletedSkillId = skillToDelete;
        console.log('[Delete Skill] Will remove skill ID:', deletedSkillId);
        
        // Close dialog FIRST
        setDeleteSkillConfirmOpen(false);
        setSkillToDelete(null);
        
        // CRITICAL: Update state with new array reference to force React re-render
        setSkills(prevSkills => {
          console.log('[Delete Skill] 🔄 Updating state...');
          console.log('[Delete Skill] Previous skills:', prevSkills.length);
          console.log('[Delete Skill] Previous IDs:', prevSkills.map(s => s.id));
          
          const updated = prevSkills.filter(skill => {
            const keep = skill.id !== deletedSkillId;
            if (!keep) {
              console.log('[Delete Skill] Filtering out:', skill.id, skill.title);
            }
            return keep;
          });
          
          console.log('[Delete Skill] Updated skills:', updated.length);
          console.log('[Delete Skill] Updated IDs:', updated.map(s => s.id));
          console.log('[Delete Skill] Arrays are different:', updated !== prevSkills);
          
          return updated;
        });
        
        // Show success message (will auto-clear after 5 seconds via useEffect)
        setSuccess('Skill deleted successfully');
        
        console.log('[Delete Skill] ✅ State update complete');
      } else {
        console.error('[Delete Skill] ❌ Delete failed:', data.message);
        setError(data.message || 'Failed to delete skill');
      }
    } catch (err: any) {
      console.error('[Delete Skill] Exception:', err);
      console.error('[Delete Skill] Error stack:', err.stack);
      setError(err.message || 'Failed to delete skill');
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleOpenSkillDialog = async (skill?: any) => {
    // Prevent editing deleted skills
    if (skill && skill.is_active === false) {
      setError('Cannot edit a deleted skill. Please refresh the page.');
      return;
    }
    
    // Ensure categories are loaded
    await loadCategories();
    
    // Determine which category to use for template loading
    let categoryIdForTemplate = selectedCategoryForSkills?.id;
    
    if (skill) {
      setEditingSkill(skill);
      
      // Determine category_id and subcategory_id from skill data
      // The API returns category_id and subcategory_id directly
      let parentCategoryId: string | null = null;
      let subcategoryId: string | null = null;
      
      // Check if the skill's category_id is actually a subcategory
      const skillCategoryInList = categories.find((c: any) => c.id === skill.category_id);
      
      if (skill.subcategory_id) {
        // Skill has subcategory_id - category_id should be the parent
        // But verify: if category_id points to a subcategory, find its parent
        if (skillCategoryInList && skillCategoryInList.parent_category_id) {
          // category_id is actually a subcategory, use its parent
          parentCategoryId = skillCategoryInList.parent_category_id;
          subcategoryId = skill.category_id; // The category_id is the subcategory
        } else {
          // category_id is the parent, subcategory_id is the subcategory
          parentCategoryId = skill.category_id;
          subcategoryId = skill.subcategory_id;
        }
        categoryIdForTemplate = subcategoryId; // Load template for subcategory
      } else if (skill.category_id) {
        // Skill has only category_id - check if it's a main category or subcategory
        if (skillCategoryInList && skillCategoryInList.parent_category_id) {
          // category_id is a subcategory
          parentCategoryId = skillCategoryInList.parent_category_id;
          subcategoryId = skill.category_id;
          categoryIdForTemplate = skill.category_id;
        } else {
          // category_id is a main category
          parentCategoryId = skill.category_id;
          subcategoryId = null;
          categoryIdForTemplate = skill.category_id;
        }
      } else if (selectedCategoryForSkills) {
        // Fallback to selected category
        if (selectedCategoryForSkills.parent_category_id) {
          parentCategoryId = selectedCategoryForSkills.parent_category_id;
          subcategoryId = selectedCategoryForSkills.id;
          categoryIdForTemplate = selectedCategoryForSkills.id;
        } else {
          parentCategoryId = selectedCategoryForSkills.id;
          subcategoryId = null;
          categoryIdForTemplate = selectedCategoryForSkills.id;
        }
      }
      
      console.log('[Edit Skill] Skill data:', {
        skill_category_id: skill.category_id,
        skill_subcategory_id: skill.subcategory_id,
        resolved_parentCategoryId: parentCategoryId,
        resolved_subcategoryId: subcategoryId
      });
      
      // Load template for the correct category
      if (categoryIdForTemplate) {
        await loadTemplate(categoryIdForTemplate);
      }
      
      // Parse JSONB fields if they're strings
      const parsedSkill: any = { ...skill };
      if (parsedSkill.measurements && typeof parsedSkill.measurements === 'string') {
        try {
          parsedSkill.measurements = JSON.parse(parsedSkill.measurements);
        } catch (e) {
          console.error('Error parsing measurements:', e);
          parsedSkill.measurements = [];
        }
      }
      if (parsedSkill.images && typeof parsedSkill.images === 'string') {
        try {
          parsedSkill.images = JSON.parse(parsedSkill.images);
        } catch (e) {
          console.error('Error parsing images:', e);
          parsedSkill.images = [];
        }
      }
      if (parsedSkill.videos && typeof parsedSkill.videos === 'string') {
        try {
          parsedSkill.videos = JSON.parse(parsedSkill.videos);
        } catch (e) {
          console.error('Error parsing videos:', e);
          parsedSkill.videos = [];
        }
      }
      if (parsedSkill.text_content && typeof parsedSkill.text_content === 'string') {
        try {
          parsedSkill.text_content = JSON.parse(parsedSkill.text_content);
        } catch (e) {
          console.error('Error parsing text_content:', e);
          parsedSkill.text_content = [];
        }
      }
      
      // Ensure category values are set correctly
      const initialForm: any = {
        ...parsedSkill,
        category_id: parentCategoryId || skill.category_id,
        subcategory_id: subcategoryId !== null ? subcategoryId : (skill.subcategory_id || null)
      };
      
      // If this uses Court Dimensions form, map images/videos/measurements to content structure
      const renderComponent = categoryTemplate?.renderComponent || categoryTemplate?.render_component;
      if (renderComponent === 'court_dimensions' || renderComponent === 'CourtDimensionsTemplate') {
        // Initialize content structure if it doesn't exist
        if (!initialForm.content || typeof initialForm.content !== 'object') {
          initialForm.content = {
            Images: [],
            Videos: [],
            Text: [],
            Notes: []
          };
        }
        
        // Map images array to content.Images
        if (parsedSkill.images && Array.isArray(parsedSkill.images) && parsedSkill.images.length > 0) {
          initialForm.content.Images = parsedSkill.images.map((img: any) => ({
            title: img.title || '',
            url: img.url || '',
            Author: img.Author || '',
            textBefore: img.textBefore || '',
            textAfter: img.textAfter || ''
          }));
        } else {
          initialForm.content.Images = initialForm.content.Images || [];
        }
        
        // Map videos array to content.Videos
        if (parsedSkill.videos && Array.isArray(parsedSkill.videos) && parsedSkill.videos.length > 0) {
          initialForm.content.Videos = parsedSkill.videos.map((vid: any) => ({
            title: vid.title || '',
            url: vid.url || '',
            Author: vid.Author || '',
            textBefore: vid.textBefore || '',
            textAfter: vid.textAfter || '',
            videoType: vid.videoType || 'youtube'
          }));
        } else {
          initialForm.content.Videos = initialForm.content.Videos || [];
        }
        
        // Map measurements array to content.Notes
        if (parsedSkill.measurements && Array.isArray(parsedSkill.measurements) && parsedSkill.measurements.length > 0) {
          initialForm.content.Notes = parsedSkill.measurements.map((meas: any) => ({
            NoteTitle: meas.Dimensions || meas.NoteTitle || '',
            Value: meas.Value || {}
          }));
        } else {
          initialForm.content.Notes = initialForm.content.Notes || [];
        }
        
        // Map text_content array to content.Text
        console.log('[Edit Skill] parsedSkill.text_content:', parsedSkill.text_content, 'type:', typeof parsedSkill.text_content, 'isArray:', Array.isArray(parsedSkill.text_content));
        if (parsedSkill.text_content && Array.isArray(parsedSkill.text_content) && parsedSkill.text_content.length > 0) {
          initialForm.content.Text = parsedSkill.text_content.map((text: any) => ({
            title: text.title || '',
            content: text.content || '',
            Author: text.Author || ''
          }));
          console.log('[Edit Skill] Mapped text_content to content.Text:', initialForm.content.Text);
        } else {
          initialForm.content.Text = initialForm.content.Text || [];
          console.log('[Edit Skill] No text_content found or empty, using empty array');
        }
      }
      
      console.log('[Edit Skill] Initial form:', {
        original_category_id: skill.category_id,
        original_subcategory_id: skill.subcategory_id,
        resolved_category_id: initialForm.category_id,
        resolved_subcategory_id: initialForm.subcategory_id,
        content: initialForm.content
      });
      
      // Set form with correct category values
      setSkillForm(initialForm);
    } else {
      setEditingSkill(null);
      
      // Initialize form based on selected category
      // If selectedCategoryForSkills is a subcategory, use its parent as category_id
      let parentCategoryId: string | null = null;
      let subcategoryId: string | null = null;
      
      if (selectedCategoryForSkills) {
        if (selectedCategoryForSkills.parent_category_id) {
          // Selected category is a subcategory
          parentCategoryId = selectedCategoryForSkills.parent_category_id;
          subcategoryId = selectedCategoryForSkills.id;
          categoryIdForTemplate = selectedCategoryForSkills.id;
        } else {
          // Selected category is a main category
          parentCategoryId = selectedCategoryForSkills.id;
          subcategoryId = null;
          categoryIdForTemplate = selectedCategoryForSkills.id;
        }
      }
      
      // Ensure template is loaded
      if (categoryIdForTemplate && !categoryTemplate) {
        await loadTemplate(categoryIdForTemplate);
      }
      
      const initialForm: any = {
        category_id: parentCategoryId,
        subcategory_id: subcategoryId,
        is_active: true // Default to active
      };
      
      // If template exists, initialize fields from template schema
      if (categoryTemplate?.tableSchema?.columns) {
        categoryTemplate.tableSchema.columns.forEach((col: any) => {
          if (col.name === 'is_active') {
            initialForm[col.name] = true;
          } else if (col.type?.includes('BOOLEAN')) {
            initialForm[col.name] = false;
          } else if (col.type?.includes('[]')) {
            initialForm[col.name] = [];
          } else {
            initialForm[col.name] = '';
          }
        });
      }
      
      setSkillForm(initialForm);
    }
    setSkillDialogOpen(true);
  };

  const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
  const [parentCategoryForSubcategory, setParentCategoryForSubcategory] = useState<any>(null);

  const handleOpenCategoryDialog = (category?: any, isSubcategory: boolean = false, parentCategory?: any) => {
    if (category) {
      setEditingCategory(category);
      setIsAddingSubcategory(false);
      setParentCategoryForSubcategory(null);
      setCategoryForm({
        sport: category.sport,
        name: category.name,
        parentCategoryId: category.parent_category_id || '',
        tableName: category.table_name || '',
        description: category.description || '',
        displayOrder: category.display_order || 0,
        categoryType: category.category_type || 'general'
      });
    } else {
      setEditingCategory(null);
      setIsAddingSubcategory(isSubcategory);
      setParentCategoryForSubcategory(parentCategory || null);
      
      // If adding subcategory, use parent category's sport and set parentCategoryId
      const parentCat = parentCategory || null;
      setCategoryForm({
        sport: parentCat?.sport || selectedSportForCategories || 'Badminton',
        name: '',
        parentCategoryId: isSubcategory && parentCat ? parentCat.id : '',
        tableName: '',
        description: '',
        displayOrder: 0,
        categoryType: 'general'
      });
    }
    setCategoryDialogOpen(true);
  };

  const handleAcademyExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setAcademiesLoading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          const academies = jsonData.map((row: any) => ({
            name: row['Name'] || row['name'] || '',
            description: row['Description'] || row['description'] || '',
            country: row['Country'] || row['country'] || '',
            city: row['City'] || row['city'] || '',
            address: row['Address'] || row['address'] || '',
            contact_email: row['Email'] || row['email'] || row['Contact Email'] || row['contact_email'] || '',
            contact_phone: row['Phone'] || row['phone'] || row['Contact Phone'] || row['contact_phone'] || '',
          }));
          const validAcademies = academies.filter((a: any) => a.name);
          if (validAcademies.length === 0) {
            setError('No valid academies found in the Excel file');
            return;
          }
          const response = await fetch('/api/academies/bulk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ academies: validAcademies }),
          });
          const result = await response.json();
          if (result.success) {
            setSuccess(`Successfully imported ${result.data.created.length} academies`);
            setAcademyBulkUploadOpen(false);
            loadAcademies();
          } else {
            setError(result.message || 'Failed to import academies');
          }
        } catch (err: any) {
          setError('Error processing Excel file: ' + err.message);
        } finally {
          setAcademiesLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      setError('Error reading file: ' + err.message);
      setAcademiesLoading(false);
    }
    event.target.value = '';
  };

  // Ranking Stroke Types functions
  const normalizeDeceptionValue = (value: string | undefined | null): string => {
    if (!value) return '';
    const val = String(value).trim();
    if (val === 'Yes' || val === 'No') return val;
    return '';
  };

  const normalizeDifficultyValue = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    if (typeof value === 'number') {
      return Math.max(0, Math.min(5, Math.round(value)));
    }
    const str = String(value).trim();
    const num = parseInt(str, 10);
    if (!isNaN(num) && num >= 0 && num <= 5) return num;
    const lowerStr = str.toLowerCase();
    if (lowerStr === 'low' || lowerStr === '1') return 1;
    if (lowerStr === 'medium' || lowerStr === '2' || lowerStr === '3') return 3;
    if (lowerStr === 'high' || lowerStr === '4' || lowerStr === '5') return 5;
    return 0;
  };

  const loadStrokeTypes = async (sport?: string) => {
    const sportToLoad = sport || currentSport || 'Badminton';
    setStrokeTypesLoading(true);
    try {
      const response = await fetch(`/api/admin/master-strokes?sport=${encodeURIComponent(sportToLoad)}`, {
        headers: adminAuthHeaders(),
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 200));
        setError('Server returned an invalid response. Please check if the backend server is running.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        const normalizedData = data.data.map((st: any) => ({
          ...st,
          difficulty: st.difficulty || '',
          deception: normalizeDeceptionValue(st.deception),
          showOnHomepage: st.showOnHomepage === true,
          areaOfImprovementOptions: parseOptionList(st.areaOfImprovementOptions),
          suggestedPracticesOptions: parseOptionList(st.suggestedPracticesOptions),
          controlledPressureLevel: st.controlledPressureLevel || st.controlledExecutionConstraint || '',
          roles: st.roles || '',
          singles: st.singles === true,
          doubles: st.doubles === true,
        }));
        setStrokeTypes(normalizedData);
      } else {
        setError(data.message || 'Failed to load stroke types');
      }
    } catch (err: any) {
      console.error('Error loading stroke types:', err);
      setError(err.message || 'Failed to load stroke types. Please check if the backend server is running.');
    } finally {
      setStrokeTypesLoading(false);
    }
  };

  const handleOpenStrokeTypeDialog = (strokeType?: RankingStrokeType) => {
    if (strokeType) {
      setEditingStrokeType(strokeType);
      const formDataToSet = {
        id: strokeType.id,
        code: strokeType.code || '',
        courtPosition: strokeType.courtPosition || '',
        halfSide: strokeType.halfSide || '',
        strokeType: strokeType.strokeType || '',
        strokeAction: strokeType.strokeAction || '',
        playType: strokeType.playType || '',
        shotType: strokeType.shotType || '',
        placement: strokeType.placement || '',
        difficulty: normalizeDifficultyValue(strokeType.difficulty),
        deception: normalizeDeceptionValue(strokeType.deception),
        returnResponse: strokeType.returnResponse || '',
        referenceVideos: strokeType.referenceVideos || '',
        strokeVideoSections: normalizeStrokeVideoSections(strokeType.strokeVideoSections, strokeType.referenceVideos),
        areaOfImprovementOptions: parseOptionList(strokeType.areaOfImprovementOptions),
        suggestedPracticesOptions: parseOptionList(strokeType.suggestedPracticesOptions),
        controlledPressureLevel: strokeType.controlledPressureLevel || (strokeType as any).controlledExecutionConstraint || '',
        foot: strokeType.foot || '',
        roles: strokeType.roles || '',
        singles: strokeType.singles === true,
        doubles: strokeType.doubles === true,
        isActive: strokeType.isActive !== undefined ? strokeType.isActive : true,
        showOnHomepage: strokeType.showOnHomepage === true,
        displayOrder: strokeType.displayOrder !== undefined ? strokeType.displayOrder : 0,
      };
      setStrokeTypeFormData(formDataToSet);
      setStrokeVideoSections(formDataToSet.strokeVideoSections);
      setStrokeMediaGroups({
        regularTrainingVideos: normalizeStrokeMediaItems(strokeType.regularTrainingVideos),
        creatorReferenceVideos: normalizeStrokeMediaItems(strokeType.creatorReferenceVideos),
        amateurSampleVideos: normalizeStrokeMediaItems(strokeType.amateurSampleVideos),
        contestVideos: normalizeStrokeMediaItems(strokeType.contestVideos),
      });
    } else {
      setEditingStrokeType(null);
      setStrokeTypeFormData({
        code: '',
        courtPosition: '',
        halfSide: '',
        strokeType: '',
        strokeAction: '',
        playType: '',
        shotType: '',
        placement: '',
        difficulty: 0,
        deception: '',
        returnResponse: '',
        referenceVideos: '',
        strokeVideoSections: createEmptyStrokeVideoSections(),
        areaOfImprovementOptions: ['Consult the coaches'],
        suggestedPracticesOptions: ['Consult the coaches'],
        controlledPressureLevel: '',
        foot: '',
        roles: '',
        singles: false,
        doubles: false,
        isActive: true,
        showOnHomepage: false,
        displayOrder: 0,
      });
      setStrokeVideoSections(createEmptyStrokeVideoSections());
      setStrokeMediaGroups({
        regularTrainingVideos: [createEmptyStrokeMediaItem(1)],
        creatorReferenceVideos: [createEmptyStrokeMediaItem(1)],
        amateurSampleVideos: [createEmptyStrokeMediaItem(1)],
        contestVideos: [createEmptyStrokeMediaItem(1)],
      });
    }
    setStrokeAreaOptionInput('');
    setStrokeSuggestedOptionInput('');
    setStrokeTypesDialogOpen(true);
  };

  const handleCloseStrokeTypeDialog = () => {
    setStrokeTypesDialogOpen(false);
    setEditingStrokeType(null);
    setStrokeTypeFormData({
      code: '',
      courtPosition: '',
      halfSide: '',
      strokeType: '',
      strokeAction: '',
      playType: '',
      shotType: '',
      placement: '',
      difficulty: 0,
      deception: '',
      returnResponse: '',
      referenceVideos: '',
      strokeVideoSections: createEmptyStrokeVideoSections(),
      areaOfImprovementOptions: ['Consult the coaches'],
      suggestedPracticesOptions: ['Consult the coaches'],
      controlledPressureLevel: '',
      foot: '',
      roles: '',
      singles: false,
      doubles: false,
      isActive: true,
      showOnHomepage: false,
      displayOrder: 0,
    });
    setStrokeVideoSections(createEmptyStrokeVideoSections());
    setStrokeMediaGroups({
      regularTrainingVideos: [createEmptyStrokeMediaItem(1)],
      creatorReferenceVideos: [createEmptyStrokeMediaItem(1)],
      amateurSampleVideos: [createEmptyStrokeMediaItem(1)],
      contestVideos: [createEmptyStrokeMediaItem(1)],
    });
    setStrokeAreaOptionInput('');
    setStrokeSuggestedOptionInput('');
  };

  const handleAddStrokeOption = (
    field: 'areaOfImprovementOptions' | 'suggestedPracticesOptions',
    rawValue: string
  ) => {
    const value = rawValue.trim();
    if (!value) return;
    const existing = parseOptionList(strokeTypeFormData[field]);
    if (existing.some((option) => option.toLowerCase() === value.toLowerCase())) return;
    const withoutDefault = existing.filter((option) => option !== 'Consult the coaches');
    setStrokeTypeFormData({
      ...strokeTypeFormData,
      [field]: [...withoutDefault, value],
    });
  };

  const handleRemoveStrokeOption = (
    field: 'areaOfImprovementOptions' | 'suggestedPracticesOptions',
    optionToRemove: string
  ) => {
    const next = parseOptionList(strokeTypeFormData[field]).filter((option) => option !== optionToRemove);
    setStrokeTypeFormData({
      ...strokeTypeFormData,
      [field]: next.length > 0 ? next : ['Consult the coaches'],
    });
  };

  const handleSaveStrokeType = async () => {
    if (!strokeTypeFormData.code) {
      setError('Code is required');
      return;
    }
    setStrokeTypesLoading(true);
    try {
      const url = editingStrokeType?.id
        ? `/api/admin/master-strokes/${editingStrokeType.id}?sport=${encodeURIComponent(currentSport)}`
        : `/api/admin/master-strokes?sport=${encodeURIComponent(currentSport)}`;
      const method = editingStrokeType?.id ? 'PUT' : 'POST';
      const cleanedStrokeVideoSections = strokeVideoSections.map((section) => {
        const videos = (section.videos || [])
          .map((video, index) => ({
            displayOrder: Number(video.displayOrder ?? video.sequence ?? index + 1),
            sequence: Number(video.displayOrder ?? video.sequence ?? index + 1),
            title: String(video.title || '').trim(),
            videoUrl: String(video.videoUrl || '').trim(),
            text: String(video.text || '').trim(),
            imageUrls: (video.imageUrls || []).map((url) => String(url || '').trim()).filter(Boolean),
            sponsorName: String(video.sponsorName || '').trim(),
            sponsorLogoUrl: String(video.sponsorLogoUrl || '').trim(),
          }))
          .filter((video) => video.videoUrl || video.title || video.text || video.imageUrls.length > 0 || video.sponsorName || video.sponsorLogoUrl)
          .sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
        const firstVideo = videos.find((video) => video.videoUrl) || videos[0];
        return {
          ...section,
          videos,
          videoUrl: firstVideo?.videoUrl || '',
          text: firstVideo?.text || '',
          imageUrls: firstVideo?.imageUrls || [],
        };
      });
      const validVideoUrls = getStrokeSectionVideoUrls(cleanedStrokeVideoSections);
      const referenceVideosString = validVideoUrls.length > 0 ? validVideoUrls.join(',') : '';
      const cleanStrokeMediaItems = (items: StrokeMediaItem[]) =>
        items
          .map((item, index) => ({
            displayOrder: Number(item.displayOrder ?? item.sequence ?? index + 1),
            sequence: Number(item.displayOrder ?? item.sequence ?? index + 1),
            title: String(item.title || '').trim(),
            url: String(item.url || '').trim(),
            name: String(item.name || '').trim(),
            notes: String(item.notes || '').trim(),
            type: item.type || 'video',
            sponsorName: String(item.sponsorName || '').trim(),
            sponsorLogoUrl: String(item.sponsorLogoUrl || '').trim(),
            isActive: item.isActive !== false,
          }))
          .filter((item) => item.url || item.title || item.name || item.notes || item.sponsorName || item.sponsorLogoUrl);
      const difficultyValue = typeof strokeTypeFormData.difficulty === 'number' 
        ? (strokeTypeFormData.difficulty > 0 ? String(strokeTypeFormData.difficulty) : '') 
        : (strokeTypeFormData.difficulty || '');
      const payload = {
        ...strokeTypeFormData,
        sport: currentSport,
        difficulty: difficultyValue,
        referenceVideos: referenceVideosString,
        strokeVideoSections: cleanedStrokeVideoSections,
        regularTrainingVideos: cleanStrokeMediaItems(strokeMediaGroups.regularTrainingVideos),
        creatorReferenceVideos: cleanStrokeMediaItems(strokeMediaGroups.creatorReferenceVideos),
        amateurSampleVideos: cleanStrokeMediaItems(strokeMediaGroups.amateurSampleVideos),
        contestVideos: cleanStrokeMediaItems(strokeMediaGroups.contestVideos),
        areaOfImprovementOptions: parseOptionList(strokeTypeFormData.areaOfImprovementOptions),
        suggestedPracticesOptions: parseOptionList(strokeTypeFormData.suggestedPracticesOptions),
      };
      const response = await fetch(url, {
        method,
        headers: adminJsonHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        if (data.data) {
          const normalizedSavedStroke = {
            ...data.data,
            difficulty: data.data.difficulty || '',
            deception: normalizeDeceptionValue(data.data.deception),
            showOnHomepage: data.data.showOnHomepage === true,
            areaOfImprovementOptions: parseOptionList(data.data.areaOfImprovementOptions),
            suggestedPracticesOptions: parseOptionList(data.data.suggestedPracticesOptions),
            controlledPressureLevel: data.data.controlledPressureLevel || data.data.controlledExecutionConstraint || '',
            roles: data.data.roles || '',
            singles: data.data.singles === true,
            doubles: data.data.doubles === true,
          };
          setStrokeTypes((prev) => {
            const exists = prev.some((item) => item.id === normalizedSavedStroke.id);
            return exists
              ? prev.map((item) => item.id === normalizedSavedStroke.id ? normalizedSavedStroke : item)
              : [normalizedSavedStroke, ...prev];
          });
        }
        setSuccess(editingStrokeType ? 'Stroke type updated successfully!' : 'Stroke type created successfully');
        await loadStrokeTypes(currentSport);
        handleCloseStrokeTypeDialog();
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(data.message || 'Failed to save stroke type');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save stroke type');
    } finally {
      setStrokeTypesLoading(false);
    }
  };

  const handleDeleteStrokeType = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this stroke type?')) {
      return;
    }
    setStrokeTypesLoading(true);
    try {
      const response = await fetch(`/api/admin/master-strokes/${id}?sport=${encodeURIComponent(currentSport)}`, {
        method: 'DELETE',
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Stroke type deleted successfully');
        loadStrokeTypes(currentSport);
      } else {
        setError(data.message || 'Failed to delete stroke type');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete stroke type');
    } finally {
      setStrokeTypesLoading(false);
    }
  };

  const handleStrokeVideoSectionChange = (sectionKey: string, field: 'displayOrder', value: string) => {
    setStrokeVideoSections((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) return section;
        if (field === 'displayOrder') {
          return { ...section, displayOrder: Number(value) || 0 };
        }
        return section;
      })
    );
  };

  const handleStrokeSectionVideoChange = (
    sectionKey: string,
    index: number,
    field: 'displayOrder' | 'title' | 'videoUrl' | 'text' | 'imageUrlsText' | 'sponsorName' | 'sponsorLogoUrl',
    value: string
  ) => {
    setStrokeVideoSections((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) return section;
        const currentVideos = section.videos && section.videos.length > 0
          ? section.videos
          : [{ displayOrder: 1, sequence: 1, title: section.label, videoUrl: section.videoUrl || '', text: section.text || '', imageUrls: section.imageUrls || [], sponsorName: '', sponsorLogoUrl: '' }];
        const videos = currentVideos.map((video, videoIndex) => {
          if (videoIndex !== index) return video;
          if (field === 'displayOrder') {
            const displayOrder = Number(value) || 0;
            return { ...video, displayOrder, sequence: displayOrder };
          }
          if (field === 'imageUrlsText') {
            return {
              ...video,
              imageUrls: value
                .split(',')
                .map((url) => url.trim())
                .filter((url) => url.length > 0),
            };
          }
          return { ...video, [field]: value };
        });
        const firstVideo = videos[0];
        return {
          ...section,
          videos,
          videoUrl: firstVideo?.videoUrl || '',
          text: firstVideo?.text || '',
          imageUrls: firstVideo?.imageUrls || [],
        };
      })
    );
  };

  const handleAddStrokeSectionVideo = (sectionKey: string) => {
    setStrokeVideoSections((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) return section;
        const currentVideos = section.videos && section.videos.length > 0
          ? section.videos
          : [{ displayOrder: 1, sequence: 1, title: section.label, videoUrl: section.videoUrl || '', text: section.text || '', imageUrls: section.imageUrls || [], sponsorName: '', sponsorLogoUrl: '' }];
        const nextOrder = currentVideos.reduce((max, video, index) => Math.max(max, Number(video.displayOrder ?? video.sequence ?? index + 1)), 0) + 1;
        return {
          ...section,
          videos: [...currentVideos, { displayOrder: nextOrder, sequence: nextOrder, title: '', videoUrl: '', text: '', imageUrls: [], sponsorName: '', sponsorLogoUrl: '' }],
        };
      })
    );
  };

  const handleRemoveStrokeSectionVideo = (sectionKey: string, index: number) => {
    setStrokeVideoSections((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) return section;
        const currentVideos = section.videos && section.videos.length > 0
          ? section.videos
          : [{ displayOrder: 1, sequence: 1, title: section.label, videoUrl: section.videoUrl || '', text: section.text || '', imageUrls: section.imageUrls || [], sponsorName: '', sponsorLogoUrl: '' }];
        const videos = currentVideos.filter((_, videoIndex) => videoIndex !== index);
        const safeVideos = videos.length > 0 ? videos : [{ displayOrder: 1, sequence: 1, title: '', videoUrl: '', text: '', imageUrls: [], sponsorName: '', sponsorLogoUrl: '' }];
        const firstVideo = safeVideos[0];
        return {
          ...section,
          videos: safeVideos,
          videoUrl: firstVideo?.videoUrl || '',
          text: firstVideo?.text || '',
          imageUrls: firstVideo?.imageUrls || [],
        };
      })
    );
  };

  const handleStrokeMediaChange = (
    group: StrokeMediaGroupKey,
    index: number,
    field: keyof StrokeMediaItem,
    value: string | number
  ) => {
    setStrokeMediaGroups((prev) => ({
      ...prev,
      [group]: prev[group].map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (field === 'displayOrder' || field === 'sequence') {
          const displayOrder = Number(value) || 0;
          return { ...item, displayOrder, sequence: displayOrder };
        }
        if (field === 'url') {
          const url = String(value);
          const isImage = /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
          return { ...item, url, type: isImage ? 'image' : 'video' };
        }
        return { ...item, [field]: value };
      }),
    }));
  };

  const handleAddStrokeMediaItem = (group: StrokeMediaGroupKey) => {
    setStrokeMediaGroups((prev) => {
      const nextOrder = (prev[group] || []).reduce((max, item, index) => Math.max(max, Number(item.displayOrder ?? item.sequence ?? index + 1)), 0) + 1;
      return { ...prev, [group]: [...prev[group], createEmptyStrokeMediaItem(nextOrder)] };
    });
  };

  const handleRemoveStrokeMediaItem = (group: StrokeMediaGroupKey, index: number) => {
    setStrokeMediaGroups((prev) => {
      const nextItems = prev[group].filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, [group]: nextItems.length > 0 ? nextItems : [createEmptyStrokeMediaItem(1)] };
    });
  };

  // Assessment Skills Management Functions
  const [assessmentSkillCategories, setAssessmentSkillCategories] = useState<string[]>([]);
  const [categoryManagementOpen, setCategoryManagementOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  
  // Load categories for a sport/assessment type from skills + persisted category list
  const loadAllCategoriesForSport = async (sport: string, assessmentType?: 'high-level' | 'master-training') => {
    try {
      const typeToLoad = assessmentType || selectedAssessmentType;
      const [skillsResponse, persistedCategoriesResponse] = await Promise.all([
        fetch(`/api/admin/assessment-skills?sport=${sport}&assessmentType=${typeToLoad}`, {
          headers: adminAuthHeaders(),
        }),
        fetch(`/api/admin/assessment-skill-categories?sport=${sport}&assessmentType=${typeToLoad}`, {
          headers: adminAuthHeaders(),
        }),
      ]);
      
      const [skillsData, persistedCategoriesData] = await Promise.all([
        skillsResponse.json(),
        persistedCategoriesResponse.json()
      ]);
      
      const allSkills = skillsData.success ? (skillsData.data || []) : [];
      const persistedCategories = persistedCategoriesData.success
        ? (persistedCategoriesData.data || []).map((c: any) => c.categoryName).filter(Boolean)
        : [];
      
      const uniqueCategories = [...new Set([
        ...allSkills.map((s: any) => s.category).filter(Boolean),
        ...persistedCategories
      ])] as string[];
      return uniqueCategories.sort();
    } catch (err: any) {
      console.error('Error loading categories:', err);
      return [];
    }
  };
  
  const loadAssessmentSkills = async (sport?: string, assessmentType?: 'high-level' | 'master-training') => {
    const sportToLoad = sport || selectedSportForAssessment;
    const typeToLoad = assessmentType || selectedAssessmentType;
    setAssessmentSkillsLoading(true);
    try {
      const response = await fetch(
        `/api/admin/assessment-skills?sport=${sportToLoad}&assessmentType=${typeToLoad}`,
        { headers: adminAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        const skills = data.data || [];
        setAssessmentSkills(skills);
        
        // Always merge categories from skills with existing (preserve manually added categories)
        const categoriesFromSkills = await loadAllCategoriesForSport(sportToLoad, typeToLoad);
        const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
        setAssessmentSkillCategories(mergedCategories.sort());
      } else {
        setError(data.message || 'Failed to load assessment skills');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assessment skills');
    } finally {
      setAssessmentSkillsLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const categoryName = newCategoryName.trim();
    if (!categoryName) return;
    
    if (assessmentSkillCategories.includes(categoryName)) {
      setError('Category already exists');
      return;
    }

    try {
      const response = await fetch('/api/admin/assessment-skill-categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sport: selectedSportForAssessment,
          assessmentType: selectedAssessmentType,
          categoryName,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Failed to add category');
        return;
      }
      setAssessmentSkillCategories([...assessmentSkillCategories, categoryName].sort());
      setNewCategoryName('');
      setSuccess('Category added successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to add category');
    }
  };

  const handleDeleteAssessmentCategory = async (categoryName: string) => {
    if (!window.confirm(`Are you sure you want to delete category "${categoryName}"? This will not delete skills using this category.`)) {
      return;
    }

    try {
      const params = new URLSearchParams({
        sport: selectedSportForAssessment,
        assessmentType: selectedAssessmentType,
        categoryName,
      });
      const response = await fetch(`/api/admin/assessment-skill-categories?${params.toString()}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Failed to delete category');
        return;
      }
      setAssessmentSkillCategories(assessmentSkillCategories.filter(cat => cat !== categoryName));
      setSuccess('Category deleted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to delete category');
    }
  };

  const parseOptionList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      const cleaned = value.map((v) => String(v).trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : ['Consult the coaches'];
    }
    if (typeof value === 'string') {
      const cleaned = value.split(',').map((v) => v.trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : ['Consult the coaches'];
    }
    return ['Consult the coaches'];
  };

  const getAssessmentLevelStatus = (
    skill: any,
    level: 'beginner' | 'intermediate' | 'advanced' | 'pro'
  ): { label: string; color: 'success' | 'warning' } => {
    const isApplicable =
      level === 'beginner'
        ? skill.showInBeginner !== false
        : level === 'intermediate'
          ? skill.showInIntermediate !== false
          : level === 'advanced'
            ? skill.showInAdvanced !== false
            : skill.showInPro !== false;

    return isApplicable
      ? { label: 'Applicable', color: 'success' }
      : { label: 'Next Level', color: 'warning' };
  };

  const getFilteredAssessmentSkills = () => {
    return [...assessmentSkills]
      .filter((skill) => selectedCategoryFilter === 'all' || skill.category === selectedCategoryFilter)
      .sort((a, b) => {
        const orderA = a.displayOrder ?? 9999;
        const orderB = b.displayOrder ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        if (a.category !== b.category) return (a.category || '').localeCompare(b.category || '');
        return (a.itemName || '').localeCompare(b.itemName || '');
      });
  };

  const handleOpenAssessmentSkillDialog = async (skill?: any) => {
    // Determine assessment type based on current view or skill data
    let assessmentTypeToUse = selectedAssessmentType;
    if (activeView === 'skill-assessment-high-level') {
      assessmentTypeToUse = 'high-level';
    } else if (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') {
      assessmentTypeToUse = 'master-training';
    }
    
    // Ensure assessment skills are loaded to get categories
    if (assessmentSkills.length === 0 || selectedAssessmentType !== assessmentTypeToUse) {
      await loadAssessmentSkills(selectedSportForAssessment, assessmentTypeToUse);
    }
    
    // Always merge categories from skills with existing (preserve manually added categories)
    const categoriesFromSkills = await loadAllCategoriesForSport(selectedSportForAssessment, assessmentTypeToUse);
    const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
    setAssessmentSkillCategories(mergedCategories.sort());
    
    if (skill) {
      setEditingAssessmentSkill(skill);
      setAssessmentSkillFormData({
        sport: skill.sport || selectedSportForAssessment,
        assessmentType: skill.assessmentType || assessmentTypeToUse,
        itemName: skill.itemName || '',
        category: skill.category || '',
        displayOrder: skill.displayOrder !== undefined && skill.displayOrder !== null ? skill.displayOrder : undefined,
        isActive: skill.isActive !== undefined ? skill.isActive : true,
        areaOfImprovementOptions: parseOptionList(skill.areaOfImprovementOptions),
        suggestedPracticesOptions: parseOptionList(skill.suggestedPracticesOptions),
        showInBeginner: skill.showInBeginner !== false,
        showInIntermediate: skill.showInIntermediate !== false,
        showInAdvanced: skill.showInAdvanced !== false,
        showInPro: skill.showInPro !== false,
        showOnHomepage: skill.showOnHomepage === true,
      });
    } else {
      setEditingAssessmentSkill(null);
      setAssessmentSkillFormData({
        sport: selectedSportForAssessment,
        assessmentType: assessmentTypeToUse,
        itemName: '',
        category: '',
        displayOrder: undefined,
        isActive: true,
        areaOfImprovementOptions: ['Consult the coaches'],
        suggestedPracticesOptions: ['Consult the coaches'],
        showInBeginner: true,
        showInIntermediate: true,
        showInAdvanced: true,
        showInPro: true,
        showOnHomepage: false,
      });
    }
    setAreaOptionInput('');
    setSuggestedOptionInput('');
    setAssessmentSkillsDialogOpen(true);
  };

  const handleCloseAssessmentSkillDialog = () => {
    setAssessmentSkillsDialogOpen(false);
    setEditingAssessmentSkill(null);
    setAssessmentSkillFormData({
      sport: selectedSportForAssessment,
      assessmentType: selectedAssessmentType,
      itemName: '',
      category: '',
      displayOrder: undefined,
      isActive: true,
      areaOfImprovementOptions: ['Consult the coaches'],
      suggestedPracticesOptions: ['Consult the coaches'],
      showInBeginner: true,
      showInIntermediate: true,
      showInAdvanced: true,
      showInPro: true,
      showOnHomepage: false,
    });
    setAreaOptionInput('');
    setSuggestedOptionInput('');
  };

  const handleAddAssessmentOption = (
    field: 'areaOfImprovementOptions' | 'suggestedPracticesOptions',
    rawValue: string
  ) => {
    const value = rawValue.trim();
    if (!value) return;
    const existing = (assessmentSkillFormData[field] || []).map((v) => String(v).trim());
    if (existing.some((v) => v.toLowerCase() === value.toLowerCase())) return;
    const next = [...existing, value];
    setAssessmentSkillFormData({
      ...assessmentSkillFormData,
      [field]: next,
    });
  };

  const handleRemoveAssessmentOption = (
    field: 'areaOfImprovementOptions' | 'suggestedPracticesOptions',
    optionToRemove: string
  ) => {
    const filtered = (assessmentSkillFormData[field] || []).filter((opt) => opt !== optionToRemove);
    setAssessmentSkillFormData({
      ...assessmentSkillFormData,
      [field]: filtered.length > 0 ? filtered : ['Consult the coaches'],
    });
  };

  const handleCloseCategoryManagement = () => {
    setCategoryManagementOpen(false);
    setNewCategoryName('');
  };

  const handleSaveAssessmentSkill = async () => {
    if (!assessmentSkillFormData.itemName || !assessmentSkillFormData.category) {
      setError('Item name and category are required');
      return;
    }
    setAssessmentSkillsLoading(true);
    try {
      const url = editingAssessmentSkill?.id
        ? `/api/admin/assessment-skills/${editingAssessmentSkill.id}`
        : '/api/admin/assessment-skills';
      const method = editingAssessmentSkill?.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: adminJsonHeaders(),
        body: JSON.stringify(assessmentSkillFormData),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(editingAssessmentSkill ? 'Assessment skill updated successfully!' : 'Assessment skill created successfully');
        await loadAssessmentSkills(assessmentSkillFormData.sport, assessmentSkillFormData.assessmentType);
        handleCloseAssessmentSkillDialog();
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(data.message || 'Failed to save assessment skill');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save assessment skill');
    } finally {
      setAssessmentSkillsLoading(false);
    }
  };

  const handleToggleAssessmentSkillLevel = async (
    skill: any,
    levelKey: 'showInBeginner' | 'showInIntermediate' | 'showInAdvanced' | 'showInPro',
    checked: boolean
  ) => {
    setAssessmentSkillsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills/${skill.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sport: skill.sport,
          assessmentType: skill.assessmentType,
          itemName: skill.itemName,
          category: skill.category,
          displayOrder: skill.displayOrder,
          isActive: skill.isActive !== false,
          areaOfImprovementOptions: skill.areaOfImprovementOptions || ['Consult the coaches'],
          suggestedPracticesOptions: skill.suggestedPracticesOptions || ['Consult the coaches'],
          showInBeginner: levelKey === 'showInBeginner' ? checked : skill.showInBeginner !== false,
          showInIntermediate: levelKey === 'showInIntermediate' ? checked : skill.showInIntermediate !== false,
          showInAdvanced: levelKey === 'showInAdvanced' ? checked : skill.showInAdvanced !== false,
          showInPro: levelKey === 'showInPro' ? checked : skill.showInPro !== false,
          showOnHomepage: skill.showOnHomepage === true,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setAssessmentSkills((prev) =>
          prev.map((s) => (s.id === skill.id ? { ...s, [levelKey]: checked } : s))
        );
      } else {
        setError(data.message || 'Failed to update level mapping');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update level mapping');
    } finally {
      setAssessmentSkillsLoading(false);
    }
  };

  const handleDeleteAssessmentSkill = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this assessment skill?')) {
      return;
    }
    setAssessmentSkillsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills/${id}`, {
        method: 'DELETE',
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Assessment skill deleted successfully');
        loadAssessmentSkills();
      } else {
        setError(data.message || 'Failed to delete assessment skill');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete assessment skill');
    } finally {
      setAssessmentSkillsLoading(false);
    }
  };

  // Fitness Assessment Management Functions
  const loadAllFitnessCategoriesForSport = async (sport: string) => {
    try {
      const response = await fetch(`/api/admin/assessment-skills?sport=${sport}&assessmentType=fitness`, {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      const allFitness = data.success ? (data.data || []) : [];
      const uniqueCategories = [...new Set(allFitness.map((f: any) => f.category).filter(Boolean))] as string[];
      return uniqueCategories.sort();
    } catch (err: any) {
      console.error('Error loading fitness categories:', err);
      return [];
    }
  };

  const loadFitnessAssessments = async (sport?: string) => {
    const sportToLoad = sport || selectedSportForFitness;
    setFitnessAssessmentsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills?sport=${sportToLoad}&assessmentType=fitness`, {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        const fitness = data.data || [];
        setFitnessAssessments(fitness);
        const categoriesFromFitness = await loadAllFitnessCategoriesForSport(sportToLoad);
        const mergedCategories = [...new Set([...categoriesFromFitness, ...fitnessCategories])];
        setFitnessCategories(mergedCategories.sort());
      } else {
        setError(data.message || 'Failed to load fitness assessments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load fitness assessments');
    } finally {
      setFitnessAssessmentsLoading(false);
    }
  };

  const handleAddFitnessCategory = () => {
    const categoryName = newFitnessCategoryName.trim();
    if (!categoryName) return;
    if (fitnessCategories.includes(categoryName)) {
      setError('Category already exists');
      return;
    }
    setFitnessCategories([...fitnessCategories, categoryName].sort());
    setNewFitnessCategoryName('');
    setSuccess('Category added successfully');
  };

  const handleDeleteFitnessCategory = (categoryName: string) => {
    if (!window.confirm(`Are you sure you want to delete category "${categoryName}"? This will not delete fitness items using this category.`)) {
      return;
    }
    setFitnessCategories(fitnessCategories.filter(cat => cat !== categoryName));
    setSuccess('Category deleted successfully');
  };

  const handleOpenFitnessDialog = async (fitness?: any) => {
    if (fitnessAssessments.length === 0) {
      await loadFitnessAssessments();
    }
    const categoriesFromFitness = await loadAllFitnessCategoriesForSport(selectedSportForFitness);
    const mergedCategories = [...new Set([...categoriesFromFitness, ...fitnessCategories])];
    setFitnessCategories(mergedCategories.sort());
    
    if (fitness) {
      setEditingFitnessAssessment(fitness);
      setFitnessFormData({
        sport: fitness.sport || selectedSportForFitness,
        assessmentType: 'fitness',
        itemName: fitness.itemName || '',
        category: fitness.category || '',
        displayOrder: fitness.displayOrder !== undefined && fitness.displayOrder !== null ? fitness.displayOrder : undefined,
        isActive: fitness.isActive !== undefined ? fitness.isActive : true,
      });
    } else {
      setEditingFitnessAssessment(null);
      setFitnessFormData({
        sport: selectedSportForFitness,
        assessmentType: 'fitness',
        itemName: '',
        category: '',
        displayOrder: undefined,
        isActive: true,
      });
    }
    setFitnessAssessmentsDialogOpen(true);
  };

  const handleCloseFitnessDialog = () => {
    setFitnessAssessmentsDialogOpen(false);
    setEditingFitnessAssessment(null);
    setFitnessFormData({
      sport: selectedSportForFitness,
      assessmentType: 'fitness',
      itemName: '',
      category: '',
      displayOrder: undefined,
      isActive: true,
    });
  };

  const handleSaveFitnessAssessment = async () => {
    if (!fitnessFormData.itemName || !fitnessFormData.category) {
      setError('Item name and category are required');
      return;
    }
    setFitnessAssessmentsLoading(true);
    try {
      const url = editingFitnessAssessment?.id
        ? `/api/admin/assessment-skills/${editingFitnessAssessment.id}`
        : '/api/admin/assessment-skills';
      const method = editingFitnessAssessment?.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: adminJsonHeaders(),
        body: JSON.stringify(fitnessFormData),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(editingFitnessAssessment ? 'Fitness assessment updated successfully!' : 'Fitness assessment created successfully');
        await loadFitnessAssessments(fitnessFormData.sport);
        handleCloseFitnessDialog();
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(data.message || 'Failed to save fitness assessment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save fitness assessment');
    } finally {
      setFitnessAssessmentsLoading(false);
    }
  };

  const handleDeleteFitnessAssessment = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fitness assessment?')) {
      return;
    }
    setFitnessAssessmentsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills/${id}`, {
        method: 'DELETE',
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Fitness assessment deleted successfully');
        loadFitnessAssessments();
      } else {
        setError(data.message || 'Failed to delete fitness assessment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete fitness assessment');
    } finally {
      setFitnessAssessmentsLoading(false);
    }
  };

  // Drill Assessment Management Functions
  const loadAllDrillCategoriesForSport = async (sport: string) => {
    try {
      const response = await fetch(`/api/admin/assessment-skills?sport=${sport}&assessmentType=drill`, {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      const allDrills = data.success ? (data.data || []) : [];
      const uniqueCategories = [...new Set(allDrills.map((d: any) => d.category).filter(Boolean))] as string[];
      return uniqueCategories.sort();
    } catch (err: any) {
      console.error('Error loading drill categories:', err);
      return [];
    }
  };

  const loadDrillAssessments = async (sport?: string) => {
    const sportToLoad = sport || selectedSportForDrill;
    setDrillAssessmentsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills?sport=${sportToLoad}&assessmentType=drill`, {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        const drills = data.data || [];
        setDrillAssessments(drills);
        const categoriesFromDrills = await loadAllDrillCategoriesForSport(sportToLoad);
        const mergedCategories = [...new Set([...categoriesFromDrills, ...drillCategories])];
        setDrillCategories(mergedCategories.sort());
      } else {
        setError(data.message || 'Failed to load drill assessments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load drill assessments');
    } finally {
      setDrillAssessmentsLoading(false);
    }
  };

  const handleAddDrillCategory = () => {
    const categoryName = newDrillCategoryName.trim();
    if (!categoryName) return;
    if (drillCategories.includes(categoryName)) {
      setError('Category already exists');
      return;
    }
    setDrillCategories([...drillCategories, categoryName].sort());
    setNewDrillCategoryName('');
    setSuccess('Category added successfully');
  };

  const handleDeleteDrillCategory = (categoryName: string) => {
    if (!window.confirm(`Are you sure you want to delete category "${categoryName}"? This will not delete drill items using this category.`)) {
      return;
    }
    setDrillCategories(drillCategories.filter(cat => cat !== categoryName));
    setSuccess('Category deleted successfully');
  };

  const handleOpenDrillDialog = async (drill?: any) => {
    if (drillAssessments.length === 0) {
      await loadDrillAssessments();
    }
    const categoriesFromDrills = await loadAllDrillCategoriesForSport(selectedSportForDrill);
    const mergedCategories = [...new Set([...categoriesFromDrills, ...drillCategories])];
    setDrillCategories(mergedCategories.sort());
    
    if (drill) {
      setEditingDrillAssessment(drill);
      setDrillFormData({
        sport: drill.sport || selectedSportForDrill,
        assessmentType: 'drill',
        itemName: drill.itemName || '',
        category: drill.category || '',
        displayOrder: drill.displayOrder !== undefined && drill.displayOrder !== null ? drill.displayOrder : undefined,
        isActive: drill.isActive !== undefined ? drill.isActive : true,
      });
    } else {
      setEditingDrillAssessment(null);
      setDrillFormData({
        sport: selectedSportForDrill,
        assessmentType: 'drill',
        itemName: '',
        category: '',
        displayOrder: undefined,
        isActive: true,
      });
    }
    setDrillAssessmentsDialogOpen(true);
  };

  const handleCloseDrillDialog = () => {
    setDrillAssessmentsDialogOpen(false);
    setEditingDrillAssessment(null);
    setDrillFormData({
      sport: selectedSportForDrill,
      assessmentType: 'drill',
      itemName: '',
      category: '',
      displayOrder: undefined,
      isActive: true,
    });
  };

  const handleSaveDrillAssessment = async () => {
    if (!drillFormData.itemName || !drillFormData.category) {
      setError('Item name and category are required');
      return;
    }
    setDrillAssessmentsLoading(true);
    try {
      const url = editingDrillAssessment?.id
        ? `/api/admin/assessment-skills/${editingDrillAssessment.id}`
        : '/api/admin/assessment-skills';
      const method = editingDrillAssessment?.id ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: adminJsonHeaders(),
        body: JSON.stringify(drillFormData),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(editingDrillAssessment ? 'Drill assessment updated successfully!' : 'Drill assessment created successfully');
        await loadDrillAssessments(drillFormData.sport);
        handleCloseDrillDialog();
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(data.message || 'Failed to save drill assessment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save drill assessment');
    } finally {
      setDrillAssessmentsLoading(false);
    }
  };

  const handleDeleteDrillAssessment = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this drill assessment?')) {
      return;
    }
    setDrillAssessmentsLoading(true);
    try {
      const response = await fetch(`/api/admin/assessment-skills/${id}`, {
        method: 'DELETE',
        headers: adminAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Drill assessment deleted successfully');
        loadDrillAssessments();
      } else {
        setError(data.message || 'Failed to delete drill assessment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete drill assessment');
    } finally {
      setDrillAssessmentsLoading(false);
    }
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet) {
        setError('No worksheet found in Excel file');
        return;
      }
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        defval: null,
        raw: false,
        blankrows: false
      });
      if (jsonData.length === 0) {
        setError('Excel file appears to be empty or has no data rows.');
        return;
      }
      const firstRow = jsonData[0];
      const codeColumnKey = firstRow ? Object.keys(firstRow).find(key => key.toLowerCase() === 'code') : null;
      if (!codeColumnKey) {
        const availableColumns = Object.keys(firstRow).join(', ');
        setError(`Excel file must have a "Code" column. Found columns: ${availableColumns || 'none'}`);
        return;
      }
      const validRows = jsonData
        .map((row) => {
          const codeKey = Object.keys(row).find(key => key.toLowerCase() === 'code');
          const codeValue = codeKey ? row[codeKey] : null;
          let codeStr = '';
          if (codeValue != null && codeValue !== undefined) {
            codeStr = String(codeValue).trim();
            if (codeStr.toLowerCase() === 'null' || codeStr.toLowerCase() === 'undefined') {
              codeStr = '';
            }
          }
          if (!codeStr) return null;
          const processedRow = { ...row };
          if (codeKey && codeKey !== 'code') {
            processedRow.code = codeValue;
          }
          return processedRow;
        })
        .filter(row => row !== null);
      if (validRows.length === 0) {
        setError(`No valid rows found. All ${jsonData.length} row(s) are missing a "Code" value.`);
        return;
      }
      setStrokeTypesLoading(true);
      const response = await fetch('/api/admin/ranking-stroke-types/bulk', {
        method: 'POST',
        headers: adminJsonHeaders(),
        body: JSON.stringify({ sport: currentSport, strokeTypes: validRows }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || `HTTP ${response.status}` };
        }
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message || `Successfully imported ${result.data.length} stroke types`);
        setStrokeTypesBulkUploadOpen(false);
        loadStrokeTypes();
      } else {
        setError(result.message || 'Failed to import stroke types');
      }
    } catch (err: any) {
      console.error('Excel upload error:', err);
      setError(err.message || 'Failed to process Excel file.');
    } finally {
      setStrokeTypesLoading(false);
      event.target.value = '';
    }
  };

  const handleExportStrokeTypes = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      strokeTypes.map((st) => ({
        Code: st.code,
        ...(isFootballSport
          ? {
              'Goal Target Zone': st.goalTargetZone || st.courtPosition || '',
              'Shooting Position': st.shootingPosition || st.halfSide || '',
              'Player Position': st.playerPosition || st.strokeType || '',
              'Distance (yards)': st.distanceYards || st.strokeAction || '',
              'Shooting Method': st.shootingMethod || st.playType || '',
              'Area of Improvement': st.areaOfImprovement || st.shotType || '',
              'Possible Pitfalls': st.possiblePitfalls || st.placement || '',
              'Shooting Rating': st.shootingRating || st.difficulty || '',
              'Controlled Pressure Level': st.controlledPressureLevel || '',
              Foot: st.foot || '',
              Roles: st.roles || '',
            }
          : {
              'Court Position': st.courtPosition || '',
              'Half Court': st.halfSide || '',
              'Stroke Type': st.strokeType || '',
              'Stroke Action': st.strokeAction || '',
              'Play Type': st.playType || '',
              'Shot Type': st.shotType || '',
              Placement: st.placement || '',
              Difficulty: st.difficulty || '',
              Singles: st.singles === true ? 'Yes' : 'No',
              Doubles: st.doubles === true ? 'Yes' : 'No',
            }),
        Deception: st.deception || '',
        'Return Response': st.returnResponse || '',
        'Area of Improvement Options': st.areaOfImprovementOptions || '',
        'Suggested Practices': st.suggestedPracticesOptions || '',
        'Reference Videos': st.referenceVideos || '',
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, isFootballSport ? 'Shooting Skills' : 'Ranking Stroke Types');
    XLSX.writeFile(workbook, isFootballSport ? `${toSportSlug(currentSport)}-shooting-skills.xlsx` : 'ranking-stroke-types.xlsx');
  };

  const renderStrokeMediaGroup = (title: string, items?: StrokeMediaItem[]) => {
    const activeItems = (items || [])
      .filter((item) => item?.url || item?.title || item?.name || item?.notes || item?.sponsorName || item?.sponsorLogoUrl)
      .map((item, index) => ({ ...item, displayOrder: item.displayOrder ?? item.sequence ?? index + 1 }))
      .sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
    if (activeItems.length === 0) return null;

    return (
      <Box sx={{ mt: 1, border: '1px solid #dbe8f6', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ px: 1.25, py: 0.75, background: 'linear-gradient(90deg, #e3f2fd 0%, #f1f8e9 100%)' }}>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#102a43' }}>
            {title} ({activeItems.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 72 }}>Seq</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 160 }}>Sponsor</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 80, textAlign: 'center' }}>Video</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeItems.map((item, index) => (
                <TableRow key={`${title}-${item.url}-${index}`} hover>
                  <TableCell>
                    <Chip size="small" label={item.displayOrder} sx={{ height: 22, minWidth: 28, fontWeight: 800 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {item.title || `Video ${index + 1}`}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {[item.name, item.notes].filter(Boolean).join(' | ') || '-'}
                  </TableCell>
                  <TableCell>
                    {(item.sponsorName || item.sponsorLogoUrl) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {item.sponsorLogoUrl ? (
                          <Box
                            component="img"
                            src={item.sponsorLogoUrl}
                            alt={item.sponsorName || 'Sponsor'}
                            sx={{ width: 28, height: 28, objectFit: 'contain', border: '1px solid #e0e7ef', borderRadius: 1, bgcolor: '#fff', flexShrink: 0 }}
                          />
                        ) : null}
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#5d4037' }}>
                          {item.sponsorName || 'Sponsor'}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Open video">
                      <IconButton size="small" color="primary" onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderStrokeLearningVideoGroup = (sections?: StrokeVideoSection[], legacyReferenceVideos?: string) => {
    const rows = normalizeStrokeVideoSections(sections, legacyReferenceVideos)
      .flatMap((section) => {
        const sectionSequence = Number(section.displayOrder || 0);
        return (section.videos || [])
          .filter((video) => video.videoUrl || video.title || video.text || (video.imageUrls || []).length > 0 || video.sponsorName || video.sponsorLogoUrl)
          .map((video, index) => {
            const videoSequence = Number(video.displayOrder ?? video.sequence ?? index + 1);
            return {
              key: `${section.key}-${videoSequence}-${video.videoUrl || video.title || index}`,
              sequenceLabel: `${sectionSequence}.${videoSequence}`,
              category: section.label,
              title: video.title || `Step ${index + 1}`,
              description: video.text || '-',
              videoUrl: video.videoUrl || '',
              imageCount: (video.imageUrls || []).length,
              sponsorName: video.sponsorName || '',
              sponsorLogoUrl: video.sponsorLogoUrl || '',
            };
          });
      });
    if (rows.length === 0) return null;

    return (
      <Box sx={{ mt: 1, border: '1px solid #dbe8f6', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ px: 1.25, py: 0.75, background: 'linear-gradient(90deg, #e3f2fd 0%, #e8f5e9 100%)' }}>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#102a43' }}>
            Structured Learning Videos ({rows.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 80 }}>Seq</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 180 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 150 }}>Sponsor</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 90, textAlign: 'center' }}>Video</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 90, textAlign: 'center' }}>Images</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Chip size="small" label={row.sequenceLabel} sx={{ height: 22, fontWeight: 800 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{row.category}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{row.description}</TableCell>
                  <TableCell>
                    {(row.sponsorName || row.sponsorLogoUrl) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {row.sponsorLogoUrl ? (
                          <Box component="img" src={row.sponsorLogoUrl} alt={row.sponsorName || 'Sponsor'} sx={{ width: 34, height: 26, objectFit: 'contain', border: '1px solid #e0e7ef', borderRadius: 1, bgcolor: '#fff' }} />
                        ) : null}
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#5d4037' }}>
                          {row.sponsorName || 'Sponsor'}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {row.videoUrl ? (
                      <Tooltip title="Open video">
                        <IconButton size="small" color="primary" onClick={() => window.open(row.videoUrl, '_blank', 'noopener,noreferrer')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">{row.imageCount || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderStrokeCoachFeedbackTable = (strokeType: RankingStrokeType) => (
    <Box sx={{ mt: 1, border: '1px solid #dbe8f6', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ px: 1.25, py: 0.75, background: 'linear-gradient(90deg, #fff8e1 0%, #fce4ec 100%)' }}>
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#5d4037' }}>
          Coach Feedback
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: 190 }}>Return Response</TableCell>
              <TableCell>{strokeType.returnResponse || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Area of Improvement</TableCell>
              <TableCell>{parseOptionList(strokeType.areaOfImprovementOptions).join(', ') || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Suggested Practices</TableCell>
              <TableCell>{parseOptionList(strokeType.suggestedPracticesOptions).join(', ') || '-'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderStrokeMediaEditor = (group: StrokeMediaGroupKey, title: string, helperText: string) => {
    const items = strokeMediaGroups[group] || [createEmptyStrokeMediaItem(1)];

    return (
      <Box sx={{ border: '1px solid #dbe8f6', borderRadius: 1.5, p: 1.25, bgcolor: '#fbfdff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f4c81' }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddStrokeMediaItem(group)} sx={{ textTransform: 'none' }}>
            Add
          </Button>
        </Box>
        <Stack spacing={1}>
          {items.map((item, index) => (
            <Box key={`${group}-${index}`} sx={{ border: '1px solid #e5edf7', borderRadius: 1, p: 1, bgcolor: '#fff' }}>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12} sm={2} md={1.2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Seq"
                    value={item.displayOrder ?? item.sequence ?? index + 1}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'displayOrder', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={2.8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={item.title || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'title', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={3.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Video/Image URL"
                    value={item.url || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'url', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Creator/Name"
                    value={item.name || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={2.2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={item.notes || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'notes', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2} md={0.6}>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveStrokeMediaItem(group, index)}
                        disabled={items.length === 1 && !item.url && !item.title}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sponsor Name"
                    value={item.sponsorName || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'sponsorName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sponsor Logo URL"
                    value={item.sponsorLogoUrl || ''}
                    onChange={(e) => handleStrokeMediaChange(group, index, 'sponsorLogoUrl', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const handleToggleSport = (sport: string) => {
    setCurrentSport(sport);
    setExpandedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleToggleSection = (sport: string, section: string) => {
    setExpandedSections(prev => {
      const sportSections = prev[sport] || [];
      return {
        ...prev,
        [sport]: sportSections.includes(section)
          ? sportSections.filter(s => s !== section)
          : [...sportSections, section]
      };
    });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const getSportIcon = (sport: string) => {
    switch (sport) {
      case 'Badminton':
        return <BadmintonIcon />;
      case 'Cricket':
        return <CricketIcon />;
      case 'Tennis':
        return <TennisIcon />;
      case 'Football':
        return <FootballIcon />;
      case 'Ice Hockey':
        return <IceHockeyIcon />;
      case 'Athletics':
        return <AthleticsIcon />;
      default:
        return <SportsIcon />;
    }
  };

  const availableSports = CORE_SPORTS;

  // Sidebar drawer content
  const drawer = (
    <Box sx={{ bgcolor: ADMIN_SIDEBAR_BG, height: '100%', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${ADMIN_SIDEBAR_BORDER}` }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '52px !important', borderBottom: `1px solid ${ADMIN_SIDEBAR_BORDER}` }}>
        <EncycdataLogo height={28} sx={{ ml: 0 }} />
        <IconButton
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{ color: brandColors.navy }}
          size="small"
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <AdminSidebarLegend collapsed={sidebarCollapsed} />
      <List sx={{ py: 0.5 }}>
        <AdminSidebarNavButton
          label="Dashboard"
          icon={<DashboardIcon />}
          selected={activeView === 'dashboard'}
          collapsed={sidebarCollapsed}
          tone={getAdminNavTone('dashboard')}
          onClick={() => handleViewChange('dashboard')}
        />
        <AdminSidebarNavButton
          label="Players"
          icon={<PeopleIcon />}
          selected={activeView === 'players'}
          collapsed={sidebarCollapsed}
          tone="v1"
          onClick={() => handleViewChange('players')}
        />
        <AdminSidebarNavButton
          label="Coaches"
          icon={<PersonIcon />}
          selected={activeView === 'coaches'}
          collapsed={sidebarCollapsed}
          tone="v1"
          onClick={() => handleViewChange('coaches')}
        />
        <AdminSidebarNavButton
          label="Academies"
          icon={<SchoolIcon />}
          selected={activeView === 'academies'}
          collapsed={sidebarCollapsed}
          tone="v1"
          onClick={() => handleViewChange('academies')}
        />
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <AdminSidebarNavButton
            label="Admin Users"
            icon={<PersonIcon />}
            selected={activeView === 'admin-user-management'}
            collapsed={sidebarCollapsed}
            tone={getAdminNavTone('admin-user-management')}
            onClick={() => handleViewChange('admin-user-management')}
          />
        )}

        <AdminSidebarSectionDivider label="EncycGames 1.0 — Contests" tone="v1" collapsed={sidebarCollapsed} />
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setContestNavExpanded(!contestNavExpanded)}
            sx={adminSectionHeaderSx('v1')}
          >
            <ListItemIcon sx={{ color: brandColors.orange, minWidth: sidebarCollapsed ? 0 : 44 }}>
              <TrophyIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Contests"
                primaryTypographyProps={{
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: brandColors.orange,
                }}
              />
            )}
            {!sidebarCollapsed && (contestNavExpanded ? (
              <ExpandLessIcon sx={{ color: brandColors.orange }} />
            ) : (
              <ExpandMoreIcon sx={{ color: brandColors.orange }} />
            ))}
          </ListItemButton>
        </ListItem>
        <Collapse in={contestNavExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {RELEASE_SPORTS.map((sport) => (
              <React.Fragment key={`contest-sport-${sport}`}>
                <AdminSidebarSportLabel sport={sport} collapsed={sidebarCollapsed} />
                <AdminSidebarChildButton
                  label="Developmental & Performance Contest"
                  collapsed={sidebarCollapsed}
                  tone="v1"
                  depth={2}
                  selected={activeView === 'contest-catalog' && currentSport === sport}
                  onClick={() => handleViewChange('contest-catalog', sport)}
                />
                <AdminSidebarChildButton
                  label="Submissions (legacy)"
                  collapsed={sidebarCollapsed}
                  tone="future"
                  depth={2}
                  selected={activeView === 'contest-tasks' && currentSport === sport}
                  onClick={() => handleViewChange('contest-tasks', sport)}
                  fontSize="0.82rem"
                />
              </React.Fragment>
            ))}
            <AdminSidebarChildButton
              label="All sports — tasks"
              collapsed={sidebarCollapsed}
              tone="future"
              depth={2}
              selected={activeView === 'contest-tasks' && currentSport === 'All Sports'}
              onClick={() => handleViewChange('contest-tasks', 'All Sports')}
              fontSize="0.82rem"
            />
          </List>
        </Collapse>

        <AdminSidebarSectionDivider label="EncycGames 1.0 — Ops" tone="v1" collapsed={sidebarCollapsed} />
        {isViewAccessible('sponsor-leads') && (
          <AdminSidebarNavButton
            label="Sponsor Management"
            icon={<HandshakeIcon />}
            selected={activeView === 'sponsor-leads'}
            collapsed={sidebarCollapsed}
            tone="v1"
            accentColor="#7c3aed"
            onClick={() => handleViewChange('sponsor-leads')}
          />
        )}
        {isViewAccessible('subscriptions') && (
          <AdminSidebarNavButton
            label="Subscriptions"
            icon={<CardMembershipIcon />}
            selected={activeView === 'subscriptions'}
            collapsed={sidebarCollapsed}
            tone="v1"
            accentColor={brandColors.orange}
            onClick={() => handleViewChange('subscriptions')}
          />
        )}
        {isViewAccessible('customer-ops') && (
          <AdminSidebarNavButton
            label="Customer Ops"
            icon={<GroupsIcon />}
            selected={activeView === 'customer-ops'}
            collapsed={sidebarCollapsed}
            tone="v1"
            accentColor={brandColors.blue}
            onClick={() => handleViewChange('customer-ops')}
          />
        )}
        {isViewAccessible('support-tickets') && (
          <AdminSidebarNavButton
            label="Support Tickets"
            icon={<SupportAgentIcon />}
            selected={activeView === 'support-tickets'}
            collapsed={sidebarCollapsed}
            tone="v1"
            accentColor={brandColors.green}
            onClick={() => handleViewChange('support-tickets')}
          />
        )}
        {isViewAccessible('careers-applications') && (
          <AdminSidebarNavButton
            label="Careers"
            icon={<WorkOutlineIcon />}
            selected={activeView === 'careers-applications'}
            collapsed={sidebarCollapsed}
            tone="v1"
            accentColor={brandColors.violet}
            onClick={() => handleViewChange('careers-applications')}
          />
        )}

        <AdminSidebarSectionDivider label="Future release — Sport modules" tone="future" collapsed={sidebarCollapsed} />
        {(user?.role === 'admin' || user?.role === 'super_admin') && isViewAccessible('sport-rollout') && (
          <AdminSidebarNavButton
            label="Sport Rollout"
            icon={<AppsIcon />}
            selected={activeView === 'sport-rollout'}
            collapsed={sidebarCollapsed}
            tone="future"
            secondary="Module matrix"
            onClick={() => handleViewChange('sport-rollout')}
          />
        )}

        {/* Sports Categories */}
        {availableSports.map((sport) => {
          const isExpanded = expandedSports.includes(sport);
          
          return (
            <React.Fragment key={sport}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleToggleSport(sport)}
                  sx={adminSectionHeaderSx('future')}
                >
                  <ListItemIcon sx={{ color: brandColors.slate, minWidth: sidebarCollapsed ? 0 : 44 }}>
                    {getSportIcon(sport)}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: brandColors.slate }}>
                            {sport}
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  {!sidebarCollapsed && (isExpanded ? (
                    <ExpandLessIcon sx={{ color: brandColors.slate }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: brandColors.slate }} />
                  ))}
                </ListItemButton>
              </ListItem>
              
              {isExpanded && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* Learning Section */}
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ ...adminSectionHeaderSx('future'), pl: 2.5 }}
                        onClick={() => handleToggleSection(sport, 'Learning')}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 28 }}>
                          {expandedSections[sport]?.includes('Learning') ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: brandColors.slate }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: brandColors.slate }} />
                          )}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                          <ListItemText 
                            primary="Learning"
                            primaryTypographyProps={{ 
                              fontSize: '0.84rem',
                              fontWeight: 600,
                              color: brandColors.slate
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={expandedSections[sport]?.includes('Learning') || false} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'category-management' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('category-management', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Skill Categories"
                              primaryTypographyProps={{ 
                                fontSize: '0.84rem',
                                fontWeight: activeView === 'category-management' && currentSport === sport ? 700 : 500,
                                color: activeView === 'category-management' && currentSport === sport ? brandColors.slate : '#64748b'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'skills-management' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('skills-management', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Skills Management"
                              primaryTypographyProps={{ 
                                fontSize: '0.84rem',
                                fontWeight: activeView === 'skills-management' && currentSport === sport ? 700 : 500,
                                color: activeView === 'skills-management' && currentSport === sport ? brandColors.slate : '#64748b'
                              }}
                            />
                          )}
                        </ListItemButton>
                      </List>
                    </Collapse>

                    {/* Training Section */}
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ ...adminSectionHeaderSx('future'), pl: 2.5 }}
                        onClick={() => handleToggleSection(sport, 'Training')}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 32 }}>
                          {expandedSections[sport]?.includes('Training') ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          )}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                          <ListItemText 
                            primary="Training"
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: 'brandColors.slate'
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={expandedSections[sport]?.includes('Training') || false} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          disabled
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Skill Assessment"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: 'brandColors.slate'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 4)}
                          selected={(activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('skill-assessment-master', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Skill Assessment Master"
                              primaryTypographyProps={{ 
                                fontSize: '0.83rem',
                                fontWeight: (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') && currentSport === sport ? 700 : 600,
                                color: (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        {[
                          { id: 'beginner', label: 'Beginner' },
                          { id: 'intermediate', label: 'Intermediate' },
                          { id: 'advanced', label: 'Advanced' },
                          { id: 'pro', label: 'Pro' },
                        ].map((levelItem) => (
                          <ListItemButton
                            key={`${sport}-${levelItem.id}`}
                            sx={adminChildNavButtonSx('future', 5)}
                            selected={
                              (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') &&
                              currentSport === sport &&
                              searchParams.get('level') === levelItem.id
                            }
                            onClick={() => {
                              setCurrentSport(sport);
                              handleViewChange('skill-assessment-master', sport, { level: levelItem.id });
                            }}
                          >
                          {!sidebarCollapsed && (
                              <ListItemText 
                                primary={levelItem.label}
                                primaryTypographyProps={{ 
                                  fontSize: '0.83rem',
                                  fontWeight:
                                    (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') &&
                                    currentSport === sport &&
                                    searchParams.get('level') === levelItem.id
                                      ? 700
                                      : 600,
                                  color:
                                    (activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') &&
                                    currentSport === sport &&
                                    searchParams.get('level') === levelItem.id
                                      ? 'brandColors.slate'
                                      : 'inherit'
                                }}
                              />
                            )}
                          </ListItemButton>
                        ))}
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 4)}
                          selected={activeView === 'self-practice-master' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('self-practice-master', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText
                              primary="Self-Practice Master"
                              primaryTypographyProps={{
                                fontSize: '0.83rem',
                                fontWeight: activeView === 'self-practice-master' && currentSport === sport ? 700 : 600,
                                color: activeView === 'self-practice-master' && currentSport === sport ? 'brandColors.slate' : 'inherit',
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 4)}
                          selected={activeView === 'joy-fun-master' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('joy-fun-master', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText
                              primary="Joy & Fun Master"
                              primaryTypographyProps={{
                                fontSize: '0.83rem',
                                fontWeight: activeView === 'joy-fun-master' && currentSport === sport ? 700 : 600,
                                color: activeView === 'joy-fun-master' && currentSport === sport ? 'brandColors.slate' : 'inherit',
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 4)}
                          selected={activeView === 'homework-master' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('homework-master', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText
                              primary="Homework Master"
                              primaryTypographyProps={{
                                fontSize: '0.83rem',
                                fontWeight: activeView === 'homework-master' && currentSport === sport ? 700 : 600,
                                color: activeView === 'homework-master' && currentSport === sport ? 'brandColors.slate' : 'inherit',
                              }}
                            />
                          )}
                        </ListItemButton>
                      </List>
                    </Collapse>

                    {/* Master Data Section */}
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ ...adminSectionHeaderSx('future'), pl: 2.5 }}
                        onClick={() => handleToggleSection(sport, 'Master Data')}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 32 }}>
                          {expandedSections[sport]?.includes('Master Data') ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          )}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                          <ListItemText 
                            primary="Master Data"
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: 'brandColors.slate'
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={expandedSections[sport]?.includes('Master Data') || false} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'ranking-stroke-types' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('ranking-stroke-types', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary={isShootingSport(sport) ? 'Shooting' : 'Strokes & Shots'}
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'ranking-stroke-types' && currentSport === sport ? 700 : 600,
                                color: activeView === 'ranking-stroke-types' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'master-drills' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('master-drills', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Drills"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'master-drills' && currentSport === sport ? 700 : 600,
                                color: activeView === 'master-drills' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'master-fitness' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('master-fitness', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Fitness"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'master-fitness' && currentSport === sport ? 700 : 600,
                                color: activeView === 'master-fitness' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'level-mappings' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('level-mappings', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Level Mappings"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'level-mappings' && currentSport === sport ? 700 : 600,
                                color: activeView === 'level-mappings' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'landing-elite-benchmarks' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('landing-elite-benchmarks', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Landing Benchmarks"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'landing-elite-benchmarks' && currentSport === sport ? 700 : 600,
                                color: activeView === 'landing-elite-benchmarks' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                      </List>
                    </Collapse>

                    {/* Ranking Section */}
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ ...adminSectionHeaderSx('future'), pl: 2.5 }}
                        onClick={() => handleToggleSection(sport, 'Ranking')}
                      >
                        <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 32 }}>
                          {expandedSections[sport]?.includes('Ranking') ? (
                            <ExpandLessIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16, color: 'brandColors.slate' }} />
                          )}
                        </ListItemIcon>
                        {!sidebarCollapsed && (
                          <ListItemText 
                            primary="Ranking"
                            primaryTypographyProps={{ 
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: 'brandColors.slate'
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={expandedSections[sport]?.includes('Ranking') || false} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'ranking-stroke-types' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('ranking-stroke-types', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary={isShootingSport(sport) ? 'Shooting' : 'Stroke Types'}
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'ranking-stroke-types' && currentSport === sport ? 700 : 600,
                                color: activeView === 'ranking-stroke-types' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'grade-configurations' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('grade-configurations', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Grade Configurations"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'grade-configurations' && currentSport === sport ? 700 : 600,
                                color: activeView === 'grade-configurations' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'video-limits' && currentSport === sport}
                          onClick={() => {
                            setCurrentSport(sport);
                            handleViewChange('video-limits', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Video Limits"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'video-limits' && currentSport === sport ? 700 : 600,
                                color: activeView === 'video-limits' && currentSport === sport ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'video-evaluation-queue'}
                          onClick={() => {
                            handleViewChange('video-evaluation-queue', sport);
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Video Evaluation Queue"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'video-evaluation-queue' ? 700 : 600,
                                color: activeView === 'video-evaluation-queue' ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                        <ListItemButton
                          sx={adminChildNavButtonSx('future', 3)}
                          selected={activeView === 'evaluation-admin-management'}
                          onClick={() => {
                            handleViewChange('evaluation-admin-management');
                          }}
                        >
                          {!sidebarCollapsed && (
                            <ListItemText 
                              primary="Evaluation Admin Management"
                              primaryTypographyProps={{ 
                                fontSize: '0.85rem',
                                fontWeight: activeView === 'evaluation-admin-management' ? 700 : 600,
                                color: activeView === 'evaluation-admin-management' ? 'brandColors.slate' : 'inherit'
                              }}
                            />
                          )}
                        </ListItemButton>
                      </List>
                    </Collapse>
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Logout */}
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              color: '#d32f2f',
              '&:hover': {
                bgcolor: '#ffebee',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#d32f2f', minWidth: sidebarCollapsed ? 0 : 56 }}>
              <LogoutIcon />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText 
                primary="Logout"
                primaryTypographyProps={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 600,
                  color: '#d32f2f'
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, flexShrink: { sm: 0 }, transition: 'width 0.3s' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: ADMIN_SIDEBAR_BG,
              transition: 'width 0.3s'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              bgcolor: ADMIN_SIDEBAR_BG,
              transition: 'width 0.3s',
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
          transition: 'width 0.3s, margin 0.3s'
        }}
      >
        <Container maxWidth="xl">

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Content based on activeView */}
      {activeView === 'dashboard' && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {stats?.users?.total || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Players
                  </Typography>
                  <Typography variant="h4">
                    {stats?.players?.total || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Coaches
                  </Typography>
                  <Typography variant="h4">
                    {stats?.coaches?.total || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {activeView === 'players' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Players Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreatePlayerOpen(true)}
            >
              Add Player
            </Button>
          </Box>

          <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Player ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Primary Sport</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Age Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players
                .filter((player) => player.user) // Filter out players without user data
                .map((player) => (
                <TableRow key={player.id}>
                  <TableCell>
                    <Chip label={player.playerIdCode || 'N/A'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{player.user?.firstName || ''} {player.user?.lastName || ''}</TableCell>
                  <TableCell>{player.user?.email || '-'}</TableCell>
                  <TableCell>{player.gender || '-'}</TableCell>
                  <TableCell>{player.country || '-'}</TableCell>
                  <TableCell>{player.primarySport || '-'}</TableCell>
                  <TableCell>
                    <Chip label={player.level} size="small" />
                  </TableCell>
                  <TableCell>{player.ageCategory}</TableCell>
                  <TableCell>
                    <Chip
                      label={player.user?.isActive ? 'Active' : 'Inactive'}
                      color={player.user?.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Player">
                      <IconButton size="small" onClick={() => handleEditPlayer(player)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Player">
                      <IconButton size="small" color="error" onClick={() => handleDeletePlayer(player.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      )}

      {activeView === 'coaches' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Coaches Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateCoachOpen(true)}
            >
              Add Coach
            </Button>
          </Box>

          <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Coach ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Primary Sport</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && coaches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : coaches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No coaches found. Click "Add Coach" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coaches.map((coach) => (
                <TableRow key={coach.id}>
                  <TableCell>
                    <Chip label={coach.coachIdCode || 'N/A'} size="small" variant="outlined" color="primary" />
                  </TableCell>
                  <TableCell>
                    {coach.user?.firstName ?? '—'} {coach.user?.lastName ?? ''}
                  </TableCell>
                  <TableCell>{coach.user?.email ?? '—'}</TableCell>
                  <TableCell>{coach.country || '-'}</TableCell>
                  <TableCell>{coach.primarySport || '-'}</TableCell>
                  <TableCell>{coach.specialization || 'N/A'}</TableCell>
                  <TableCell>{coach.experience ? `${coach.experience} years` : 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={coach.user?.isActive ? 'Active' : 'Inactive'}
                      color={coach.user?.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Manage Players">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          setSelectedCoachForLinking(coach);
                          setPlayerCoachLinkOpen(true);
                        }}
                      >
                        <PeopleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Coach">
                      <IconButton size="small" onClick={() => handleEditCoach(coach)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Coach">
                      <IconButton size="small" color="error" onClick={() => handleDeleteCoachClick(coach.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      )}

      {activeView === 'academies' && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Academies Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setAcademyBulkUploadOpen(true)}
              >
                Bulk Upload
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAcademyDialog()}
              >
                Add Academy
              </Button>
            </Box>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Academies List */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Academies ({academies.length})
              </Typography>
              
              {academiesLoading && academies.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Academy ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {academies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No academies found. Click "Add Academy" to create one.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        academies.map((academy) => (
                          <TableRow key={academy.id} hover>
                            <TableCell>
                              <Chip 
                                label={academy.academy_id_code || 'N/A'} 
                                color="primary" 
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                                {academy.name}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={academy.status === 'permanently_closed' ? 'Permanently Closed' : 'Active'} 
                                color={academy.status === 'permanently_closed' ? 'error' : 'success'} 
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{academy.description || 'N/A'}</TableCell>
                            <TableCell>{academy.country || 'N/A'}</TableCell>
                            <TableCell>{academy.city || 'N/A'}</TableCell>
                            <TableCell>{academy.address || 'N/A'}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{academy.contact_email || 'N/A'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {academy.contact_phone || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Generate Admin ID">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setGenerateAdminAcademy(academy);
                                    setGenerateAdminForm({
                                      firstName: '',
                                      lastName: '',
                                      contactEmail: academy.contact_email || '',
                                      contactPhone: academy.contact_phone || '',
                                    });
                                    setGeneratedAdminCredentials(null);
                                  }}
                                >
                                  <VpnKeyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Academy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenAcademyDialog(academy)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Academy">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAcademy(academy.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Academy Dialog */}
          <Dialog open={academyDialogOpen} onClose={handleCloseAcademyDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingAcademy ? 'Edit Academy' : 'Add New Academy'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Academy Name *"
                    value={academyFormData.name}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={academyFormData.description}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, description: e.target.value })}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={academyFormData.country}
                      onChange={(e) => setAcademyFormData({ ...academyFormData, country: e.target.value })}
                      label="Country"
                    >
                      <MenuItem value="">
                        <em>Select Country</em>
                      </MenuItem>
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={academyFormData.city}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={academyFormData.address}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, address: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    type="email"
                    value={academyFormData.contact_email}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, contact_email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    value={academyFormData.contact_phone}
                    onChange={(e) => setAcademyFormData({ ...academyFormData, contact_phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={academyFormData.status}
                      onChange={(e) => setAcademyFormData({ ...academyFormData, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="permanently_closed">Permanently Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={assessmentSkillFormData.showOnHomepage === true}
                        onChange={(e) =>
                          setAssessmentSkillFormData({ ...assessmentSkillFormData, showOnHomepage: e.target.checked })
                        }
                      />
                    }
                    label="Show on Home Page preview"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAcademyDialog}>Cancel</Button>
              <Button onClick={handleSaveAcademy} variant="contained" disabled={academiesLoading}>
                {academiesLoading ? <CircularProgress size={20} /> : editingAcademy ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={!!generateAdminAcademy}
            onClose={() => {
              setGenerateAdminAcademy(null);
              setGeneratedAdminCredentials(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Generate Admin ID — {generateAdminAcademy?.name}</DialogTitle>
            <DialogContent>
              {generatedAdminCredentials ? (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="success">
                    Share this Admin ID and password offline or by email. Contact details are not a login.
                  </Alert>
                  <TextField label="Admin ID" value={generatedAdminCredentials.adminIdCode} InputProps={{ readOnly: true }} />
                  {generatedAdminCredentials.temporaryPassword ? (
                    <TextField
                      label="Temporary password (shown once)"
                      value={generatedAdminCredentials.temporaryPassword}
                      InputProps={{ readOnly: true }}
                    />
                  ) : null}
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Creates another Admin ID for this academy. The person signs in with Admin ID, not the contact email.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First name *"
                      value={generateAdminForm.firstName}
                      onChange={(e) => setGenerateAdminForm({ ...generateAdminForm, firstName: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last name *"
                      value={generateAdminForm.lastName}
                      onChange={(e) => setGenerateAdminForm({ ...generateAdminForm, lastName: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact email (not login)"
                      type="email"
                      value={generateAdminForm.contactEmail}
                      onChange={(e) => setGenerateAdminForm({ ...generateAdminForm, contactEmail: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact mobile (not login)"
                      value={generateAdminForm.contactPhone}
                      onChange={(e) => setGenerateAdminForm({ ...generateAdminForm, contactPhone: e.target.value })}
                    />
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setGenerateAdminAcademy(null);
                  setGeneratedAdminCredentials(null);
                }}
              >
                {generatedAdminCredentials ? 'Done' : 'Cancel'}
              </Button>
              {!generatedAdminCredentials && (
                <Button variant="contained" onClick={handleGenerateAcademyAdmin} disabled={generateAdminLoading}>
                  {generateAdminLoading ? <CircularProgress size={20} /> : 'Generate Admin ID'}
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Bulk Upload Dialog */}
          <Dialog open={academyBulkUploadOpen} onClose={() => setAcademyBulkUploadOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Upload Academies</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload an Excel file (.xlsx) with the following columns:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                  <li>Name (required)</li>
                  <li>Description</li>
                  <li>Country</li>
                  <li>City</li>
                  <li>Address</li>
                  <li>Email / Contact Email</li>
                  <li>Phone / Contact Phone</li>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="academy-excel-upload-input"
                    type="file"
                    onChange={handleAcademyExcelUpload}
                  />
                  <label htmlFor="academy-excel-upload-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                      disabled={academiesLoading}
                    >
                      {academiesLoading ? 'Uploading...' : 'Choose Excel File'}
                    </Button>
                  </label>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAcademyBulkUploadOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {activeView === 'ranking-stroke-types' && (
        <Box>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              {isFootballSport ? 'Shooting Skills Assessment' : 'Ranking Stroke Types Management'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportStrokeTypes}
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setStrokeTypesBulkUploadOpen(true)}
              >
                Bulk Upload
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenStrokeTypeDialog()}
              >
                Add Stroke Type
              </Button>
            </Box>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Stroke Types List */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {isFootballSport ? `Master Shooting Skills (${strokeTypes.length})` : `Master Ranking Stroke Types (${strokeTypes.length})`}
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => loadStrokeTypes(currentSport)}
                  disabled={strokeTypesLoading}
                >
                  Refresh
                </Button>
              </Box>

              {strokeTypesLoading && strokeTypes.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'grid' },
                      gridTemplateColumns: 'minmax(90px, 0.8fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(110px, 0.8fr)',
                      gap: 1,
                      px: 2,
                      py: 1,
                      border: '1px solid #dbe8f6',
                      borderRadius: 1,
                      bgcolor: '#f7fbff',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Code</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{isFootballSport ? 'Goal Zone' : 'Court'}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{isFootballSport ? 'Shoot Pos' : 'Half'}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{isFootballSport ? 'Player Pos' : 'Stroke/Shot'}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{isFootballSport ? 'Distance' : 'Action'}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{isFootballSport ? 'Method' : 'Placement'}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
                  </Box>
                  {strokeTypes.map((strokeType) => {
                    const difficultyValue = normalizeDifficultyValue(strokeType.difficulty);
                    return (
                      <Accordion
                        key={strokeType.id || strokeType.code}
                        disableGutters
                        sx={{
                          boxShadow: 'none',
                          border: '1px solid #dbe8f6',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pr: 2.5 }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr auto',
                                md: 'minmax(90px, 0.8fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(110px, 0.8fr)',
                              },
                              gap: 1,
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{strokeType.code}</Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>{isFootballSport ? (strokeType.goalTargetZone || strokeType.courtPosition || '-') : (strokeType.courtPosition || '-')}</Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>{isFootballSport ? (strokeType.shootingPosition || strokeType.halfSide || '-') : (strokeType.halfSide || '-')}</Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 700 }}>
                              {isFootballSport ? (strokeType.playerPosition || strokeType.strokeType || '-') : (strokeType.strokeType || strokeType.shotType || '-')}
                            </Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>{isFootballSport ? (strokeType.distanceYards || strokeType.strokeAction || '-') : (strokeType.strokeAction || '-')}</Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>{isFootballSport ? (strokeType.shootingMethod || strokeType.playType || '-') : (strokeType.placement || '-')}</Typography>
                            <Chip
                              label={strokeType.isActive !== false ? 'Active' : 'Inactive'}
                              size="small"
                              color={strokeType.isActive !== false ? 'success' : 'default'}
                              sx={{ justifySelf: { xs: 'end', md: 'start' }, mr: { xs: 1, md: 0 } }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 0.5 }}>
                                <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => handleOpenStrokeTypeDialog(strokeType)} disabled={strokeTypesLoading} sx={{ textTransform: 'none' }}>
                                  Edit
                                </Button>
                                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => strokeType.id && handleDeleteStrokeType(strokeType.id)} disabled={strokeTypesLoading} sx={{ textTransform: 'none' }}>
                                  Delete
                                </Button>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Goal Target Zone' : 'Court Position'}:</b> {isFootballSport ? (strokeType.goalTargetZone || strokeType.courtPosition || '-') : (strokeType.courtPosition || '-')}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Shooting Position' : 'Half Court'}:</b> {isFootballSport ? (strokeType.shootingPosition || strokeType.halfSide || '-') : (strokeType.halfSide || '-')}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Player Position' : 'Stroke Action'}:</b> {isFootballSport ? (strokeType.playerPosition || strokeType.strokeType || '-') : (strokeType.strokeAction || '-')}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Distance (yards)' : 'Play Type'}:</b> {isFootballSport ? (strokeType.distanceYards || strokeType.strokeAction || '-') : (strokeType.playType || '-')}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Shooting Method' : 'Shot Type'}:</b> {isFootballSport ? (strokeType.shootingMethod || strokeType.playType || '-') : (strokeType.shotType || '-')}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography variant="body2"><b>{isFootballSport ? 'Possible Pitfalls' : 'Placement'}:</b> {isFootballSport ? (strokeType.possiblePitfalls || strokeType.placement || '-') : (strokeType.placement || '-')}</Typography></Grid>
                            {isFootballSport && (
                              <Grid item xs={12} md={6}><Typography variant="body2"><b>Controlled Pressure Level:</b> {strokeType.controlledPressureLevel || '-'}</Typography></Grid>
                            )}
                            {isFootballSport && (
                              <Grid item xs={12} md={6}><Typography variant="body2"><b>Foot:</b> {strokeType.foot || '-'}</Typography></Grid>
                            )}
                            {isFootballSport && (
                              <Grid item xs={12} md={6}><Typography variant="body2"><b>Roles:</b> {strokeType.roles || '-'}</Typography></Grid>
                            )}
                            {!isFootballSport && (
                              <Grid item xs={12} md={6}><Typography variant="body2"><b>Singles:</b> {strokeType.singles ? 'Yes' : 'No'}</Typography></Grid>
                            )}
                            {!isFootballSport && (
                              <Grid item xs={12} md={6}><Typography variant="body2"><b>Doubles:</b> {strokeType.doubles ? 'Yes' : 'No'}</Typography></Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2"><b>Difficulty:</b></Typography>
                                <Rating value={difficultyValue} max={5} readOnly size="small" />
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2">
                                <b>Home Page:</b> {strokeType.showOnHomepage ? 'Visible' : 'Hidden'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              {renderStrokeCoachFeedbackTable(strokeType)}
                            </Grid>
                            <Grid item xs={12}>
                              {renderStrokeLearningVideoGroup(strokeType.strokeVideoSections, strokeType.referenceVideos)}
                            </Grid>
                            <Grid item xs={12}>
                              {renderStrokeMediaGroup('Regular Training Videos', strokeType.regularTrainingVideos)}
                              {renderStrokeMediaGroup('Creator / Reference Videos', strokeType.creatorReferenceVideos)}
                              {renderStrokeMediaGroup('Amateur Videos', strokeType.amateurSampleVideos)}
                              {renderStrokeMediaGroup('Contest Videos', strokeType.contestVideos)}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Dialog */}
          <Dialog open={strokeTypesDialogOpen} onClose={handleCloseStrokeTypeDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              {editingStrokeType ? (isFootballSport ? 'Edit Shooting Skill' : 'Edit Stroke Type') : (isFootballSport ? 'Add New Shooting Skill' : 'Add New Stroke Type')}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code *"
                    value={strokeTypeFormData.code}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, code: e.target.value })}
                    required
                    helperText="Unique identifier (editable)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Goal Target Zone' : 'Court Position'}
                    value={strokeTypeFormData.courtPosition || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, courtPosition: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Shooting Position' : 'Half Court'}
                    value={strokeTypeFormData.halfSide || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, halfSide: e.target.value })}
                    helperText={isFootballSport ? "Example: Right Foot / Left Foot / Header" : "Enter 'Right Side' or 'Left Side'"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Player Position' : 'Stroke Type'}
                    value={strokeTypeFormData.strokeType || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, strokeType: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Distance (yards)' : 'Stroke Action'}
                    value={strokeTypeFormData.strokeAction || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, strokeAction: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Shooting Method' : 'Play Type'}
                    value={strokeTypeFormData.playType || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, playType: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Area of Improvement' : 'Shot Type'}
                    value={strokeTypeFormData.shotType || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, shotType: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={isFootballSport ? 'Possible Pitfalls' : 'Placement'}
                    value={strokeTypeFormData.placement || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, placement: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      {isFootballSport ? 'Shooting Rating' : 'Difficulty'}
                    </Typography>
                    <Rating
                      value={typeof strokeTypeFormData.difficulty === 'number' ? strokeTypeFormData.difficulty : normalizeDifficultyValue(strokeTypeFormData.difficulty)}
                      onChange={(_, newValue) => {
                        setStrokeTypeFormData({ ...strokeTypeFormData, difficulty: newValue || 0 });
                      }}
                      max={5}
                      size="large"
                    />
                    {Number(strokeTypeFormData.difficulty || 0) > 0 && (
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({Number(strokeTypeFormData.difficulty || 0)}/5)
                      </Typography>
                    )}
                    {isFootballSport && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          1. Hit target zones 0-10%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          2. Hit target zones 11-25%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          3. Hit target zones 30-50%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          4. Hit target zones 51-70%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          5. Hit target zones 70+%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Deception</InputLabel>
                    <Select
                      value={strokeTypeFormData.deception || ''}
                      label="Deception"
                      onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, deception: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {isFootballSport && (
                    <TextField
                      fullWidth
                      label="Controlled Pressure Level"
                      value={strokeTypeFormData.controlledPressureLevel || ''}
                      onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, controlledPressureLevel: e.target.value })}
                      helperText="Example: Under pressure, one-touch only, weak-foot only, limited time"
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isFootballSport && (
                    <TextField
                      fullWidth
                      label="Foot"
                      value={strokeTypeFormData.foot || ''}
                      onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, foot: e.target.value })}
                      helperText="Example: Right / Left / Both"
                    />
                  )}
                </Grid>
                {isFootballSport && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Roles"
                      value={strokeTypeFormData.roles || ''}
                      onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, roles: e.target.value })}
                      helperText="Example: Winger, Midfielder, Defender, Striker"
                    />
                  </Grid>
                )}
                {!isFootballSport && (
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={strokeTypeFormData.singles === true}
                          onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, singles: e.target.checked })}
                        />
                      }
                      label="Singles"
                    />
                  </Grid>
                )}
                {!isFootballSport && (
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={strokeTypeFormData.doubles === true}
                          onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, doubles: e.target.checked })}
                        />
                      }
                      label="Doubles"
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Return Response"
                    value={strokeTypeFormData.returnResponse || ''}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, returnResponse: e.target.value })}
                    multiline
                    rows={2}
                    helperText="Expected return response for this stroke type"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Area of Improvement
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add improvement item"
                      value={strokeAreaOptionInput}
                      onChange={(e) => setStrokeAreaOptionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddStrokeOption('areaOfImprovementOptions', strokeAreaOptionInput);
                          setStrokeAreaOptionInput('');
                        }
                      }}
                      placeholder="Default: Consult the coaches"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleAddStrokeOption('areaOfImprovementOptions', strokeAreaOptionInput);
                        setStrokeAreaOptionInput('');
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {parseOptionList(strokeTypeFormData.areaOfImprovementOptions).map((option) => (
                      <Chip
                        key={`stroke-area-${option}`}
                        label={option}
                        onDelete={() => handleRemoveStrokeOption('areaOfImprovementOptions', option)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Suggested Practices
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add practice item"
                      value={strokeSuggestedOptionInput}
                      onChange={(e) => setStrokeSuggestedOptionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddStrokeOption('suggestedPracticesOptions', strokeSuggestedOptionInput);
                          setStrokeSuggestedOptionInput('');
                        }
                      }}
                      placeholder="Default: Consult the coaches"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleAddStrokeOption('suggestedPracticesOptions', strokeSuggestedOptionInput);
                        setStrokeSuggestedOptionInput('');
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {parseOptionList(strokeTypeFormData.suggestedPracticesOptions).map((option) => (
                      <Chip
                        key={`stroke-practice-${option}`}
                        label={option}
                        onDelete={() => handleRemoveStrokeOption('suggestedPracticesOptions', option)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Structured Learning Videos
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Define the ordered video cards shown to players and coaches. Each section can include multiple ordered videos with title, URL, description, image URLs, and sponsor details.
                  </Typography>
                  {strokeVideoSections.map((section) => {
                    const sectionVideos = section.videos && section.videos.length > 0
                      ? section.videos
                      : [{ displayOrder: 1, sequence: 1, title: section.label, videoUrl: section.videoUrl || '', text: section.text || '', imageUrls: section.imageUrls || [] }];
                    return (
                    <Box key={section.key} sx={{ border: '1px solid #dbe8f6', borderRadius: 1, p: 1.25, mb: 1.25, bgcolor: '#fbfdff' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {section.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Add one or more ordered videos for this category.
                          </Typography>
                        </Box>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddStrokeSectionVideo(section.key)} sx={{ textTransform: 'none' }}>
                          Add Video
                        </Button>
                      </Box>
                      <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            type="number"
                            label={`${section.label} Sequence`}
                            value={section.displayOrder ?? 0}
                            onChange={(e) => handleStrokeVideoSectionChange(section.key, 'displayOrder', e.target.value)}
                            inputProps={{ min: 0 }}
                            helperText="Lower number displays first"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            {sectionVideos.map((video, videoIndex) => (
                              <Box key={`${section.key}-video-${videoIndex}`} sx={{ border: '1px solid #e5edf7', borderRadius: 1, p: 1, bgcolor: '#fff' }}>
                                <Grid container spacing={1} alignItems="flex-start">
                                  <Grid item xs={12} sm={2} md={1.2}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="number"
                                      label="Seq"
                                      value={video.displayOrder ?? video.sequence ?? videoIndex + 1}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'displayOrder', e.target.value)}
                                      inputProps={{ min: 0 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={2.8}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Title"
                                      value={video.title || ''}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'title', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={3.2}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Video URL"
                                      value={video.videoUrl || ''}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'videoUrl', e.target.value)}
                                      placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={2.4}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Description"
                                      value={video.text || ''}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'text', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5} md={1.8}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Image URLs"
                                      value={(video.imageUrls || []).join(', ')}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'imageUrlsText', e.target.value)}
                                      placeholder="Comma separated"
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2} md={0.6}>
                                    <Tooltip title="Remove video">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleRemoveStrokeSectionVideo(section.key, videoIndex)}
                                          disabled={sectionVideos.length === 1 && !video.videoUrl && !video.title}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Sponsor Name"
                                      value={video.sponsorName || ''}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'sponsorName', e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Sponsor Logo URL"
                                      value={video.sponsorLogoUrl || ''}
                                      onChange={(e) => handleStrokeSectionVideoChange(section.key, videoIndex, 'sponsorLogoUrl', e.target.value)}
                                      placeholder="https://example.com/logo.png"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                  })}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Skill Video Libraries
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Add regular training, creator/reference, amateur, and contest videos as separate editable rows. Lower sequence numbers display first.
                  </Typography>
                  <Stack spacing={1.25}>
                    {renderStrokeMediaEditor('regularTrainingVideos', 'Regular Training Videos', 'Normal coaching or training videos for this skill.')}
                    {renderStrokeMediaEditor('creatorReferenceVideos', 'Creator / Reference Videos', 'Creator, coach, athlete, or professional reference videos.')}
                    {renderStrokeMediaEditor('amateurSampleVideos', 'Amateur Videos', 'Doable sample videos from players or amateurs.')}
                    {renderStrokeMediaEditor('contestVideos', 'Contest Videos', 'Videos linked from contest tasks that are relevant to this skill.')}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={strokeTypeFormData.isActive !== false ? 'active' : 'inactive'}
                      label="Status"
                      onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, isActive: e.target.value === 'active' })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={strokeTypeFormData.showOnHomepage === true}
                        onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, showOnHomepage: e.target.checked })}
                      />
                    }
                    label="Show on Home Page preview"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={strokeTypeFormData.displayOrder || 0}
                    onChange={(e) => setStrokeTypeFormData({ ...strokeTypeFormData, displayOrder: parseInt(e.target.value) || 0 })}
                    helperText="Lower numbers appear first"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseStrokeTypeDialog}>Cancel</Button>
              <Button onClick={handleSaveStrokeType} variant="contained" disabled={strokeTypesLoading}>
                {strokeTypesLoading ? <CircularProgress size={20} /> : editingStrokeType ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Upload Dialog */}
          <Dialog open={strokeTypesBulkUploadOpen} onClose={() => setStrokeTypesBulkUploadOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{isFootballSport ? 'Bulk Upload Shooting Skills' : 'Bulk Upload Ranking Stroke Types'}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload an Excel file (.xlsx) with the following columns:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                  <li>Code (required) - Unique identifier</li>
                  <li>{isFootballSport ? 'Goal Target Zone' : 'Court Position'}</li>
                  <li>{isFootballSport ? 'Shooting Position' : 'Half Court'}</li>
                  <li>{isFootballSport ? 'Player Position' : 'Stroke Type'}</li>
                  <li>{isFootballSport ? 'Distance (yards)' : 'Stroke Action'}</li>
                  <li>{isFootballSport ? 'Shooting Method' : 'Play Type'}</li>
                  <li>{isFootballSport ? 'Area of Improvement' : 'Shot Type'}</li>
                  <li>{isFootballSport ? 'Possible Pitfalls' : 'Placement'}</li>
                  <li>{isFootballSport ? 'Shooting Rating (1-5 star rating: 1=0-10%, 2=11-25%, 3=30-50%, 4=51-70%, 5=70+%)' : 'Difficulty (1-5 star rating)'}</li>
                  {isFootballSport && <li>Controlled Pressure Level</li>}
                  {isFootballSport && <li>Foot</li>}
                  {isFootballSport && <li>Roles</li>}
                  {!isFootballSport && <li>Singles (Yes/No)</li>}
                  {!isFootballSport && <li>Doubles (Yes/No)</li>}
                  <li>Deception (Yes/No)</li>
                  <li>Return Response</li>
                  <li>Reference Videos (comma-separated URLs)</li>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  If a stroke type with the same code already exists, it will be updated. Otherwise, a new one will be created.
                </Alert>
                <Box sx={{ mt: 3 }}>
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="excel-upload-input"
                    type="file"
                    onChange={handleExcelUpload}
                  />
                  <label htmlFor="excel-upload-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                      disabled={strokeTypesLoading}
                    >
                      {strokeTypesLoading ? 'Uploading...' : 'Choose Excel File'}
                    </Button>
                  </label>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setStrokeTypesBulkUploadOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {activeView === 'assessment-skills' && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Skill Assessment - {selectedSportForAssessment}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Assessment Type</InputLabel>
                <Select
                  value={selectedAssessmentType}
                  label="Assessment Type"
                  onChange={(e) => {
                    const type = e.target.value as 'high-level' | 'master-training';
                    setSelectedAssessmentType(type);
                    setSelectedCategoryFilter('all');
                    loadAssessmentSkills(selectedSportForAssessment, type);
                  }}
                >
                  <MenuItem value="high-level">High-level</MenuItem>
                  <MenuItem value="master-training">Master Data Training</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={selectedCategoryFilter}
                  label="Filter by Category"
                  onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {assessmentSkillCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={async () => {
                  // Load all categories from both high-level and low-level when opening dialog
                  // Merge with existing to preserve manually added categories
                  const categoriesFromSkills = await loadAllCategoriesForSport(selectedSportForAssessment);
                  const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
                  setAssessmentSkillCategories(mergedCategories.sort());
                  setCategoryManagementOpen(true);
                }}
                sx={{ mr: 1 }}
              >
                Add/View Category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAssessmentSkillDialog()}
              >
                Add Skill
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {selectedAssessmentType === 'high-level' ? 'High-level' : 'Master Data Training'} Assessment Skills (
                  {getFilteredAssessmentSkills().length} / {assessmentSkills.length})
              </Typography>
              
              {assessmentSkillsLoading && assessmentSkills.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : assessmentSkills.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No assessment skills found for {selectedSportForAssessment} - {selectedAssessmentType}.
                    Click "Add Skill" to create one.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    ref={assessmentSkillsContainerRef}
                    sx={{
                      maxHeight: 600,
                      overflow: 'auto',
                      pr: 6,
                    }}
                  >
                    {getFilteredAssessmentSkills().map((skill) => (
                      <Accordion key={skill.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                            <Chip label={skill.category} size="small" color="primary" variant="outlined" />
                            <Typography variant="subtitle2">{skill.itemName}</Typography>
                            <Chip
                              label={skill.isActive !== false ? 'Active' : 'Inactive'}
                              color={skill.isActive !== false ? 'success' : 'default'}
                              size="small"
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">Display Order: {skill.displayOrder ?? '-'}</Typography>
                            <Chip
                              size="small"
                              color={skill.showOnHomepage ? 'info' : 'default'}
                              label={skill.showOnHomepage ? 'Home Page: Visible' : 'Home Page: Hidden'}
                            />
                            <Tooltip title="Edit Skill">
                              <IconButton size="small" onClick={() => handleOpenAssessmentSkillDialog(skill)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Skill">
                              <IconButton size="small" color="error" onClick={() => handleDeleteAssessmentSkill(skill.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Area of Improvement
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {parseOptionList(skill.areaOfImprovementOptions ?? skill.area_of_improvement_options).map((option) => (
                              <Chip key={`${skill.id}-aoi-${option}`} label={option} size="small" />
                            ))}
                          </Box>

                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Suggested Practices
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: selectedAssessmentType === 'master-training' ? 1 : 0 }}>
                            {parseOptionList(skill.suggestedPracticesOptions ?? skill.suggested_practices_options).map((option) => (
                              <Chip key={`${skill.id}-sp-${option}`} label={option} size="small" />
                            ))}
                          </Box>

                          {selectedAssessmentType === 'master-training' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={skill.showInBeginner !== false}
                                    onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInBeginner', e.target.checked)}
                                  />
                                }
                                label="Beginner"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={skill.showInIntermediate !== false}
                                    onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInIntermediate', e.target.checked)}
                                  />
                                }
                                label="Intermediate"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={skill.showInAdvanced !== false}
                                    onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInAdvanced', e.target.checked)}
                                  />
                                }
                                label="Advanced"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={skill.showInPro !== false}
                                    onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInPro', e.target.checked)}
                                  />
                                }
                                label="Pro"
                              />
                              <FormControlLabel
                                control={<Checkbox size="small" checked={skill.showOnHomepage === true} disabled />}
                                label="Home Page"
                              />
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      zIndex: 1000,
                    }}
                  >
                    <IconButton
                      onClick={() => assessmentSkillsContainerRef.current?.scrollBy({ top: -140, behavior: 'smooth' })}
                      sx={{ bgcolor: '#1976d2', color: 'white', '&:hover': { bgcolor: '#1565c0' } }}
                      size="small"
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => assessmentSkillsContainerRef.current?.scrollBy({ top: 140, behavior: 'smooth' })}
                      sx={{ bgcolor: '#1976d2', color: 'white', '&:hover': { bgcolor: '#1565c0' } }}
                      size="small"
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Dialog */}
          <Dialog open={assessmentSkillsDialogOpen} onClose={handleCloseAssessmentSkillDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingAssessmentSkill ? 'Edit Assessment Skill' : 'Add New Assessment Skill'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Assessment Type *</InputLabel>
                    <Select
                      value={assessmentSkillFormData.assessmentType}
                      label="Assessment Type *"
                      onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, assessmentType: e.target.value as 'high-level' | 'master-training' })}
                      required
                    >
                      <MenuItem value="high-level">High-level</MenuItem>
                      <MenuItem value="master-training">Master Data Training</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Name *"
                    value={assessmentSkillFormData.itemName}
                    onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, itemName: e.target.value })}
                    required
                    helperText="Name of the skill/item to be assessed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      value={assessmentSkillFormData.category}
                      label="Category *"
                      onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, category: e.target.value })}
                      required
                    >
                      {assessmentSkillCategories.length === 0 ? (
                        <MenuItem disabled value="">
                          No categories available. Add categories first.
                        </MenuItem>
                      ) : (
                        assessmentSkillCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      {assessmentSkillCategories.length === 0 
                        ? 'Click "Add/View Category" to create categories first'
                        : 'Select a category from the list'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={assessmentSkillFormData.displayOrder ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAssessmentSkillFormData({ 
                        ...assessmentSkillFormData, 
                        displayOrder: value === '' ? undefined : (parseInt(value) || undefined)
                      });
                    }}
                    helperText="Lower numbers appear first (optional)"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={assessmentSkillFormData.isActive !== false ? 'active' : 'inactive'}
                      label="Status"
                      onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, isActive: e.target.value === 'active' })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAssessmentSkillDialog}>Cancel</Button>
              <Button onClick={handleSaveAssessmentSkill} variant="contained" disabled={assessmentSkillsLoading}>
                {assessmentSkillsLoading ? <CircularProgress size={20} /> : editingAssessmentSkill ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Category Management Dialog */}
          <Dialog open={categoryManagementOpen} onClose={handleCloseCategoryManagement} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Categories - {selectedSportForAssessment}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Existing Categories ({assessmentSkillCategories.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      // Reload all categories from both high-level and low-level skills
                      // BUT merge with existing categories to preserve manually added ones
                      const categoriesFromSkills = await loadAllCategoriesForSport(selectedSportForAssessment);
                      // Merge: combine categories from skills with existing categories (preserve manually added)
                      const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
                      setAssessmentSkillCategories(mergedCategories.sort());
                      setSuccess(`Categories reloaded. Found ${categoriesFromSkills.length} from skills, ${mergedCategories.length} total (including manually added)`);
                    }}
                  >
                    Reload from Skills
                  </Button>
                </Box>
                
                {assessmentSkillCategories.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No categories found. Click "Reload from Skills" to load categories from existing skills, or add a new one below.
                  </Alert>
                ) : (
                  <List sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {assessmentSkillCategories.map((category, index) => (
                      <ListItem
                        key={category}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleDeleteAssessmentCategory(category)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={`${index + 1}. ${category}`} />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Add New Category
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Footwork, Forehand Strokes"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newCategoryName.trim()) {
                        handleAddCategory();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddCategory}
                    disabled={!newCategoryName.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCategoryManagement}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {activeView === 'homework-master' && (
        <AdminTrainingHomework sport={currentSport} />
      )}
      {activeView === 'self-practice-master' && (
        <AdminTrainingContentMaster sport={currentSport} contentType="self-practice" />
      )}
      {activeView === 'joy-fun-master' && (
        <AdminTrainingContentMaster sport={currentSport} contentType="joy-fun-learning" />
      )}

      {(activeView === 'skill-assessment-high-level' || activeView === 'skill-assessment-master' || activeView === 'skill-assessment-low-level') && (() => {
        const isHighLevel = activeView === 'skill-assessment-high-level';
        const fixedAssessmentType = isHighLevel ? 'high-level' : 'master-training';
        const title = isHighLevel ? 'Skill Assessment High Level' : 'Skill Assessment Master';
        const isLevelSpecificAssessmentView = !isHighLevel && Boolean(searchParams.get('level'));
        const selectedAssessmentLevel =
          (searchParams.get('level') as 'beginner' | 'intermediate' | 'advanced' | 'pro' | null) || 'beginner';
        
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {title} - {selectedSportForAssessment}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={selectedCategoryFilter}
                    label="Filter by Category"
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {assessmentSkillCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={async () => {
                    // Load all categories from both high-level and low-level when opening dialog
                    // Merge with existing to preserve manually added categories
                    const categoriesFromSkills = await loadAllCategoriesForSport(selectedSportForAssessment);
                    const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
                    setAssessmentSkillCategories(mergedCategories.sort());
                    setCategoryManagementOpen(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  Add/View Category
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAssessmentSkillDialog()}
                >
                  Add Skill
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isHighLevel ? 'High-level' : 'Master Data Training'} Assessment Skills (
                    {getFilteredAssessmentSkills().length} / {assessmentSkills.length})
                </Typography>
                
                {assessmentSkillsLoading && assessmentSkills.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : assessmentSkills.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No assessment skills found for {selectedSportForAssessment} - {fixedAssessmentType}.
                      Click "Add Skill" to create one.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      ref={fixedAssessmentSkillsContainerRef}
                      sx={{
                        maxHeight: 600,
                        overflow: 'auto',
                        pr: 6,
                      }}
                    >
                      {getFilteredAssessmentSkills().map((skill) => (
                        <Accordion key={skill.id} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                              <Chip label={skill.category} size="small" color="primary" variant="outlined" />
                              <Typography variant="subtitle2">{skill.itemName}</Typography>
                              <Chip
                                label={
                                  isLevelSpecificAssessmentView
                                    ? getAssessmentLevelStatus(skill, selectedAssessmentLevel).label
                                    : skill.isActive !== false
                                      ? 'Active'
                                      : 'Inactive'
                                }
                                color={
                                  isLevelSpecificAssessmentView
                                    ? getAssessmentLevelStatus(skill, selectedAssessmentLevel).color
                                    : skill.isActive !== false
                                      ? 'success'
                                      : 'default'
                                }
                                size="small"
                                sx={{ ml: 'auto' }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Display Order: {skill.displayOrder ?? '-'}</Typography>
                              <Chip
                                size="small"
                                color={skill.showOnHomepage ? 'info' : 'default'}
                                label={skill.showOnHomepage ? 'Home Page: Visible' : 'Home Page: Hidden'}
                              />
                            {!isLevelSpecificAssessmentView && (
                              <>
                                <Tooltip title="Edit Skill">
                                  <IconButton size="small" onClick={() => handleOpenAssessmentSkillDialog(skill)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Skill">
                                  <IconButton size="small" color="error" onClick={() => handleDeleteAssessmentSkill(skill.id)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Area of Improvement
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              {parseOptionList(skill.areaOfImprovementOptions ?? skill.area_of_improvement_options).map((option) => (
                                <Chip key={`${skill.id}-aoi-fixed-${option}`} label={option} size="small" />
                              ))}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Suggested Practices
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: isHighLevel ? 0 : 1 }}>
                              {parseOptionList(skill.suggestedPracticesOptions ?? skill.suggested_practices_options).map((option) => (
                                <Chip key={`${skill.id}-sp-fixed-${option}`} label={option} size="small" />
                              ))}
                            </Box>

                            {!isHighLevel && (
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                <FormControlLabel
                                  disabled={isLevelSpecificAssessmentView}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={skill.showInBeginner !== false}
                                      disabled={isLevelSpecificAssessmentView}
                                      onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInBeginner', e.target.checked)}
                                    />
                                  }
                                  label="Beginner"
                                />
                                <FormControlLabel
                                  disabled={isLevelSpecificAssessmentView}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={skill.showInIntermediate !== false}
                                      disabled={isLevelSpecificAssessmentView}
                                      onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInIntermediate', e.target.checked)}
                                    />
                                  }
                                  label="Intermediate"
                                />
                                <FormControlLabel
                                  disabled={isLevelSpecificAssessmentView}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={skill.showInAdvanced !== false}
                                      disabled={isLevelSpecificAssessmentView}
                                      onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInAdvanced', e.target.checked)}
                                    />
                                  }
                                  label="Advanced"
                                />
                                <FormControlLabel
                                  disabled={isLevelSpecificAssessmentView}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={skill.showInPro !== false}
                                      disabled={isLevelSpecificAssessmentView}
                                      onChange={(e) => handleToggleAssessmentSkillLevel(skill, 'showInPro', e.target.checked)}
                                    />
                                  }
                                  label="Pro"
                                />
                                <FormControlLabel
                                  control={<Checkbox size="small" checked={skill.showOnHomepage === true} disabled />}
                                  label="Home Page"
                                />
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        zIndex: 1000,
                      }}
                    >
                      <IconButton
                        onClick={() => fixedAssessmentSkillsContainerRef.current?.scrollBy({ top: -140, behavior: 'smooth' })}
                        sx={{ bgcolor: '#1976d2', color: 'white', '&:hover': { bgcolor: '#1565c0' } }}
                        size="small"
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => fixedAssessmentSkillsContainerRef.current?.scrollBy({ top: 140, behavior: 'smooth' })}
                        sx={{ bgcolor: '#1976d2', color: 'white', '&:hover': { bgcolor: '#1565c0' } }}
                        size="small"
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={assessmentSkillsDialogOpen} onClose={handleCloseAssessmentSkillDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                {editingAssessmentSkill ? 'Edit Assessment Skill' : 'Add New Assessment Skill'}
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Assessment Type *</InputLabel>
                      <Select
                        value={fixedAssessmentType}
                        label="Assessment Type *"
                        disabled
                        required
                      >
                        <MenuItem value={fixedAssessmentType}>{isHighLevel ? 'High-level' : 'Master Data Training'}</MenuItem>
                      </Select>
                      <FormHelperText>Assessment type is fixed for this page</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Item Name *"
                      value={assessmentSkillFormData.itemName}
                      onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, itemName: e.target.value })}
                      required
                      helperText="Name of the skill/item to be assessed"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Category *</InputLabel>
                      <Select
                        value={assessmentSkillFormData.category}
                        label="Category *"
                        onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, category: e.target.value })}
                        required
                      >
                        {assessmentSkillCategories.length === 0 ? (
                          <MenuItem disabled value="">
                            No categories available. Add categories first.
                          </MenuItem>
                        ) : (
                          assessmentSkillCategories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        {assessmentSkillCategories.length === 0 
                          ? 'Click "Add/View Category" to create categories first'
                          : 'Select a category from the list'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Display Order"
                      type="number"
                      value={assessmentSkillFormData.displayOrder ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAssessmentSkillFormData({ 
                          ...assessmentSkillFormData, 
                          displayOrder: value === '' ? undefined : (parseInt(value) || undefined)
                        });
                      }}
                      helperText="Lower numbers appear first (optional)"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={assessmentSkillFormData.isActive !== false ? 'active' : 'inactive'}
                        label="Status"
                        onChange={(e) => setAssessmentSkillFormData({ ...assessmentSkillFormData, isActive: e.target.value === 'active' })}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={assessmentSkillFormData.showOnHomepage === true}
                          onChange={(e) =>
                            setAssessmentSkillFormData({ ...assessmentSkillFormData, showOnHomepage: e.target.checked })
                          }
                        />
                      }
                      label="Show on Home Page preview"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Area of Improvement Options</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add option and press Enter"
                        value={areaOptionInput}
                        onChange={(e) => setAreaOptionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddAssessmentOption('areaOfImprovementOptions', areaOptionInput);
                            setAreaOptionInput('');
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleAddAssessmentOption('areaOfImprovementOptions', areaOptionInput);
                          setAreaOptionInput('');
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(assessmentSkillFormData.areaOfImprovementOptions || ['Consult the coaches']).map((option) => (
                        <Chip
                          key={`area-opt-${option}`}
                          label={option}
                          onDelete={() => handleRemoveAssessmentOption('areaOfImprovementOptions', option)}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Suggested Practices Options</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add option and press Enter"
                        value={suggestedOptionInput}
                        onChange={(e) => setSuggestedOptionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddAssessmentOption('suggestedPracticesOptions', suggestedOptionInput);
                            setSuggestedOptionInput('');
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleAddAssessmentOption('suggestedPracticesOptions', suggestedOptionInput);
                          setSuggestedOptionInput('');
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(assessmentSkillFormData.suggestedPracticesOptions || ['Consult the coaches']).map((option) => (
                        <Chip
                          key={`suggest-opt-${option}`}
                          label={option}
                          onDelete={() => handleRemoveAssessmentOption('suggestedPracticesOptions', option)}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseAssessmentSkillDialog}>Cancel</Button>
                <Button onClick={handleSaveAssessmentSkill} variant="contained" disabled={assessmentSkillsLoading}>
                  {assessmentSkillsLoading ? <CircularProgress size={20} /> : editingAssessmentSkill ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Category Management Dialog */}
            <Dialog open={categoryManagementOpen} onClose={handleCloseCategoryManagement} maxWidth="sm" fullWidth>
              <DialogTitle>Manage Categories - {selectedSportForAssessment}</DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">
                      Existing Categories ({assessmentSkillCategories.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        // Reload all categories from both high-level and low-level skills
                        // BUT merge with existing categories to preserve manually added ones
                        const categoriesFromSkills = await loadAllCategoriesForSport(selectedSportForAssessment);
                        // Merge: combine categories from skills with existing categories (preserve manually added)
                        const mergedCategories = [...new Set([...categoriesFromSkills, ...assessmentSkillCategories])];
                        setAssessmentSkillCategories(mergedCategories.sort());
                        setSuccess(`Categories reloaded. Found ${categoriesFromSkills.length} from skills, ${mergedCategories.length} total (including manually added)`);
                      }}
                    >
                      Reload from Skills
                    </Button>
                  </Box>
                  
                  {assessmentSkillCategories.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No categories found. Click "Reload from Skills" to load categories from existing skills, or add a new one below.
                    </Alert>
                  ) : (
                    <List sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      {assessmentSkillCategories.map((category, index) => (
                        <ListItem
                          key={category}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleDeleteAssessmentCategory(category)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText primary={`${index + 1}. ${category}`} />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Add New Category
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      label="Category Name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g., Footwork, Forehand Strokes"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newCategoryName.trim()) {
                          handleAddCategory();
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddCategory}
                      disabled={!newCategoryName.trim()}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseCategoryManagement}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      })()}

      {activeView === 'fitness-assessment' && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Fitness Assessment - {selectedSportForFitness}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={selectedFitnessCategoryFilter}
                  label="Filter by Category"
                  onChange={(e) => setSelectedFitnessCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {fitnessCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={async () => {
                  const categoriesFromFitness = await loadAllFitnessCategoriesForSport(selectedSportForFitness);
                  const mergedCategories = [...new Set([...categoriesFromFitness, ...fitnessCategories])];
                  setFitnessCategories(mergedCategories.sort());
                  setFitnessCategoryManagementOpen(true);
                }}
                sx={{ mr: 1 }}
              >
                Add/View Category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenFitnessDialog()}
              >
                Add Fitness Item
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fitness Assessment Items (
                  {selectedFitnessCategoryFilter === 'all' 
                    ? fitnessAssessments.length 
                    : fitnessAssessments.filter(f => f.category === selectedFitnessCategoryFilter).length
                  } / {fitnessAssessments.length})
              </Typography>
              
              {fitnessAssessmentsLoading && fitnessAssessments.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : fitnessAssessments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No fitness assessment items found for {selectedSportForFitness}.
                    Click "Add Fitness Item" to create one.
                  </Typography>
                </Box>
              ) : (
                <TableContainer 
                  component={Paper}
                  sx={{ 
                    maxHeight: 600,
                    overflow: 'auto',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Display Order</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fitnessAssessments
                        .filter((fitness) => selectedFitnessCategoryFilter === 'all' || fitness.category === selectedFitnessCategoryFilter)
                        .sort((a, b) => {
                          const orderA = a.displayOrder ?? 9999;
                          const orderB = b.displayOrder ?? 9999;
                          if (orderA !== orderB) return orderA - orderB;
                          if (a.category !== b.category) return (a.category || '').localeCompare(b.category || '');
                          return (a.itemName || '').localeCompare(b.itemName || '');
                        })
                        .map((fitness) => (
                        <TableRow key={fitness.id} hover>
                          <TableCell>{fitness.displayOrder ?? '-'}</TableCell>
                          <TableCell>
                            <Chip label={fitness.category} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>{fitness.itemName}</TableCell>
                          <TableCell>
                            <Chip
                              label={fitness.isActive ? 'Active' : 'Inactive'}
                              color={fitness.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Fitness Item">
                              <IconButton size="small" onClick={() => handleOpenFitnessDialog(fitness)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Fitness Item">
                              <IconButton size="small" color="error" onClick={() => handleDeleteFitnessAssessment(fitness.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Dialog */}
          <Dialog open={fitnessAssessmentsDialogOpen} onClose={handleCloseFitnessDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingFitnessAssessment ? 'Edit Fitness Assessment Item' : 'Add New Fitness Assessment Item'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Name *"
                    value={fitnessFormData.itemName}
                    onChange={(e) => setFitnessFormData({ ...fitnessFormData, itemName: e.target.value })}
                    required
                    helperText="Name of the fitness item to be assessed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      value={fitnessFormData.category}
                      label="Category *"
                      onChange={(e) => setFitnessFormData({ ...fitnessFormData, category: e.target.value })}
                      required
                    >
                      {fitnessCategories.length === 0 ? (
                        <MenuItem disabled value="">
                          No categories available. Add categories first.
                        </MenuItem>
                      ) : (
                        fitnessCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      {fitnessCategories.length === 0 
                        ? 'Click "Add/View Category" to create categories first'
                        : 'Select a category from the list'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={fitnessFormData.displayOrder ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFitnessFormData({ 
                        ...fitnessFormData, 
                        displayOrder: value === '' ? undefined : (parseInt(value) || undefined)
                      });
                    }}
                    helperText="Lower numbers appear first (optional)"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={fitnessFormData.isActive !== false ? 'active' : 'inactive'}
                      label="Status"
                      onChange={(e) => setFitnessFormData({ ...fitnessFormData, isActive: e.target.value === 'active' })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseFitnessDialog}>Cancel</Button>
              <Button onClick={handleSaveFitnessAssessment} variant="contained" disabled={fitnessAssessmentsLoading}>
                {fitnessAssessmentsLoading ? <CircularProgress size={20} /> : editingFitnessAssessment ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Category Management Dialog */}
          <Dialog open={fitnessCategoryManagementOpen} onClose={() => setFitnessCategoryManagementOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Categories - {selectedSportForFitness}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Existing Categories ({fitnessCategories.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      const categoriesFromFitness = await loadAllFitnessCategoriesForSport(selectedSportForFitness);
                      const mergedCategories = [...new Set([...categoriesFromFitness, ...fitnessCategories])];
                      setFitnessCategories(mergedCategories.sort());
                      setSuccess(`Categories reloaded. Found ${categoriesFromFitness.length} from fitness items, ${mergedCategories.length} total (including manually added)`);
                    }}
                  >
                    Reload from Fitness Items
                  </Button>
                </Box>
                
                {fitnessCategories.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No categories found. Click "Reload from Fitness Items" to load categories from existing items, or add a new one below.
                  </Alert>
                ) : (
                  <List sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {fitnessCategories.map((category, index) => (
                      <ListItem
                        key={category}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleDeleteFitnessCategory(category)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={`${index + 1}. ${category}`} />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Add New Category
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={newFitnessCategoryName}
                    onChange={(e) => setNewFitnessCategoryName(e.target.value)}
                    placeholder="e.g., Core Strength, Endurance"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newFitnessCategoryName.trim()) {
                        handleAddFitnessCategory();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddFitnessCategory}
                    disabled={!newFitnessCategoryName.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setFitnessCategoryManagementOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {activeView === 'drill-assessment' && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Drill Assessment - {selectedSportForDrill}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={selectedDrillCategoryFilter}
                  label="Filter by Category"
                  onChange={(e) => setSelectedDrillCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {drillCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={async () => {
                  const categoriesFromDrills = await loadAllDrillCategoriesForSport(selectedSportForDrill);
                  const mergedCategories = [...new Set([...categoriesFromDrills, ...drillCategories])];
                  setDrillCategories(mergedCategories.sort());
                  setDrillCategoryManagementOpen(true);
                }}
                sx={{ mr: 1 }}
              >
                Add/View Category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDrillDialog()}
              >
                Add Drill Item
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Drill Assessment Items (
                  {selectedDrillCategoryFilter === 'all' 
                    ? drillAssessments.length 
                    : drillAssessments.filter(d => d.category === selectedDrillCategoryFilter).length
                  } / {drillAssessments.length})
              </Typography>
              
              {drillAssessmentsLoading && drillAssessments.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : drillAssessments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No drill assessment items found for {selectedSportForDrill}.
                    Click "Add Drill Item" to create one.
                  </Typography>
                </Box>
              ) : (
                <TableContainer 
                  component={Paper}
                  sx={{ 
                    maxHeight: 600,
                    overflow: 'auto',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Display Order</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillAssessments
                        .filter((drill) => selectedDrillCategoryFilter === 'all' || drill.category === selectedDrillCategoryFilter)
                        .sort((a, b) => {
                          const orderA = a.displayOrder ?? 9999;
                          const orderB = b.displayOrder ?? 9999;
                          if (orderA !== orderB) return orderA - orderB;
                          if (a.category !== b.category) return (a.category || '').localeCompare(b.category || '');
                          return (a.itemName || '').localeCompare(b.itemName || '');
                        })
                        .map((drill) => (
                        <TableRow key={drill.id} hover>
                          <TableCell>{drill.displayOrder ?? '-'}</TableCell>
                          <TableCell>
                            <Chip label={drill.category} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>{drill.itemName}</TableCell>
                          <TableCell>
                            <Chip
                              label={drill.isActive ? 'Active' : 'Inactive'}
                              color={drill.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Drill Item">
                              <IconButton size="small" onClick={() => handleOpenDrillDialog(drill)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Drill Item">
                              <IconButton size="small" color="error" onClick={() => handleDeleteDrillAssessment(drill.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Dialog */}
          <Dialog open={drillAssessmentsDialogOpen} onClose={handleCloseDrillDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingDrillAssessment ? 'Edit Drill Assessment Item' : 'Add New Drill Assessment Item'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Name *"
                    value={drillFormData.itemName}
                    onChange={(e) => setDrillFormData({ ...drillFormData, itemName: e.target.value })}
                    required
                    helperText="Name of the drill item to be assessed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      value={drillFormData.category}
                      label="Category *"
                      onChange={(e) => setDrillFormData({ ...drillFormData, category: e.target.value })}
                      required
                    >
                      {drillCategories.length === 0 ? (
                        <MenuItem disabled value="">
                          No categories available. Add categories first.
                        </MenuItem>
                      ) : (
                        drillCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      {drillCategories.length === 0 
                        ? 'Click "Add/View Category" to create categories first'
                        : 'Select a category from the list'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Order"
                    type="number"
                    value={drillFormData.displayOrder ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDrillFormData({ 
                        ...drillFormData, 
                        displayOrder: value === '' ? undefined : (parseInt(value) || undefined)
                      });
                    }}
                    helperText="Lower numbers appear first (optional)"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={drillFormData.isActive !== false ? 'active' : 'inactive'}
                      label="Status"
                      onChange={(e) => setDrillFormData({ ...drillFormData, isActive: e.target.value === 'active' })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDrillDialog}>Cancel</Button>
              <Button onClick={handleSaveDrillAssessment} variant="contained" disabled={drillAssessmentsLoading}>
                {drillAssessmentsLoading ? <CircularProgress size={20} /> : editingDrillAssessment ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Category Management Dialog */}
          <Dialog open={drillCategoryManagementOpen} onClose={() => setDrillCategoryManagementOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Categories - {selectedSportForDrill}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Existing Categories ({drillCategories.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      const categoriesFromDrills = await loadAllDrillCategoriesForSport(selectedSportForDrill);
                      const mergedCategories = [...new Set([...categoriesFromDrills, ...drillCategories])];
                      setDrillCategories(mergedCategories.sort());
                      setSuccess(`Categories reloaded. Found ${categoriesFromDrills.length} from drill items, ${mergedCategories.length} total (including manually added)`);
                    }}
                  >
                    Reload from Drill Items
                  </Button>
                </Box>
                
                {drillCategories.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No categories found. Click "Reload from Drill Items" to load categories from existing items, or add a new one below.
                  </Alert>
                ) : (
                  <List sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {drillCategories.map((category, index) => (
                      <ListItem
                        key={category}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={() => handleDeleteDrillCategory(category)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={`${index + 1}. ${category}`} />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Add New Category
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={newDrillCategoryName}
                    onChange={(e) => setNewDrillCategoryName(e.target.value)}
                    placeholder="e.g., Forehand Drills, Backhand Drills"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newDrillCategoryName.trim()) {
                        handleAddDrillCategory();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddDrillCategory}
                    disabled={!newDrillCategoryName.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDrillCategoryManagementOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {activeView === 'video-limits' && (
        <AdminVideoLimitsTab sport={currentSport} />
      )}
      {activeView === 'contest-catalog' && (
        <AdminContestCatalog sport={currentSport} />
      )}
      {activeView === 'contest-tasks' && (
        <AdminContestTasks sport={currentSport} />
      )}
      {activeView === 'sponsor-leads' && (
        <AdminSponsorManagement />
      )}
      {activeView === 'subscriptions' && (
        <AdminSubscriptions />
      )}
      {activeView === 'customer-ops' && (
        <AdminCustomerOps />
      )}
      {activeView === 'support-tickets' && (
        <AdminSupportTickets />
      )}
      {activeView === 'careers-applications' && (
        <AdminCareersApplications />
      )}
      {activeView === 'sport-rollout' && (
        <AdminSportModuleRollout />
      )}
      {activeView === 'video-evaluation-queue' && (
        <AdminVideoEvaluationQueue sport={currentSport} />
      )}
      {activeView === 'evaluation-admin-management' && (
        <EvaluationAdminManagement />
      )}
      {activeView === 'admin-user-management' && (
        <AdminUserManagement />
      )}
      {activeView === 'master-drills' && (
        <AdminMasterDrills sport={currentSport} />
      )}
      {activeView === 'master-fitness' && (
        <AdminMasterFitness sport={currentSport} />
      )}
      {activeView === 'level-mappings' && (
        <AdminLevelMappings sport={currentSport} />
      )}
      {activeView === 'landing-elite-benchmarks' && (
        <AdminLandingEliteBenchmarks />
      )}

      {activeView === 'grade-configurations' && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Grade Configurations - Badminton
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingGradeId(null);
              setGradeForm({
                sport: 'Badminton',
                grade: 'C',
                min_score: 0,
                max_score: null,
                description: '',
                is_active: true
              });
              setGradeDialogOpen(true);
            }}
          >
            Add Grade
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Min Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gradeConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No grade configurations found for Badminton. Click "Add Grade" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    gradeConfigs
                      .sort((a, b) => a.min_score - b.min_score)
                      .map((config) => (
                        <TableRow key={config.id} hover>
                          <TableCell>
                            <Chip 
                              label={config.grade} 
                              color={config.grade === 'A+' ? 'success' : config.grade === 'A' ? 'primary' : config.grade === 'B' ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{config.min_score}</TableCell>
                          <TableCell>{config.max_score || 'No limit'}</TableCell>
                          <TableCell>{config.description || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={config.is_active ? 'Active' : 'Inactive'} 
                              color={config.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingGradeId(config.id);
                                setGradeForm({
                                  sport: config.sport,
                                  grade: config.grade,
                                  min_score: config.min_score,
                                  max_score: config.max_score,
                                  description: config.description || '',
                                  is_active: config.is_active
                                });
                                setGradeDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteGrade(config.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        </>
      )}

      {activeView === 'category-management' && (
        <>
          {success && (
            <Alert 
              key={`success-${success}`}
              severity="success" 
              sx={{ mb: 2 }} 
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Skill Categories - Badminton
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenCategoryDialog()}
              >
                Add Main Category
              </Button>
            </Box>
          </Box>

          <Card>
            <CardContent>
              {loading && categories.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Table Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Display Order</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No categories found. Click "Add Main Category" to create one.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (() => {
                          const mainCategories = categories.filter((cat: any) => !cat.parent_category_id);
                          const subcategories = categories.filter((cat: any) => cat.parent_category_id);
                          
                          return mainCategories.map((mainCat: any) => {
                            const subs = subcategories.filter((sub: any) => sub.parent_category_id === mainCat.id);
                            const isExpanded = expandedCategoryIds.includes(mainCat.id);
                            
                            return (
                              <React.Fragment key={mainCat.id}>
                                <TableRow hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {subs.length > 0 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            if (isExpanded) {
                                              setExpandedCategoryIds(expandedCategoryIds.filter(id => id !== mainCat.id));
                                            } else {
                                              setExpandedCategoryIds([...expandedCategoryIds, mainCat.id]);
                                            }
                                          }}
                                        >
                                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {mainCat.name}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label="Main Category" color="primary" size="small" />
                                  </TableCell>
                                  <TableCell>{mainCat.full_table_name || mainCat.table_name || '-'}</TableCell>
                                  <TableCell>{mainCat.description || '-'}</TableCell>
                                  <TableCell>{mainCat.display_order || 0}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenCategoryDialog(mainCat)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteCategory(mainCat.id)}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenCategoryDialog(null, true, mainCat)}
                                      color="primary"
                                      title="Add Subcategory"
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                {isExpanded && subs.map((sub: any) => (
                                  <TableRow key={sub.id} hover sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                    <TableCell>
                                      <Box sx={{ pl: 6, display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2">└─ {sub.name}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Chip label="Subcategory" color="secondary" size="small" />
                                    </TableCell>
                                    <TableCell>{sub.full_table_name || sub.table_name || '-'}</TableCell>
                                    <TableCell>{sub.description || '-'}</TableCell>
                                    <TableCell>{sub.display_order || 0}</TableCell>
                                    <TableCell>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenCategoryDialog(sub)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteCategory(sub.id)}
                                        color="error"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          });
                        })()
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeView === 'skills-management' && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Skills Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Category</InputLabel>
                <Select
                  value={selectedCategoryForSkills?.id || ''}
                  label="Select Category"
                  onChange={async (e) => {
                    const category = categories.find((c: any) => c.id === e.target.value);
                    setSelectedCategoryForSkills(category);
                    // Don't clear template - let loadSkills handle it
                    if (category && (category.table_name || category.full_table_name)) {
                      await loadSkills(category);
                    } else {
                      setSkills([]);
                      setCategoryTemplate(null);
                      setError('Selected category does not have a table assigned');
                    }
                  }}
                >
                  <MenuItem value="">-- Select Category --</MenuItem>
                  {(() => {
                    // Show all categories (main and subcategories) that have a table
                    const allCategoriesWithTables = categories.filter((cat: any) => {
                      // Include main categories with tables
                      if (!cat.parent_category_id && (cat.table_name || cat.full_table_name)) {
                        return true;
                      }
                      // Include subcategories with tables
                      if (cat.parent_category_id && (cat.table_name || cat.full_table_name)) {
                        return true;
                      }
                      return false;
                    });
                    
                    // Sort properly: main categories by display_order, subcategories immediately after their parent
                    const sortedCategories: any[] = [];
                    
                    // Get all main categories (with or without tables) sorted by display_order
                    const mainCategories = categories
                      .filter((cat: any) => !cat.parent_category_id)
                      .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
                    
                    // For each main category, add it (if it has a table) and then its subcategories (if they have tables)
                    mainCategories.forEach((mainCat: any) => {
                      // Add main category if it has a table
                      if (mainCat.table_name || mainCat.full_table_name) {
                        sortedCategories.push(mainCat);
                      }
                      
                      // Get subcategories of this main category that have tables, sorted by display_order
                      const subcategories = categories
                        .filter((cat: any) => 
                          cat.parent_category_id === mainCat.id && 
                          (cat.table_name || cat.full_table_name)
                        )
                        .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
                      
                      // Add subcategories immediately after their parent
                      sortedCategories.push(...subcategories);
                    });
                    
                    return sortedCategories.map((cat: any) => {
                      // Show parent category name if it's a subcategory
                      const parentCategory = cat.parent_category_id ? categories.find((c: any) => c.id === cat.parent_category_id) : null;
                      
                      // Get order numbers
                      const mainOrder = parentCategory ? (parentCategory.display_order || 0) : (cat.display_order || 0);
                      const subOrder = parentCategory ? (cat.display_order || 0) : null;
                      
                      // Format display name with order numbers
                      let categoryDisplayName = '';
                      if (parentCategory) {
                        // Subcategory: show "1.2 Category Name" format
                        categoryDisplayName = `${mainOrder}.${subOrder} ${parentCategory.name} > ${cat.name}`;
                      } else {
                        // Main category: show "1. Category Name" format
                        categoryDisplayName = `${mainOrder}. ${cat.name}`;
                      }
                      
                      // Use full_table_name if available, otherwise construct it
                      const tableDisplayName = cat.full_table_name || 
                        (cat.sport && cat.table_name ? `${cat.sport.toLowerCase()}_${cat.table_name}` : cat.table_name) ||
                        'No table';
                      return (
                        <MenuItem key={cat.id} value={cat.id}>
                          {categoryDisplayName} ({tableDisplayName})
                        </MenuItem>
                      );
                    });
                  })()}
                </Select>
              </FormControl>
              {selectedCategoryForSkills && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => {
                      const sport = selectedCategoryForSkills.sport?.toLowerCase() || 'badminton';
                      window.open(`/player/learning/courses/${sport}`, '_blank');
                    }}
                  >
                    Preview LMS
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSkillDialog()}
                    disabled={!selectedCategoryForSkills || (!selectedCategoryForSkills.table_name && !selectedCategoryForSkills.full_table_name)}
                  >
                    Add Skill
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {selectedCategoryForSkills ? (
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {selectedCategoryForSkills.name} Skills
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Table: {selectedCategoryForSkills.full_table_name || selectedCategoryForSkills.table_name} | {skills.length} skills
                  </Typography>
                </Box>
                
                {(skillsLoading || templateLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    {categoryTemplate && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Template loaded: {categoryTemplate.templateName} | Display Columns: {categoryTemplate.displayColumns?.length || 0}
                      </Alert>
                    )}
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        maxHeight: 600,
                        border: '4px solid #2196f3',
                        borderRadius: '4px',
                        overflowX: 'auto',
                        overflowY: 'auto',
                        position: 'relative',
                      }}
                    >
                      <Table stickyHeader sx={{ borderCollapse: 'separate', minWidth: 1400, tableLayout: 'auto' }}>
                        <TableHead>
                          <TableRow 
                            sx={{ 
                              background: '#f1f8e9',
                              backgroundImage: `
                                repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(76, 175, 80, 0.2) 3px, rgba(76, 175, 80, 0.2) 4px)
                              `,
                              borderBottom: '4px solid #2196f3',
                              '& .MuiTableCell-root': {
                                borderBottom: '4px solid #2196f3',
                                borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                                '&:last-child': {
                                  borderRight: 'none',
                                },
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                backgroundColor: '#f1f8e9',
                              },
                            }}
                          >
                            {(() => {
                              // Check if this is a "General" category type
                              // This applies to ALL categories with category_type === 'general' 
                              // (court_dimensions, rule1, and any future General subcategories)
                              // Detection is based on category_type field, not component names
                              const isGeneralType = categoryTemplate?.categoryType === 'general' || 
                                                   categoryTemplate?.category_type === 'general' ||
                                                   selectedCategoryForSkills?.category_type === 'general';
                              
                              // Get display columns from template or use all columns from first skill
                              let displayColumns: string[] = [];
                              
                              console.log('Table render - categoryTemplate:', categoryTemplate);
                              console.log('Table render - category_type:', categoryTemplate?.categoryType || categoryTemplate?.category_type || selectedCategoryForSkills?.category_type);
                              console.log('Table render - isGeneralType:', isGeneralType);
                              console.log('Table render - displayColumns:', categoryTemplate?.displayColumns);
                              
                              // For General types, only show Title column - all content goes in Content column
                              // This ensures consistent layout for all General category types
                              if (isGeneralType) {
                                displayColumns = ['title', 'content'];
                                console.log('✅ General type detected - using Title + Content columns');
                              } else if (categoryTemplate?.displayColumns && Array.isArray(categoryTemplate.displayColumns) && categoryTemplate.displayColumns.length > 0) {
                                // Use template's display columns, but filter out is_active
                                displayColumns = categoryTemplate.displayColumns.filter((col: string) => col !== 'is_active');
                                console.log('✅ Using template displayColumns:', displayColumns);
                              } else if (categoryTemplate?.tableSchema?.columns && Array.isArray(categoryTemplate.tableSchema.columns)) {
                                // If displayColumns not set, use columns from tableSchema that have show_in_list: true
                                // Always include title and skill_name, exclude is_active unless explicitly shown
                                displayColumns = categoryTemplate.tableSchema.columns
                                  .filter((col: any) => {
                                    // Explicitly exclude is_active - it should never appear in listings
                                    if (col.name === 'is_active') {
                                      return false;
                                    }
                                    const isImportant = col.name === 'title' || col.name === 'skill_name';
                                    return (col.show_in_list === true || isImportant);
                                  })
                                  .map((col: any) => col.name);
                                
                                // Ensure title is always included if it exists in the schema
                                if (categoryTemplate.tableSchema.columns.some((col: any) => col.name === 'title') && !displayColumns.includes('title')) {
                                  displayColumns.unshift('title');
                                }
                              } else if (skills.length > 0) {
                                // If no template, use all keys from first skill (except internal fields and is_active)
                                const firstSkill = skills[0];
                                displayColumns = Object.keys(firstSkill).filter(key => 
                                  !['id', 'created_at', 'updated_at', 'category_id', 'subcategory_id', 'is_active'].includes(key)
                                );
                              } else {
                                // If no template and no skills, wait for template to load
                                // Don't show fallback columns - they're wrong
                                if (templateLoading) {
                                  displayColumns = []; // Wait for template
                                } else {
                                  displayColumns = ['title']; // Generic fallback only if template truly doesn't exist
                                }
                              }

                              // Get field labels from template
                              const fieldLabels = categoryTemplate?.fieldLabels || {};
                              
                              // Helper to get label for a column
                              const getColumnLabel = (col: string): string => {
                                if (col === 'content') return 'Content';
                                if (fieldLabels[col]) return fieldLabels[col];
                                // Convert snake_case to Title Case
                                return col.split('_').map(word => 
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ');
                              };

                              return (
                                <>
                                  {displayColumns.map((col) => (
                                    <TableCell 
                                      key={col}
                                      sx={{ 
                                        fontWeight: 700, 
                                        color: '#1b5e20', 
                                        fontSize: '0.95rem', 
                                        py: 1.5, 
                                        bgcolor: '#f1f8e9', 
                                        verticalAlign: 'middle', 
                                        textAlign: 'left',
                                        maxWidth: col === 'title' || col === 'skill_name' ? 200 : (col === 'content' ? 'none' : 200),
                                        width: col === 'content' ? 'auto' : undefined,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}
                                    >
                                      {getColumnLabel(col)}
                                    </TableCell>
                                  ))}
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 700, 
                                      color: '#1b5e20', 
                                      fontSize: '0.95rem', 
                                      py: 1.5, 
                                      bgcolor: '#f1f8e9', 
                                      verticalAlign: 'middle', 
                                      textAlign: 'center',
                                      position: 'sticky',
                                      right: 0,
                                      minWidth: 100,
                                      width: 100,
                                      zIndex: 101,
                                      boxShadow: '-2px 0 4px rgba(0,0,0,0.15)',
                                      borderLeft: '2px solid rgba(0,0,0,0.15)'
                                    }}
                                  >
                                    Actions
                                  </TableCell>
                                </>
                              );
                            })()}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {skills.length === 0 ? (
                            <TableRow>
                              <TableCell 
                                colSpan={
                                  (categoryTemplate?.displayColumns?.length || 
                                   (skills.length > 0 ? Object.keys(skills[0]).filter(k => !['id', 'created_at', 'updated_at'].includes(k)).length : 7)) + 1
                                } 
                                align="center" 
                                sx={{ py: 3 }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  No skills found in this category. Click "Add Skill" to create one.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            skills.map((skill: any) => {
                              // Check if this is a "General" category type
                              // This applies to ALL categories with category_type === 'general' 
                              // (court_dimensions, rule1, and any future General subcategories)
                              // Detection is based on category_type field, not component names
                              const isGeneralType = categoryTemplate?.categoryType === 'general' || 
                                                   categoryTemplate?.category_type === 'general' ||
                                                   selectedCategoryForSkills?.category_type === 'general';
                              
                              // Get display columns from template or use all columns
                              let displayColumns: string[] = [];
                              
                              // For General types, only show Title column - all content goes in Content column
                              // This ensures consistent layout for all General category types
                              if (isGeneralType) {
                                displayColumns = ['title', 'content'];
                              } else if (categoryTemplate?.tableSchema?.columns && Array.isArray(categoryTemplate.tableSchema.columns)) {
                                // Use all columns from tableSchema that have show_in_list: true OR have data
                                displayColumns = categoryTemplate.tableSchema.columns
                                  .filter((col: any) => {
                                    // Show if: show_in_list is not false AND (has data OR is is_active)
                                    let hasData = skill[col.name] !== null && skill[col.name] !== undefined && skill[col.name] !== '';
                                    // For JSONB arrays (measurements, images, videos), check if they have valid items
                                    if ((col.name === 'measurements' || col.name === 'images' || col.name === 'videos' || col.name === 'text_content') && Array.isArray(skill[col.name])) {
                                      if (col.name === 'measurements') {
                                        hasData = skill[col.name].some((item: any) => item && (item.key || item.name) && (item.value || item.Value));
                                      } else {
                                        hasData = skill[col.name].length > 0 && skill[col.name].some((item: any) => item && (item.url || item.title || item.content));
                                      }
                                    }
                                    // Title and skill_name are always important and should show
                                    // is_active should NEVER show in listings (removed feature)
                                    const isImportant = col.name === 'title' || col.name === 'skill_name';
                                    // Explicitly exclude is_active - it should never appear in listings
                                    if (col.name === 'is_active') {
                                      return false;
                                    }
                                    // Always show title/skill_name even if no data
                                    return (col.show_in_list === true || (isImportant && (hasData || col.show_in_list !== false)) || (hasData && col.name !== 'is_active')) && 
                                           !['id', 'created_at', 'updated_at', 'category_id', 'subcategory_id'].includes(col.name);
                                  })
                                  .map((col: any) => col.name);
                              } else if (categoryTemplate?.displayColumns && Array.isArray(categoryTemplate.displayColumns) && categoryTemplate.displayColumns.length > 0) {
                                // Use template's display columns, but also include any columns with data
                                // Filter out is_active from template columns
                                const templateCols = categoryTemplate.displayColumns.filter((col: string) => col !== 'is_active');
                                const allKeys = Object.keys(skill).filter(key => 
                                  !['id', 'created_at', 'updated_at', 'category_id', 'subcategory_id', 'is_active'].includes(key) &&
                                  skill[key] !== null && skill[key] !== undefined && skill[key] !== ''
                                );
                                displayColumns = [...new Set([...templateCols, ...allKeys])];
                              } else {
                                // Fallback: use all keys that have data (exclude is_active)
                                displayColumns = Object.keys(skill).filter(key => 
                                  !['id', 'created_at', 'updated_at', 'category_id', 'subcategory_id', 'is_active'].includes(key) &&
                                  skill[key] !== null && skill[key] !== undefined && skill[key] !== ''
                                );
                              }

                              // Helper to format all content for General types
                              const formatGeneralContent = (skill: any): React.ReactNode => {
                                const content: React.ReactNode[] = [];
                                
                                // Description
                                if (skill.description) {
                                  content.push(
                                    <Box key="desc" sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>Description:</Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{skill.description}</Typography>
                                    </Box>
                                  );
                                }
                                
                                // Explanation
                                if (skill.explanation) {
                                  content.push(
                                    <Box key="expl" sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>Explanation:</Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{skill.explanation}</Typography>
                                    </Box>
                                  );
                                }
                                
                                // Images
                                if (skill.images && Array.isArray(skill.images) && skill.images.length > 0) {
                                  content.push(
                                    <Box key="images" sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>Images:</Typography>
                                      {skill.images.map((item: any, idx: number) => (
                                        <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                          {item.url ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                                              📷 {item.title || `Image ${idx + 1}`}
                                            </a>
                                          ) : (
                                            <span style={{ color: '#999' }}>📷 Image {idx + 1} (no URL)</span>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                                
                                // Videos
                                if (skill.videos && Array.isArray(skill.videos) && skill.videos.length > 0) {
                                  content.push(
                                    <Box key="videos" sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>Videos:</Typography>
                                      {skill.videos.map((item: any, idx: number) => (
                                        <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                          {item.url ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                                              🎥 {item.title || `Video ${idx + 1}`}
                                            </a>
                                          ) : (
                                            <span style={{ color: '#999' }}>🎥 Video {idx + 1} (no URL)</span>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                                
                                // Measurements/Notes
                                if (skill.measurements && Array.isArray(skill.measurements) && skill.measurements.length > 0) {
                                  const validMeasurements = skill.measurements.filter((item: any) => item && (item.key || item.name) && (item.value || item.Value));
                                  if (validMeasurements.length > 0) {
                                    content.push(
                                      <Box key="notes" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#666' }}>Notes:</Typography>
                                        {validMeasurements.map((item: any, idx: number) => (
                                          <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                            <strong>{item.key || item.name || 'N/A'}:</strong> {item.value || item.Value || 'N/A'}
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  }
                                }
                                
                                return content.length > 0 ? <Box>{content}</Box> : <Typography variant="body2" color="text.secondary">-</Typography>;
                              };
                              
                              // Helper to format cell value
                              const formatCellValue = (value: any, column: string): React.ReactNode => {
                                // For General types, handle content column specially
                                if (column === 'content') {
                                  return formatGeneralContent(skill);
                                }
                                
                                if (value === null || value === undefined) {
                                  if (column === 'is_active') {
                                    return (
                                      <Chip 
                                        label="Inactive" 
                                        color="default"
                                        size="small"
                                      />
                                    );
                                  }
                                  return '-';
                                }
                                
                                // Handle JSONB objects (measurements, images, videos)
                                if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
                                  // Check if it's a JSONB object that should be displayed
                                  if (column === 'measurements' || column === 'images' || column === 'videos') {
                                    // If it's already an array, use it; otherwise try to parse
                                    const arr = Array.isArray(value) ? value : (value.data || []);
                                    if (Array.isArray(arr) && arr.length > 0) {
                                      if (column === 'measurements') {
                                        const validMeasurements = arr.filter((item: any) => item && (item.key || item.name) && (item.value || item.Value));
                                        if (validMeasurements.length === 0) return '-';
                                        return (
                                          <Box sx={{ fontSize: '0.875rem', lineHeight: 1.8 }}>
                                            {validMeasurements.map((item: any, idx: number) => (
                                              <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                                <strong>{item.key || item.name || 'N/A'}:</strong> {item.value || item.Value || 'N/A'}
                                              </Box>
                                            ))}
                                          </Box>
                                        );
                                      } else {
                                        return arr.map((item: any, idx: number) => (
                                          <Box key={idx} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                                            {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer">Link {idx + 1}</a> : '-'}
                                            {item.description && ` - ${item.description}`}
                                          </Box>
                                        ));
                                      }
                                    }
                                    return '-';
                                  }
                                  // For other objects, stringify
                                  return JSON.stringify(value);
                                }
                                
                                // Handle arrays
                                if (Array.isArray(value)) {
                                  // Check if it's an array of objects (key-value pairs or media)
                                  if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                                    if (column === 'measurements') {
                                      const validMeasurements = value.filter((item: any) => item && (item.key || item.name) && (item.value || item.Value));
                                      if (validMeasurements.length === 0) return '-';
                                      return (
                                        <Box sx={{ fontSize: '0.875rem', lineHeight: 1.8 }}>
                                          {validMeasurements.map((item: any, idx: number) => (
                                            <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                              <strong>{item.key || item.name || 'N/A'}:</strong> {item.value || item.Value || 'N/A'}
                                            </Box>
                                          ))}
                                        </Box>
                                      );
                                    } else if (column === 'images') {
                                      if (value.length === 0) return '-';
                                      return (
                                        <Box sx={{ fontSize: '0.875rem', lineHeight: 1.8 }}>
                                          {value.map((item: any, idx: number) => (
                                            <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                              {item.url ? (
                                                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                                                  📷 {item.title || `Image ${idx + 1}`}
                                                </a>
                                              ) : (
                                                <span style={{ color: '#999' }}>📷 Image {idx + 1} (no URL)</span>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      );
                                    } else if (column === 'videos') {
                                      if (value.length === 0) return '-';
                                      return (
                                        <Box sx={{ fontSize: '0.875rem', lineHeight: 1.8 }}>
                                          {value.map((item: any, idx: number) => (
                                            <Box key={idx} sx={{ mb: 0.5, display: 'block' }}>
                                              {item.url ? (
                                                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                                                  🎥 {item.title || `Video ${idx + 1}`}
                                                </a>
                                              ) : (
                                                <span style={{ color: '#999' }}>🎥 Video {idx + 1} (no URL)</span>
                                              )}
                                            </Box>
                                          ))}
                                        </Box>
                                      );
                                    }
                                    return value.map((item: any, idx: number) => (
                                      <Box key={idx} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                                        {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                      </Box>
                                    ));
                                  }
                                  return value.length > 0 ? value.join(', ') : '-';
                                }
                                
                                // Handle booleans
                                if (typeof value === 'boolean') {
                                  if (column === 'is_active') {
                                    return (
                                      <Chip 
                                        label={value ? 'Active' : 'Inactive'} 
                                        color={value ? 'success' : 'default'}
                                        size="small"
                                      />
                                    );
                                  }
                                  return value ? 'Yes' : 'No';
                                }
                                
                                // Handle long text
                                if (typeof value === 'string' && value.length > 50) {
                                  return (
                                    <Typography variant="body2" sx={{ 
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      title: value
                                    }}>
                                      {value}
                                    </Typography>
                                  );
                                }
                                
                                // Handle objects (like JSONB)
                                if (typeof value === 'object') {
                                  return JSON.stringify(value);
                                }
                                
                                return String(value);
                              };

                              return (
                                <TableRow key={skill.id} hover>
                                  {displayColumns.map((col) => (
                                    <TableCell 
                                      key={col}
                                      sx={{
                                        maxWidth: col === 'title' || col === 'skill_name' ? 200 : (col === 'content' ? 'none' : 250),
                                        width: col === 'content' ? 'auto' : undefined,
                                        overflow: 'visible',
                                        wordBreak: 'break-word',
                                        verticalAlign: 'top',
                                        padding: '8px',
                                        whiteSpace: 'normal'
                                      }}
                                    >
                                      {formatCellValue(skill[col], col)}
                                    </TableCell>
                                  ))}
                                  <TableCell 
                                    sx={{
                                      position: 'sticky',
                                      right: 0,
                                      backgroundColor: '#ffffff',
                                      minWidth: 100,
                                      width: 100,
                                      zIndex: 100,
                                      boxShadow: '-2px 0 4px rgba(0,0,0,0.15)',
                                      whiteSpace: 'nowrap',
                                      borderLeft: '2px solid rgba(0,0,0,0.15)',
                                      verticalAlign: 'middle'
                                    }}
                                  >
                                    {skill.is_active !== false ? (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenSkillDialog(skill)}
                                          title="Edit skill"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteSkill(skill.id)}
                                          color="error"
                                          title="Delete skill"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <Chip 
                                        label="Deleted" 
                                        size="small" 
                                        color="default" 
                                        variant="outlined"
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info">
              Please select a category from the dropdown to view and manage skills.
            </Alert>
          )}
        </>
      )}

      {/* Create Player Dialog */}
      <Dialog 
        open={createPlayerOpen} 
        onClose={() => { setCreatePlayerOpen(false); setEditingPlayerId(null); setPlayerForm({}); }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{editingPlayerId ? 'Edit Player' : 'Create New Player'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={playerForm.firstName || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                value={playerForm.lastName || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={playerForm.email || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, email: e.target.value })}
                disabled={!!editingPlayerId}
                helperText={editingPlayerId ? "Email cannot be changed" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={playerForm.password || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, password: e.target.value })}
                disabled={!!editingPlayerId}
                helperText={editingPlayerId ? "Password cannot be changed" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth *"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={playerForm.dateOfBirth || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, dateOfBirth: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender *</InputLabel>
                <Select
                  value={playerForm.gender || ''}
                  onChange={(e) => setPlayerForm({ ...playerForm, gender: e.target.value as any })}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Level *</InputLabel>
                <Select
                  value={playerForm.level || ''}
                  onChange={(e) => setPlayerForm({ ...playerForm, level: e.target.value as any })}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Age Category *</InputLabel>
                <Select
                  value={playerForm.ageCategory || ''}
                  onChange={(e) => setPlayerForm({ ...playerForm, ageCategory: e.target.value as any })}
                >
                  <MenuItem value="U9">U9</MenuItem>
                  <MenuItem value="U11">U11</MenuItem>
                  <MenuItem value="U13">U13</MenuItem>
                  <MenuItem value="U15">U15</MenuItem>
                  <MenuItem value="U17">U17</MenuItem>
                  <MenuItem value="U19">U19</MenuItem>
                  <MenuItem value="Senior">Senior</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={playerForm.country || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Primary Sport *</InputLabel>
                <Select
                  value={playerForm.primarySport || ''}
                  onChange={(e) => setPlayerForm({ ...playerForm, primarySport: e.target.value })}
                >
                  <MenuItem value="Badminton">Badminton</MenuItem>
                  <MenuItem value="Tennis">Tennis</MenuItem>
                  <MenuItem value="Pickleball">Pickleball</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Secondary Sport</InputLabel>
                <Select
                  value={playerForm.secondarySport || ''}
                  onChange={(e) => setPlayerForm({ ...playerForm, secondarySport: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Badminton">Badminton</MenuItem>
                  <MenuItem value="Tennis">Tennis</MenuItem>
                  <MenuItem value="Pickleball">Pickleball</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Academy Name"
                value={playerForm.academyName || ''}
                onChange={(e) => setPlayerForm({ ...playerForm, academyName: e.target.value })}
                helperText="Leave empty if using personal trainer"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreatePlayerOpen(false); setEditingPlayerId(null); setPlayerForm({}); }}>Cancel</Button>
          <Button onClick={handleCreatePlayer} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (editingPlayerId ? 'Save Changes' : 'Create Player')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Coach Dialog */}
      <Dialog 
        open={createCoachOpen} 
        onClose={() => { setCreateCoachOpen(false); setEditingCoachId(null); setCoachForm({}); }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{editingCoachId ? 'Edit Coach' : 'Create New Coach'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={coachForm.firstName || ''}
                onChange={(e) => setCoachForm({ ...coachForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                value={coachForm.lastName || ''}
                onChange={(e) => setCoachForm({ ...coachForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={coachForm.email || ''}
                onChange={(e) => setCoachForm({ ...coachForm, email: e.target.value })}
                disabled={!!editingCoachId}
                helperText={editingCoachId ? "Email cannot be changed" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                value={coachForm.password || ''}
                onChange={(e) => setCoachForm({ ...coachForm, password: e.target.value })}
                disabled={!!editingCoachId}
                helperText={editingCoachId ? "Password cannot be changed" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Primary Sport *</InputLabel>
                <Select
                  value={coachForm.primarySport || ''}
                  onChange={(e) => setCoachForm({ ...coachForm, primarySport: e.target.value })}
                >
                  <MenuItem value="Badminton">Badminton</MenuItem>
                  <MenuItem value="Tennis">Tennis</MenuItem>
                  <MenuItem value="Pickleball">Pickleball</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Specialization</InputLabel>
                <Select
                  value={coachForm.specialization || ''}
                  onChange={(e) => setCoachForm({ ...coachForm, specialization: e.target.value })}
                >
                  <MenuItem value="Singles">Singles</MenuItem>
                  <MenuItem value="Doubles">Doubles</MenuItem>
                  <MenuItem value="Junior Development">Junior Development</MenuItem>
                  <MenuItem value="Advanced Training">Advanced Training</MenuItem>
                  <MenuItem value="Fitness & Conditioning">Fitness & Conditioning</MenuItem>
                  <MenuItem value="Mental Coaching">Mental Coaching</MenuItem>
                  <MenuItem value="Technique Specialist">Technique Specialist</MenuItem>
                  <MenuItem value="Tournament Preparation">Tournament Preparation</MenuItem>
                  <MenuItem value="General Coaching">General Coaching</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience (years)"
                type="number"
                value={coachForm.experience || ''}
                onChange={(e) => setCoachForm({ ...coachForm, experience: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={coachForm.country || ''}
                onChange={(e) => setCoachForm({ ...coachForm, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Academy Name"
                value={coachForm.academyName || ''}
                onChange={(e) => setCoachForm({ ...coachForm, academyName: e.target.value })}
                helperText="Leave empty if personal trainer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Trainer Type</InputLabel>
                <Select
                  value={coachForm.isPersonalTrainer ? 'personal' : 'academy'}
                  onChange={(e) => setCoachForm({ ...coachForm, isPersonalTrainer: e.target.value === 'personal' })}
                >
                  <MenuItem value="academy">Academy Coach</MenuItem>
                  <MenuItem value="personal">Personal Trainer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={3}
                value={coachForm.bio || ''}
                onChange={(e) => setCoachForm({ ...coachForm, bio: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateCoachOpen(false); setEditingCoachId(null); setCoachForm({}); }}>Cancel</Button>
          <Button onClick={handleCreateCoach} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (editingCoachId ? 'Save Changes' : 'Create Coach')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Performance Data Dialog */}
      <Dialog open={performanceOpen} onClose={() => setPerformanceOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Enter Performance Data</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Player *</InputLabel>
                <Select
                  value={performanceForm.playerId || ''}
                  onChange={(e) => setPerformanceForm({ ...performanceForm, playerId: e.target.value })}
                >
                  {players
                    .filter((player) => player.user) // Filter out players without user data
                    .map((player) => (
                      <MenuItem key={player.id} value={player.id}>
                        {player.user?.firstName || ''} {player.user?.lastName || ''} {!player.user && `(ID: ${player.id})`}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Shot Type *</InputLabel>
                <Select
                  value={performanceForm.shotTypeId || ''}
                  onChange={(e) => setPerformanceForm({ ...performanceForm, shotTypeId: e.target.value })}
                >
                  {shotTypes.map((shot) => (
                    <MenuItem key={shot.id} value={shot.id}>
                      {shot.name} (Level {shot.difficultyRating})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Court Grid *</InputLabel>
                <Select
                  value={performanceForm.courtGridId || ''}
                  onChange={(e) => setPerformanceForm({ ...performanceForm, courtGridId: e.target.value })}
                >
                  {courtGrids.map((grid) => (
                    <MenuItem key={grid.id} value={grid.id}>
                      {grid.name} ({grid.courtType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Attempted *"
                type="number"
                value={performanceForm.attempted || ''}
                onChange={(e) => setPerformanceForm({ ...performanceForm, attempted: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Successful *"
                type="number"
                value={performanceForm.successful || ''}
                onChange={(e) => setPerformanceForm({ ...performanceForm, successful: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Accuracy Score (0-100) *"
                type="number"
                value={performanceForm.accuracyScore || ''}
                onChange={(e) => setPerformanceForm({ ...performanceForm, accuracyScore: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Speed Score (0-100) *"
                type="number"
                value={performanceForm.speedScore || ''}
                onChange={(e) => setPerformanceForm({ ...performanceForm, speedScore: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={performanceForm.comments || ''}
                onChange={(e) => setPerformanceForm({ ...performanceForm, comments: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPerformanceOpen(false)}>Cancel</Button>
          <Button onClick={handleEnterPerformance} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Enter Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player-Coach Linking Dialog */}
      {selectedCoachForLinking && playerCoachLinkOpen && (
        <PlayerCoachLinking
          coach={selectedCoachForLinking}
          onClose={() => {
            setPlayerCoachLinkOpen(false);
            setSelectedCoachForLinking(null);
          }}
        />
      )}

      {/* Grade Configuration Dialog */}
      <Dialog open={gradeDialogOpen} onClose={() => { setGradeDialogOpen(false); setEditingGradeId(null); setGradeForm({ sport: 'Badminton', grade: 'C', min_score: 0, max_score: null, description: '', is_active: true }); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGradeId ? 'Edit Grade Configuration' : 'Add Grade Configuration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sport"
                value={gradeForm.sport || 'Badminton'}
                disabled
                helperText="This page is for Badminton grade configurations"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Grade *</InputLabel>
                <Select
                  value={gradeForm.grade || 'C'}
                  label="Grade *"
                  onChange={(e) => setGradeForm({ ...gradeForm, grade: e.target.value })}
                >
                  <MenuItem value="C">C</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="A+">A+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Score *"
                type="number"
                value={gradeForm.min_score ?? ''}
                onChange={(e) => setGradeForm({ ...gradeForm, min_score: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Score"
                type="number"
                value={gradeForm.max_score ?? ''}
                onChange={(e) => setGradeForm({ ...gradeForm, max_score: e.target.value ? parseInt(e.target.value) : null })}
                inputProps={{ min: 0 }}
                helperText="Leave empty for no upper limit (e.g., A+)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={gradeForm.description || ''}
                onChange={(e) => setGradeForm({ ...gradeForm, description: e.target.value })}
                placeholder="e.g., Needs improvement, Good performance, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={gradeForm.is_active !== false ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setGradeForm({ ...gradeForm, is_active: e.target.value === 'active' })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGradeDialogOpen(false); setEditingGradeId(null); setGradeForm({ sport: 'Badminton', grade: 'C', min_score: 0, max_score: null, description: '', is_active: true }); }}>
            Cancel
          </Button>
          <Button onClick={handleSaveGrade} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : editingGradeId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteTarget?.type}?
            This action will mark the {deleteTarget?.type} as deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Skill Confirmation Dialog */}
      <Dialog open={deleteSkillConfirmOpen} onClose={() => { setDeleteSkillConfirmOpen(false); setSkillToDelete(null); }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this skill? This will deactivate it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteSkillConfirmOpen(false); setSkillToDelete(null); }}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              console.log('[Delete Skill] 🗑️ Delete button clicked in dialog');
              console.log('[Delete Skill] skillToDelete state:', skillToDelete);
              console.log('[Delete Skill] selectedCategoryForSkills:', selectedCategoryForSkills);
              await confirmDeleteSkill();
            }} 
            color="error" 
            variant="contained" 
            disabled={skillsLoading}
          >
            {skillsLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryConfirmOpen} onClose={() => { setDeleteCategoryConfirmOpen(false); setCategoryToDelete(null); }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this category? This will deactivate it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteCategoryConfirmOpen(false); setCategoryToDelete(null); }}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              console.log('[Delete Category] 🗑️ Delete button clicked in dialog');
              console.log('[Delete Category] categoryToDelete:', categoryToDelete);
              await confirmDeleteCategory();
            }} 
            color="error" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => { setCategoryDialogOpen(false); setEditingCategory(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : (isAddingSubcategory || categoryForm.parentCategoryId) ? 'Add Subcategory' : 'Add Main Category'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Sport *</InputLabel>
                <Select
                  value={categoryForm.sport}
                  label="Sport *"
                  onChange={(e) => setCategoryForm({ ...categoryForm, sport: e.target.value })}
                  disabled={!!editingCategory || (isAddingSubcategory && !!parentCategoryForSubcategory)}
                >
                  <MenuItem value="Badminton">Badminton</MenuItem>
                  <MenuItem value="Cricket">Cricket</MenuItem>
                  <MenuItem value="Tennis">Tennis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!editingCategory && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Parent Category</InputLabel>
                  <Select
                    value={categoryForm.parentCategoryId}
                    label="Parent Category"
                    onChange={(e) => setCategoryForm({ ...categoryForm, parentCategoryId: e.target.value })}
                    disabled={isAddingSubcategory && !!parentCategoryForSubcategory}
                  >
                    <MenuItem value="">None (Main Category)</MenuItem>
                    {categories
                      .filter((cat: any) => !cat.parent_category_id && cat.sport === categoryForm.sport)
                      .map((cat: any) => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name *"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category Type *</InputLabel>
                <Select
                  value={categoryForm.categoryType || 'general'}
                  label="Category Type *"
                  onChange={(e) => setCategoryForm({ ...categoryForm, categoryType: e.target.value })}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="core">Core</MenuItem>
                </Select>
                <FormHelperText>
                  General: Uses title, text, before/after text, images, videos, key-value fields. Core: Uses skill-specific schema.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Table Name"
                value={categoryForm.tableName}
                onChange={(e) => {
                  let value = e.target.value;
                  // Auto-add sport prefix if user types without it
                  const sport = categoryForm.sport || 'Badminton';
                  const normalizedSport = sport.toLowerCase();
                  if (value && !value.toLowerCase().startsWith(normalizedSport + '_')) {
                    // Don't auto-modify while typing, but show warning
                  }
                  setCategoryForm({ ...categoryForm, tableName: value });
                }}
                helperText={
                  categoryForm.tableName && !categoryForm.tableName.toLowerCase().startsWith((categoryForm.sport || 'Badminton').toLowerCase() + '_')
                    ? `⚠️ Sport prefix will be auto-added: ${(categoryForm.sport || 'Badminton').toLowerCase()}_${categoryForm.tableName}`
                    : categoryForm.tableName
                      ? `✓ Format: {sport}_learning_{type}_{category} (e.g., badminton_learning_skills_strokes_shots)`
                      : 'Optional. Format: {sport}_learning_{type}_{category}. Sport prefix will be auto-added if missing.'
                }
                placeholder="learning_skills_strokes_shots"
                error={!!(categoryForm.tableName && !categoryForm.tableName.toLowerCase().startsWith((categoryForm.sport || 'Badminton').toLowerCase() + '_') && categoryForm.tableName.length > 0)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={categoryForm.displayOrder === 0 ? '' : categoryForm.displayOrder}
                onChange={(e) => {
                  const value = e.target.value;
                  setCategoryForm({ ...categoryForm, displayOrder: value === '' ? 0 : parseInt(value) || 0 });
                }}
                inputProps={{ min: 0 }}
                placeholder="0"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCategoryDialogOpen(false); setEditingCategory(null); }}>
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} variant="contained" disabled={loading || !categoryForm.name}>
            {loading ? <CircularProgress size={20} /> : editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skills Management Dialog */}
      <Dialog 
        open={skillDialogOpen} 
        onClose={() => { setSkillDialogOpen(false); setEditingSkill(null); setSkillForm({}); }} 
        maxWidth={(() => {
          const renderComponent = categoryTemplate?.renderComponent || categoryTemplate?.render_component;
          return renderComponent === 'court_dimensions' || renderComponent === 'CourtDimensionsTemplate' ? 'md' : 'lg';
        })()} 
        fullWidth={(() => {
          const renderComponent = categoryTemplate?.renderComponent || categoryTemplate?.render_component;
          return !(renderComponent === 'court_dimensions' || renderComponent === 'CourtDimensionsTemplate');
        })()}
      >
        <DialogTitle>
          {editingSkill ? 'Edit Skill' : 'Add New Skill'}
          {selectedCategoryForSkills && ` - ${selectedCategoryForSkills.name}`}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {/* Check render_component from template to determine which form to use */}
          {(() => {
            const renderComponent = categoryTemplate?.renderComponent || categoryTemplate?.render_component;
            const useCourtDimensionsForm = renderComponent === 'court_dimensions' || renderComponent === 'CourtDimensionsTemplate';
            // Removed excessive logging - only log when categoryTemplate changes
            return useCourtDimensionsForm;
          })() ? (
            <>
              {/* Category and Module Selection for Court Dimensions */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                  Category & Module
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!!editingSkill}>
                      <InputLabel id="category-label">Category *</InputLabel>
                      <Select
                        labelId="category-label"
                        value={skillForm.category_id || ''}
                        label="Category *"
                        onChange={(e) => {
                          const category = categories.find((c: any) => c.id === e.target.value);
                          setSkillForm({ ...skillForm, category_id: e.target.value, subcategory_id: null });
                          if (category) loadTemplate(category.id);
                        }}
                        required
                      >
                        {categories
                          .filter((cat: any) => {
                            // Always show main categories (no parent)
                            if (cat.parent_category_id) return false;
                            
                            // If editing, show the selected category even if it doesn't have table_name
                            if (editingSkill && skillForm.category_id === cat.id) {
                              return true;
                            }
                            
                            // If creating new and selectedCategoryForSkills is a subcategory, 
                            // show its parent even if it doesn't have table_name
                            if (!editingSkill && selectedCategoryForSkills?.parent_category_id && 
                                cat.id === selectedCategoryForSkills.parent_category_id) {
                              return true;
                            }
                            
                            // Otherwise, only show categories with table_name (subcategories have tables)
                            return cat.table_name || cat.full_table_name;
                          })
                          .map((cat: any) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!!editingSkill || !!selectedCategoryForSkills?.parent_category_id}>
                      <InputLabel id="subcategory-label">Module/Subcategory</InputLabel>
                      <Select
                        labelId="subcategory-label"
                        value={skillForm.subcategory_id || ''}
                        label="Module/Subcategory"
                        onChange={(e) => {
                          const subcategoryId = e.target.value || null;
                          setSkillForm({ ...skillForm, subcategory_id: subcategoryId });
                          if (subcategoryId) {
                            const subcategory = categories.find((c: any) => c.id === subcategoryId);
                            if (subcategory && (subcategory.table_name || subcategory.full_table_name)) {
                              loadTemplate(subcategoryId);
                            }
                          }
                        }}
                      >
                        <MenuItem value="">-- None --</MenuItem>
                        {(() => {
                          const parentCategoryId = skillForm.category_id;
                          if (!parentCategoryId) return [];
                          
                          const subcategories = categories.filter((cat: any) => cat.parent_category_id === parentCategoryId);
                          
                          if (editingSkill && skillForm.subcategory_id) {
                            const currentSub = categories.find((c: any) => c.id === skillForm.subcategory_id);
                            if (currentSub && !subcategories.find((s: any) => s.id === currentSub.id)) {
                              subcategories.push(currentSub);
                            }
                          }
                          
                          return subcategories.map((cat: any) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <CourtDimensionsForm skillForm={skillForm} setSkillForm={setSkillForm} />
            </>
          ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Category and Module Selection */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                  Category & Module
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!!editingSkill}>
                      <InputLabel id="default-category-label">Category *</InputLabel>
                      <Select
                        labelId="default-category-label"
                        value={skillForm.category_id || ''}
                        label="Category *"
                        onChange={(e) => {
                          const category = categories.find((c: any) => c.id === e.target.value);
                          setSkillForm({ ...skillForm, category_id: e.target.value, subcategory_id: null });
                          if (category) loadTemplate(category.id);
                        }}
                        required
                      >
                        {categories
                          .filter((cat: any) => {
                            // Always show main categories (no parent)
                            if (cat.parent_category_id) return false;
                            
                            // If editing, show the selected category even if it doesn't have table_name
                            if (editingSkill && skillForm.category_id === cat.id) {
                              return true;
                            }
                            
                            // If creating new and selectedCategoryForSkills is a subcategory, 
                            // show its parent even if it doesn't have table_name
                            if (!editingSkill && selectedCategoryForSkills?.parent_category_id && 
                                cat.id === selectedCategoryForSkills.parent_category_id) {
                              return true;
                            }
                            
                            // Otherwise, only show categories with table_name (subcategories have tables)
                            return cat.table_name || cat.full_table_name;
                          })
                          .map((cat: any) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!!editingSkill || !!selectedCategoryForSkills?.parent_category_id}>
                      <InputLabel id="default-subcategory-label">Module/Subcategory</InputLabel>
                      <Select
                        labelId="default-subcategory-label"
                        value={skillForm.subcategory_id || ''}
                        label="Module/Subcategory"
                        onChange={(e) => {
                          const subcategoryId = e.target.value || null;
                          setSkillForm({ ...skillForm, subcategory_id: subcategoryId });
                          if (subcategoryId) {
                            const subcategory = categories.find((c: any) => c.id === subcategoryId);
                            if (subcategory && (subcategory.table_name || subcategory.full_table_name)) {
                              loadTemplate(subcategoryId);
                            }
                          } else {
                            const mainCategoryId = skillForm.category_id;
                            if (mainCategoryId) {
                              loadTemplate(mainCategoryId);
                            }
                          }
                        }}
                      >
                        <MenuItem value="">-- None --</MenuItem>
                        {(() => {
                          const parentCategoryId = skillForm.category_id;
                          if (!parentCategoryId) return [];
                          
                          const subcategories = categories.filter((cat: any) => cat.parent_category_id === parentCategoryId);
                          
                          if (editingSkill && skillForm.subcategory_id) {
                            const currentSub = categories.find((c: any) => c.id === skillForm.subcategory_id);
                            if (currentSub && !subcategories.find((s: any) => s.id === currentSub.id)) {
                              subcategories.push(currentSub);
                            }
                          }
                          
                          return subcategories.map((cat: any) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            {/* Dynamic Form Sections from Template */}
            {templateLoading ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : categoryTemplate && categoryTemplate.formSections ? (
              Object.keys(categoryTemplate.formSections).map((sectionKey) => {
                const section = categoryTemplate.formSections[sectionKey];
                const sectionFields = categoryTemplate.tableSchema?.columns?.filter((col: any) => 
                  section.fields?.includes(col.name)
                ) || [];

                if (sectionFields.length === 0) return null;

                return (
                  <Grid item xs={12} key={sectionKey}>
                    <Accordion 
                      defaultExpanded={!section.collapsible || expandedFormSections[sectionKey]}
                      expanded={expandedFormSections[sectionKey] !== false}
                      onChange={(e, expanded) => {
                        setExpandedFormSections({ ...expandedFormSections, [sectionKey]: expanded });
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {section.label || sectionKey}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {sectionFields.map((field: any) => {
                            if (field.show_in_form === false) return null;
                            const fieldLabel = categoryTemplate.fieldLabels?.[field.name] || field.label || field.name;
                            const fieldValue = skillForm[field.name] ?? (field.type?.includes('[]') ? [] : (field.type?.includes('BOOLEAN') ? true : ''));

                            // Render based on ui_type
                            switch (field.ui_type) {
                              case 'select':
                                return (
                                  <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                                    <FormControl fullWidth required={field.required}>
                                      <InputLabel>{fieldLabel}{field.required ? ' *' : ''}</InputLabel>
                                      <Select
                                        value={fieldValue || ''}
                                        label={fieldLabel}
                                        onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.value })}
                                      >
                                        {field.options?.map((opt: string) => (
                                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                );
                              case 'textarea':
                                return (
                                  <Grid item xs={12} key={field.name}>
                                    <TextField
                                      fullWidth
                                      label={fieldLabel + (field.required ? ' *' : '')}
                                      multiline
                                      rows={field.rows || 3}
                                      value={fieldValue || ''}
                                      onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.value })}
                                      required={field.required}
                                    />
                                  </Grid>
                                );
                              case 'number':
                                return (
                                  <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                                    <TextField
                                      fullWidth
                                      label={fieldLabel + (field.required ? ' *' : '')}
                                      type="number"
                                      value={fieldValue || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setSkillForm({ ...skillForm, [field.name]: val === '' ? null : (field.type?.includes('INTEGER') ? parseInt(val) : parseFloat(val)) });
                                      }}
                                      inputProps={{ 
                                        min: field.min, 
                                        max: field.max, 
                                        step: field.step || (field.type?.includes('DECIMAL') ? 0.01 : 1)
                                      }}
                                      required={field.required}
                                    />
                                  </Grid>
                                );
                              case 'url':
                                return (
                                  <Grid item xs={12} key={field.name}>
                                    <TextField
                                      fullWidth
                                      label={fieldLabel + (field.required ? ' *' : '')}
                                      type="url"
                                      value={fieldValue || ''}
                                      onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.value })}
                                      required={field.required}
                                    />
                                  </Grid>
                                );
                              case 'multiline':
                                return (
                                  <Grid item xs={12} key={field.name}>
                                    <TextField
                                      fullWidth
                                      label={fieldLabel + (field.required ? ' *' : '')}
                                      multiline
                                      rows={4}
                                      value={Array.isArray(fieldValue) ? fieldValue.join('\n') : (fieldValue || '')}
                                      onChange={(e) => {
                                        const lines = e.target.value.split('\n').filter((l: string) => l.trim());
                                        setSkillForm({ ...skillForm, [field.name]: lines });
                                      }}
                                      helperText={`Enter each item on a new line`}
                                      required={field.required}
                                    />
                                  </Grid>
                                );
                              case 'checkbox':
                                return (
                                  <Grid item xs={12} sm={6} key={field.name}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={fieldValue !== false && fieldValue !== null}
                                          onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.checked })}
                                        />
                                      }
                                      label={fieldLabel}
                                    />
                                  </Grid>
                                );
                              default: // text
                                return (
                                  <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                                    <TextField
                                      fullWidth
                                      label={fieldLabel + (field.required ? ' *' : '')}
                                      value={fieldValue || ''}
                                      onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.value })}
                                      required={field.required}
                                    />
                                  </Grid>
                                );
                            }
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })
            ) : categoryTemplate && categoryTemplate.tableSchema?.columns ? (
              // Fallback: Show all fields if no sections defined
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 600, color: 'primary.main' }}>
                  Fields
                </Typography>
                <Grid container spacing={2}>
                  {categoryTemplate.tableSchema.columns
                    .filter((col: any) => col.show_in_form !== false && !['id', 'created_at', 'updated_at'].includes(col.name))
                    .map((field: any) => {
                      const fieldLabel = categoryTemplate.fieldLabels?.[field.name] || field.label || field.name;
                      const fieldValue = skillForm[field.name] ?? '';
                      return (
                        <Grid item xs={12} sm={6} key={field.name}>
                          <TextField
                            fullWidth
                            label={fieldLabel + (field.required ? ' *' : '')}
                            value={fieldValue || ''}
                            onChange={(e) => setSkillForm({ ...skillForm, [field.name]: e.target.value })}
                            required={field.required}
                          />
                        </Grid>
                      );
                    })}
                </Grid>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No template found for this category. Please ensure a template is configured.
                </Alert>
              </Grid>
            )}
            
            {/* Legacy fallback - only show if no template */}
            {!categoryTemplate && (
              <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Skill Name *"
                value={skillForm.skill_name || skillForm.name || ''}
                onChange={(e) => setSkillForm({ ...skillForm, skill_name: e.target.value, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={skillForm.description || ''}
                onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Explanation"
                multiline
                rows={4}
                value={skillForm.explanation || ''}
                onChange={(e) => setSkillForm({ ...skillForm, explanation: e.target.value })}
                helperText="Detailed explanation of the skill"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skillForm.path_player !== false}
                    onChange={(e) => setSkillForm({ ...skillForm, path_player: e.target.checked })}
                  />
                }
                label="Available for Player Path"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skillForm.path_coach !== false}
                    onChange={(e) => setSkillForm({ ...skillForm, path_coach: e.target.checked })}
                  />
                }
                label="Available for Coach Path"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Difficulty Level (1-5)"
                type="number"
                value={skillForm.difficulty_level || ''}
                onChange={(e) => setSkillForm({ ...skillForm, difficulty_level: parseInt(e.target.value) || null })}
                inputProps={{ min: 1, max: 5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skillForm.is_active !== false}
                    onChange={(e) => setSkillForm({ ...skillForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Videos (comma-separated URLs)"
                value={Array.isArray(skillForm.reference_videos) ? skillForm.reference_videos.join(', ') : (skillForm.reference_videos || '')}
                onChange={(e) => {
                  const videos = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                  setSkillForm({ ...skillForm, reference_videos: videos });
                }}
                helperText="Enter video URLs separated by commas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Practice Steps (one per line)"
                multiline
                rows={4}
                value={Array.isArray(skillForm.practice_steps) ? skillForm.practice_steps.join('\n') : (skillForm.practice_steps || '')}
                onChange={(e) => {
                  const steps = e.target.value.split('\n').filter(s => s.trim());
                  setSkillForm({ ...skillForm, practice_steps: steps });
                }}
                helperText="Enter each practice step on a new line"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tips (one per line)"
                multiline
                rows={3}
                value={Array.isArray(skillForm.tips) ? skillForm.tips.join('\n') : (skillForm.tips || '')}
                onChange={(e) => {
                  const tips = e.target.value.split('\n').filter(t => t.trim());
                  setSkillForm({ ...skillForm, tips: tips });
                }}
                helperText="Enter each tip on a new line"
              />
            </Grid>
              </>
            )}
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSkillDialogOpen(false); setEditingSkill(null); setSkillForm({}); }}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleSaveSkill(false)} 
            variant="outlined"
            disabled={(() => {
              if (skillsLoading) return true;
              
              // Check required fields based on template
              if (categoryTemplate?.tableSchema?.columns) {
                const requiredFields = categoryTemplate.tableSchema.columns.filter((col: any) => col.required);
                for (const field of requiredFields) {
                  const value = skillForm[field.name];
                  if (!value || (typeof value === 'string' && value.trim() === '')) {
                    return true; // Disable if any required field is empty
                  }
                }
                return false; // All required fields filled
              }
              
              // Fallback: check for skill_name or name (for legacy/strokes-shots)
              return !skillForm.skill_name && !skillForm.name && !skillForm.title;
            })()}
          >
            {skillsLoading ? <CircularProgress size={20} /> : 'Save Draft'}
          </Button>
          <Button 
            onClick={() => handleSaveSkill(true)} 
            variant="contained" 
            color="success"
            disabled={(() => {
              if (skillsLoading) return true;
              
              // Check required fields based on template
              if (categoryTemplate?.tableSchema?.columns) {
                const requiredFields = categoryTemplate.tableSchema.columns.filter((col: any) => col.required);
                for (const field of requiredFields) {
                  const value = skillForm[field.name];
                  if (!value || (typeof value === 'string' && value.trim() === '')) {
                    return true; // Disable if any required field is empty
                  }
                }
                return false; // All required fields filled
              }
              
              // Fallback: check for skill_name or name (for legacy/strokes-shots)
              return !skillForm.skill_name && !skillForm.name && !skillForm.title;
            })()}
          >
            {skillsLoading ? <CircularProgress size={20} /> : editingSkill ? 'Update & Publish' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
